/*
 * Jolly Clock-Out — Office Wall (Milestone 8, READ-ONLY POC)
 *
 * Proves: private link -> theme page -> this script -> Shopify App Proxy ->
 * Render backend -> Supabase -> safe DTO -> storefront rendering.
 *
 * Credential handling rules enforced here:
 *   - The office slug and the raw access token arrive ONLY in the URL fragment.
 *   - The fragment is stripped from the visible URL immediately on execution.
 *   - It is sent ONLY in the JSON body of a same-origin App Proxy POST.
 *   - It is never logged, rendered, put in an error message, or attached to
 *     an event.
 *   - M21: the raw Office access token IS now persisted (localStorage only),
 *     scoped per office, and only after the backend has accepted it. It is
 *     the office's shareable READ capability — server-side it authorizes
 *     exactly one operation, reading this Wall — and keeping it is what lets
 *     Share survive a refresh, since the fragment it arrived in is stripped
 *     immediately and the backend retains only its hash. Writing still needs
 *     participantToken, editing an entry its editToken, and contributing a
 *     verified contributorToken.
 *   - A separate, scoped, expiring, revocable participantToken returned by the
 *     backend IS persisted (localStorage key `jco:v1`), so a refresh or a later
 *     return visit on the same origin no longer needs the share link.
 *   - A per-WallEntry editToken is persisted in the same key. It is a credential and
 *     never reaches the DOM, a URL, a log or an event.
 *
 * What `jco:v1` holds, in full: lastOffice, participant grants, a per-Office "this
 * browser backed it" marker, per-entry edit credentials, a per-Office contributor
 * DISPLAY NAME kept purely to prefill one editable form field, and from M21 two more
 * per-Office entries — the raw read-only share token and the verified contributor
 * credential. No backing email, no answers, no message and no timestamps are ever
 * stored.
 *
 * Rendering rule: no API-derived string is ever passed to innerHTML. Content is
 * written with textContent or setAttribute, into markup cloned from a Liquid
 * <template>. No sanitizer dependency is required because no HTML is parsed.
 */
(function () {
  "use strict";

  /* The App Proxy subpath is hardcoded on purpose. It is deliberately NOT a
   * section setting: a merchant-editable endpoint would allow the raw access
   * token to be POSTed to an arbitrary host from the Theme Editor. */
  var PROXY_BASE = "/apps/jolly-clockout";

  /* M18B — the two prompts of the simplified contribution form.
   *
   * These are KEYS, not copy. Every question, character limit and ordering
   * decision still comes from the Wall DTO; this list exists only so the
   * theme can tell whether the backend is currently publishing the simplified
   * form, and nothing here is ever sent as an answer on its own.
   *
   * The form adapts, it does not assume. Prompts are rows in the backend's
   * `campaign_prompts` table and this repository cannot create, activate or
   * retire one. Until BOTH keys are published as active prompts, the Office
   * Wall keeps rendering whatever prompts the DTO does publish, exactly as
   * before — so a theme deployed ahead of the prompt sync is still a working
   * Office Wall rather than a broken one, and never builds a request naming a
   * prompt the backend would reject. */
  var SIMPLIFIED_PROMPT_KEYS = ["party_upgrade_case", "extra_budget_plan"];

  /* M22 — ISO-3166-1 alpha-2 for the office-profile country selector.
   *
   * "CODE|Readable name", because the API takes the code and the visitor reads
   * the name. Generated from the system `iso3166.tab` (public domain) rather
   * than typed, so no code is mistyped, and sorted by name so the <option>
   * order is the order a visitor scans.
   *
   * It lives here rather than in the section because it is campaign data, not
   * merchant copy: 249 hidden <span>s would be shipped in the HTML of every
   * Office Wall view, including the read-only visitors who can never open the
   * editor, whereas this sits in a cached asset and is parsed once.
   *
   * Shopify's own country lists were deliberately not used:
   * `all_country_option_tags` keys options on the NAME and carries province
   * data this form has no field for, and `localization.available_countries`
   * lists only the markets this store sells to — neither answers "where is
   * this office", which is not a shipping destination. */
  var COUNTRIES = [
    "AF|Afghanistan", "AX|Åland Islands", "AL|Albania", "DZ|Algeria", "AD|Andorra", "AO|Angola",
    "AI|Anguilla", "AQ|Antarctica", "AG|Antigua and Barbuda", "AR|Argentina", "AM|Armenia",
    "AW|Aruba", "AU|Australia", "AT|Austria", "AZ|Azerbaijan", "BS|Bahamas", "BH|Bahrain",
    "BD|Bangladesh", "BB|Barbados", "BY|Belarus", "BE|Belgium", "BZ|Belize", "BJ|Benin",
    "BM|Bermuda", "BT|Bhutan", "BO|Bolivia", "BA|Bosnia and Herzegovina", "BW|Botswana",
    "BV|Bouvet Island", "BR|Brazil", "IO|British Indian Ocean Territory", "BN|Brunei",
    "BG|Bulgaria", "BF|Burkina Faso", "BI|Burundi", "KH|Cambodia", "CM|Cameroon", "CA|Canada",
    "CV|Cape Verde", "BQ|Caribbean NL", "KY|Cayman Islands", "CF|Central African Republic",
    "TD|Chad", "CL|Chile", "CN|China", "CX|Christmas Island", "CC|Cocos (Keeling) Islands",
    "CO|Colombia", "KM|Comoros", "CD|Congo (Democratic Republic)", "CG|Congo (Republic)",
    "CK|Cook Islands", "CR|Costa Rica", "CI|Côte d’Ivoire", "HR|Croatia", "CU|Cuba", "CW|Curaçao",
    "CY|Cyprus", "CZ|Czech Republic", "DK|Denmark", "DJ|Djibouti", "DM|Dominica",
    "DO|Dominican Republic", "EC|Ecuador", "EG|Egypt", "SV|El Salvador", "GQ|Equatorial Guinea",
    "ER|Eritrea", "EE|Estonia", "SZ|Eswatini", "ET|Ethiopia", "FK|Falkland Islands",
    "FO|Faroe Islands", "FJ|Fiji", "FI|Finland", "FR|France", "GF|French Guiana",
    "PF|French Polynesia", "TF|French Southern Territories", "GA|Gabon", "GM|Gambia",
    "GE|Georgia", "DE|Germany", "GH|Ghana", "GI|Gibraltar", "GR|Greece", "GL|Greenland",
    "GD|Grenada", "GP|Guadeloupe", "GU|Guam", "GT|Guatemala", "GG|Guernsey", "GN|Guinea",
    "GW|Guinea-Bissau", "GY|Guyana", "HT|Haiti", "HM|Heard Island and McDonald Islands",
    "HN|Honduras", "HK|Hong Kong", "HU|Hungary", "IS|Iceland", "IN|India", "ID|Indonesia",
    "IR|Iran", "IQ|Iraq", "IE|Ireland", "IM|Isle of Man", "IL|Israel", "IT|Italy", "JM|Jamaica",
    "JP|Japan", "JE|Jersey", "JO|Jordan", "KZ|Kazakhstan", "KE|Kenya", "KI|Kiribati", "KW|Kuwait",
    "KG|Kyrgyzstan", "LA|Laos", "LV|Latvia", "LB|Lebanon", "LS|Lesotho", "LR|Liberia", "LY|Libya",
    "LI|Liechtenstein", "LT|Lithuania", "LU|Luxembourg", "MO|Macau", "MG|Madagascar", "MW|Malawi",
    "MY|Malaysia", "MV|Maldives", "ML|Mali", "MT|Malta", "MH|Marshall Islands", "MQ|Martinique",
    "MR|Mauritania", "MU|Mauritius", "YT|Mayotte", "MX|Mexico", "FM|Micronesia", "MD|Moldova",
    "MC|Monaco", "MN|Mongolia", "ME|Montenegro", "MS|Montserrat", "MA|Morocco", "MZ|Mozambique",
    "MM|Myanmar (Burma)", "NA|Namibia", "NR|Nauru", "NP|Nepal", "NL|Netherlands",
    "NC|New Caledonia", "NZ|New Zealand", "NI|Nicaragua", "NE|Niger", "NG|Nigeria", "NU|Niue",
    "NF|Norfolk Island", "KP|North Korea", "MK|North Macedonia", "MP|Northern Mariana Islands",
    "NO|Norway", "OM|Oman", "PK|Pakistan", "PW|Palau", "PS|Palestine", "PA|Panama",
    "PG|Papua New Guinea", "PY|Paraguay", "PE|Peru", "PH|Philippines", "PN|Pitcairn", "PL|Poland",
    "PT|Portugal", "PR|Puerto Rico", "QA|Qatar", "RE|Réunion", "RO|Romania", "RU|Russia",
    "RW|Rwanda", "BL|Saint Barthelemy", "SH|Saint Helena", "KN|Saint Kitts and Nevis",
    "LC|Saint Lucia", "MF|Saint Martin (French part)", "PM|Saint Pierre and Miquelon",
    "VC|Saint Vincent and the Grenadines", "AS|Samoa (American)", "WS|Samoa (western)",
    "SM|San Marino", "ST|Sao Tome and Principe", "SA|Saudi Arabia", "SN|Senegal", "RS|Serbia",
    "SC|Seychelles", "SL|Sierra Leone", "SG|Singapore", "SX|Sint Maarten (Dutch part)",
    "SK|Slovakia", "SI|Slovenia", "SB|Solomon Islands", "SO|Somalia", "ZA|South Africa",
    "GS|South Georgia and the South Sandwich Islands", "KR|South Korea", "SS|South Sudan",
    "ES|Spain", "LK|Sri Lanka", "SD|Sudan", "SR|Suriname", "SJ|Svalbard and Jan Mayen",
    "SE|Sweden", "CH|Switzerland", "SY|Syria", "TW|Taiwan", "TJ|Tajikistan", "TZ|Tanzania",
    "TH|Thailand", "TL|Timor-Leste", "TG|Togo", "TK|Tokelau", "TO|Tonga",
    "TT|Trinidad and Tobago", "TN|Tunisia", "TR|Turkey", "TM|Turkmenistan",
    "TC|Turks and Caicos Islands", "TV|Tuvalu", "UG|Uganda", "UA|Ukraine",
    "AE|United Arab Emirates", "GB|United Kingdom", "US|United States", "UY|Uruguay",
    "UM|US minor outlying islands", "UZ|Uzbekistan", "VU|Vanuatu", "VA|Vatican City",
    "VE|Venezuela", "VN|Vietnam", "VG|Virgin Islands (UK)", "VI|Virgin Islands (US)",
    "WF|Wallis and Futuna", "EH|Western Sahara", "YE|Yemen", "ZM|Zambia", "ZW|Zimbabwe"
  ];

  var COUNTRY_NAMES = null;

  /* Built once, on first use — a read-only visitor never pays for it. */
  function countryName(code) {
    if (typeof code !== "string" || !code) return "";
    if (!COUNTRY_NAMES) {
      COUNTRY_NAMES = {};
      for (var i = 0; i < COUNTRIES.length; i++) {
        var split = COUNTRIES[i].indexOf("|");
        COUNTRY_NAMES[COUNTRIES[i].slice(0, split)] = COUNTRIES[i].slice(split + 1);
      }
    }
    return COUNTRY_NAMES[code.trim().toUpperCase()] || "";
  }

  /* ----------------------------------------------------------------------
   * Credential capture and fragment strip.
   *
   * Runs at script execution — before any custom element upgrade, before any
   * DOM query, and independently of whether the rest of this file succeeds.
   * The strip is in `finally`, so a parse failure above it still cannot leave
   * the raw credential sitting in the address bar.
   * -------------------------------------------------------------------- */
  function trimmedOrNull(value) {
    return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
  }

  /* True when the visible URL does not already name this Office. */
  function needsOfficeParam(slug) {
    if (typeof slug !== "string" || !slug) return false;
    try {
      return new URLSearchParams(window.location.search).get("office") !== slug;
    } catch (e) {
      return false;
    }
  }

  /* Rewrites the visible URL in place: drops the fragment entirely and, when an
   * Office slug is known, ensures `?office=<slug>` is present.
   *
   * Uses URL/URLSearchParams so every OTHER query parameter is preserved —
   * `preview_theme_id`, `view` and anything else survive untouched. Only the
   * `office` key is ever written, and the only value ever passed in is a slug.
   * A credential is never a parameter here.
   *
   * Always replaceState, never pushState: no history entry may retain the token,
   * and the back button must not resurrect a consumed share link. */
  function rewriteUrl(slug) {
    try {
      var url = new URL(window.location.href);
      url.hash = "";
      if (slug) url.searchParams.set("office", slug);
      window.history.replaceState(null, "", url.pathname + url.search);
    } catch (e) {
      /* Non-fatal. Never surface or log this. */
    }
  }

  var CREDENTIALS = (function captureAndStrip() {
    var queryOffice = null;
    var fragmentOffice = null;
    var access = null;

    /* The canonical, bookmarkable location of the Office slug. It is not a
     * credential, so it may stay visible in the query string. */
    try {
      queryOffice = new URLSearchParams(window.location.search).get("office");
    } catch (e) {
      queryOffice = null;
    }

    try {
      /* H-2 — snippets/campaign-token-guard.liquid has normally already taken the
       * fragment out of the address bar, before content_for_header ran, and holds
       * it for exactly one call. The fragment parse below stays as the fallback
       * for a page rendered without the guard. */
      var early =
        typeof window.__jcoTakeUrlCredentials === "function" ? window.__jcoTakeUrlCredentials() : null;
      if (early && typeof early === "object") {
        fragmentOffice = typeof early.office === "string" ? early.office : null;
        access = typeof early.access === "string" ? early.access : null;
      }

      var hash = window.location.hash || "";
      if (!access && hash.length > 1) {
        var params = new URLSearchParams(hash.charAt(0) === "#" ? hash.slice(1) : hash);
        /* `office` in the fragment is the LEGACY share-link shape. Still honoured so
         * existing DEV links keep working; normalised to the query string below. */
        fragmentOffice = params.get("office") || fragmentOffice;
        /* The raw Office access token. Fragment-only, for this bootstrap only.
         * `k` is accepted as an alias in case a backend link template uses the
         * short name; links this theme builds always use `access`. */
        access = params.get("access") || params.get("k");
      }
    } catch (e) {
      fragmentOffice = null;
      access = null;
    } finally {
      /* Unconditional cleanup, in `finally` so a parse failure above still cannot
       * leave the credential in the address bar. Also normalises a legacy
       * fragment-carried slug into `?office=<slug>` in the same single rewrite. */
      try {
        var resolved = trimmedOrNull(queryOffice) || trimmedOrNull(fragmentOffice);
        var needsSlug = resolved && trimmedOrNull(queryOffice) !== resolved;
        if (window.location.hash || needsSlug) rewriteUrl(resolved);
      } catch (e2) {
        /* Non-fatal. Never surface or log this. */
      }
    }

    return {
      /* Precedence: explicit URL query wins, then a legacy fragment slug. */
      office: trimmedOrNull(queryOffice) || trimmedOrNull(fragmentOffice),
      access: trimmedOrNull(access)
    };
  })();

  /* ---------------------------------------------------------------------- */

  /* Reads a localized string map emitted by the section as hidden DOM text nodes.
   *
   * This deliberately replaces the earlier `JSON.parse` of a `| t | json` blob.
   * Shopify's `t` filter HTML-escapes any locale key not ending in `_html`, and
   * JSON transport preserved that escape all the way to `textContent`, which does
   * not decode HTML — so entities such as `&#39;` rendered literally. Reading from
   * real text nodes lets the HTML parser decode the entity first. Both the read
   * and the later write use `textContent`, so XSS safety is unchanged. */
  function readTextMap(root, containerSelector, keyAttribute) {
    var map = {};
    try {
      var container = root.querySelector(containerSelector);
      if (!container) return map;
      var nodes = container.querySelectorAll("[" + keyAttribute + "]");
      for (var i = 0; i < nodes.length; i++) {
        var key = nodes[i].getAttribute(keyAttribute);
        if (key) map[key] = (nodes[i].textContent || "").trim();
      }
    } catch (e) {
      return map;
    }
    return map;
  }

  /* APPROVED REMOTE ORIGINS — the theme decides, not the backend response.
   *
   * SECURITY (JC-04 / JC-08). Two decisions used to be delegated entirely to
   * whatever the backend returned: where a participant's photo is UPLOADED to,
   * and which image URLs every viewer's browser is told to FETCH. A compromised,
   * buggy or man-in-the-middled response could therefore push photo bytes to any
   * https host, or turn the Office Wall into a beacon that leaks each viewer's IP
   * and user agent to a host they never chose.
   *
   * The Storage origin is pinned to this campaign's one Supabase project by
   * EXACT hostname, and uploads are additionally confined to Storage's upload
   * endpoint. A hostname is public information; no Supabase key, service-role
   * credential or signed secret appears anywhere in the theme.
   *
   * A Storage HOSTNAME is public information. No Supabase key, service-role
   * credential or signed secret is introduced here or anywhere in the theme. */
  var SUPABASE_STORAGE_HOST = "mkposiqqbsmtwtxtefxg.supabase.co";

  /* Exact hostname match. Not a wildcard, not a suffix test: a suffix test would
   * still accept any other Supabase project, and this campaign has exactly one. */
  var APPROVED_UPLOAD_HOSTS = [SUPABASE_STORAGE_HOST];
  var APPROVED_IMAGE_HOSTS = [SUPABASE_STORAGE_HOST, "cdn.shopify.com"];

  /* Host alone is not enough for an upload. Pinning only the host would still let
   * a compromised response aim a multipart PUT at any other API on that host.
   * Uploads are therefore confined to Storage's signed-upload endpoint family
   * (`/storage/v1/object/upload/sign/<bucket>/<path>`), which is the shape this
   * client already speaks: PUT, token in the query string, file as multipart.
   *
   * Reads are deliberately wider than writes: a signed download
   * (`/storage/v1/object/sign/...`), a public object and a render/transform URL
   * are all legitimate ways for an approved photo to come back. */
  var SUPABASE_UPLOAD_PATH_PREFIX = "/storage/v1/object/upload/";
  var SUPABASE_READ_PATH_PREFIXES = ["/storage/v1/object/", "/storage/v1/render/"];

  function hostIs(hostname, list) {
    if (typeof hostname !== "string" || !hostname) return false;
    var host = hostname.toLowerCase();
    for (var i = 0; i < list.length; i++) {
      if (host === list[i]) return true;
    }
    return false;
  }

  function startsWithPath(pathname, prefix) {
    return typeof pathname === "string" && pathname.indexOf(prefix) === 0;
  }

  /* https + exact pinned host + Storage upload endpoint. All three, or refuse. */
  function isApprovedUploadTarget(parsed) {
    if (!parsed || parsed.protocol !== "https:") return false;
    if (!hostIs(parsed.hostname, APPROVED_UPLOAD_HOSTS)) return false;
    return startsWithPath(parsed.pathname, SUPABASE_UPLOAD_PATH_PREFIX);
  }

  function isApprovedImageUrl(parsed) {
    if (!parsed || parsed.protocol !== "https:") return false;
    if (hostIs(parsed.hostname, ["cdn.shopify.com"])) return true;
    if (hostIs(parsed.hostname, [SUPABASE_STORAGE_HOST])) {
      for (var i = 0; i < SUPABASE_READ_PATH_PREFIXES.length; i++) {
        if (startsWithPath(parsed.pathname, SUPABASE_READ_PATH_PREFIXES[i])) return true;
      }
      return false;
    }
    return false;
  }

  /* Only a source that is both renderable and approved.
   *
   * Allow-list rather than block-list. Anything else yields "" and the caller
   * leaves the image unset. Protocol-relative and backslash forms are rejected
   * before anything else, because they are the classic way to change the host
   * while still looking like a local path. `data:` is narrowed to base64 raster
   * images: `data:image/svg+xml` is script-bearing content and is not accepted. */
  function safeImageUrl(value) {
    if (typeof value !== "string" || !value) return "";
    var url = value.trim();
    if (!url) return "";

    /* Locally generated preview of the file this visitor just chose. */
    if (/^blob:/i.test(url)) return url;
    if (/^data:image\/(png|jpeg|jpg|gif|webp);base64,[A-Za-z0-9+/=]+$/i.test(url)) return url;

    /* Host-changing shapes: `//host`, `\\host`, `/\host`. */
    if (/^[\\/]{2}/.test(url) || /^\/\\/.test(url)) return "";

    /* Same-origin absolute path. */
    if (/^\//.test(url)) return url;

    try {
      var parsed = new URL(url, window.location.origin);
      if (parsed.protocol !== "https:") return "";
      if (parsed.origin === window.location.origin) return parsed.toString();
      if (isApprovedImageUrl(parsed)) return parsed.toString();
      return "";
    } catch (e) {
      return "";
    }
  }

  /* Literal substitution. `String.replace` with a string replacement treats
   * `$&`, `` $` ``, `$'` and `$1` as insertion patterns, so a display name
   * containing them rewrote the surrounding copy — a coworker could corrupt
   * or spoof the accessible name on someone else's photo. A function
   * replacement is taken literally. */
  function fillToken(template, token, value) {
    return String(template || "").replace(token, function () {
      return value == null ? "" : String(value);
    });
  }

  /* M29H — placeholders inside MERCHANT-EDITABLE text.
   *
   * The hero headlines are section settings, and the Shopify Theme Editor reads
   * `{{ … }}` in a setting value as a DYNAMIC SOURCE. `{{ nominator }}` and
   * `{{ office }}` are not real Liquid objects, so the editor showed them as
   * invalid red chips and refused to preview or save the whole template. The
   * campaign copy therefore uses `[[nominator]]` and `[[office]]`, which the
   * editor treats as ordinary characters.
   *
   * The old spellings stay supported at runtime so a Wall already saved with
   * `{{ office }}` keeps rendering the company name instead of printing the
   * braces at a visitor. Nothing writes the old form any more.
   *
   * Every occurrence is replaced, not just the first — a merchant may name the
   * office twice in one sentence. The replacement is a FUNCTION, so a value
   * containing `$&` or `$1` is inserted literally and cannot rewrite the
   * sentence around it; the result is written with textContent, so markup in a
   * company name stays inert. */
  function fillPlaceholder(template, name, value) {
    var text = String(template || "");
    if (!name) return text;
    var safe = value == null ? "" : String(value);
    var pattern = new RegExp("\\[\\[\\s*" + name + "\\s*\\]\\]|\\{\\{\\s*" + name + "\\s*\\}\\}", "g");
    return text.replace(pattern, function () {
      return safe;
    });
  }

  function setText(el, value) {
    if (el) el.textContent = value == null ? "" : String(value);
  }

  function firstString(source, keys) {
    if (!source || typeof source !== "object") return "";
    for (var i = 0; i < keys.length; i++) {
      var value = source[keys[i]];
      if (typeof value === "string" && value.trim() !== "") return value.trim();
    }
    return "";
  }

  /* ----------------------------------------------------------------------
   * Participant grant persistence (Milestone 9B).
   *
   * The participant grant is a SEPARATE credential from the raw Office access
   * token. It authorises exactly one Office, expires server-side after 45 days,
   * is stored hashed server-side, and is revocable. It is NOT participant
   * identity and must never become backing or voting identity.
   *
   * It lives in localStorage because Shopify's App Proxy strips cookies, so a
   * cookie-based grant is not available to this page.
   *
   * THE RAW OFFICE ACCESS TOKEN IS NEVER PASSED TO ANY FUNCTION BELOW. Only
   * `payload.participantToken` is ever persisted.
   *
   * localStorage is origin-scoped: a grant established on one origin does not
   * exist on another. That is expected and must not be worked around.
   *
   * Every access is wrapped. A thrown value is never inspected or logged — a
   * quota error can echo the written value in some engines.
   * -------------------------------------------------------------------- */

  var STORAGE_KEY = "jco:v1";

  /* Participant grant minted during THIS page's lifetime. Module-scoped rather
   * than a property on the element, so it cannot be reached by walking the DOM. */
  var SESSION_GRANT = null;

  /* Upper bound on owner-only moderation lookups per page load. A browser
   * normally owns one or two entries; this only stops a corrupted store from
   * turning one Wall load into a burst of requests. */
  var MAX_MODERATION_CHECKS = 10;

  /* How long the Wall waits for those lookups before rendering anyway. A slow
   * answer must never hold the Wall hostage; it simply lands on the next load. */
  var MODERATION_CHECK_TIMEOUT_MS = 4000;

  function emptyStore() {
    return {
      lastOffice: null,
      grants: {},
      backed: {},
      entries: {},
      contributorNames: {},
      /* M21 — two new office-scoped namespaces. Both MUST also be reconstructed
       * in readStore(); anything omitted there is silently wiped by the next
       * writeStore(). */
      share: {},
      contributors: {}
    };
  }

  function readStore() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return emptyStore();

      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return emptyStore();

      /* Every key must be carried through. readStore() RECONSTRUCTS the object, so
       * anything omitted here is silently discarded by the next writeStore() — which
       * would wipe the backed markers on any grant save, drop or prune. */
      return {
        lastOffice:
          typeof parsed.lastOffice === "string" && parsed.lastOffice ? parsed.lastOffice : null,
        grants: parsed.grants && typeof parsed.grants === "object" ? parsed.grants : {},
        backed: parsed.backed && typeof parsed.backed === "object" ? parsed.backed : {},
        entries: parsed.entries && typeof parsed.entries === "object" ? parsed.entries : {},
        contributorNames:
          parsed.contributorNames && typeof parsed.contributorNames === "object"
            ? parsed.contributorNames
            : {},
        /* The raw READ-ONLY Office share token, per office.
         *
         * M21 reverses the earlier "never persisted" rule for THIS credential
         * only, and deliberately. It was captured from the link fragment at
         * bootstrap and held in memory, and `rewriteUrl()` strips that fragment
         * immediately — so one refresh destroyed the only copy and Share then
         * told a visitor who was plainly looking at the Wall to "open your
         * private link again". The backend cannot help: it keeps only the hash,
         * so the raw token cannot be reissued from a grant.
         *
         * It is the office's own shareable read capability, not a personal
         * credential: server-side it authorizes exactly one operation, reading
         * this Wall. Writing still needs participantToken, editing an entry its
         * editToken, and contributing a verified contributorToken. */
        share: parsed.share && typeof parsed.share === "object" ? parsed.share : {},
        /* Proof this browser completed email verification for an office. Write
         * permission only — never a share credential, never in a URL. */
        contributors:
          parsed.contributors && typeof parsed.contributors === "object"
            ? parsed.contributors
            : {}
      };
    } catch (e) {
      /* Unavailable, blocked, or corrupt JSON. Treated as "no grants"; the next
       * successful write overwrites it. Never logged. */
      return emptyStore();
    }
  }

  function writeStore(store) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (e) {
      /* Blocked or full. Non-fatal: persistence simply does not happen and the
       * Office Wall degrades to the previous one-load behaviour. Never logged. */
    }
  }

  /* Opportunistic hygiene only, run while storage is already being written.
   * expiresAt NEVER gates authentication — the server is authoritative for
   * expiry and revocation. */
  function pruneExpired(grants) {
    var now = Date.now();
    Object.keys(grants).forEach(function (slug) {
      var grant = grants[slug];
      if (!grant || typeof grant !== "object" || typeof grant.token !== "string" || !grant.token) {
        delete grants[slug];
        return;
      }
      if (typeof grant.expiresAt === "string") {
        var at = Date.parse(grant.expiresAt);
        if (!isNaN(at) && at <= now) delete grants[slug];
      }
    });
    return grants;
  }

  /* ---------- Office share token (M21) ---------- */

  function saveShareToken(slug, token) {
    if (typeof slug !== "string" || !slug) return;
    if (typeof token !== "string" || !token) return;
    var store = readStore();
    store.share[slug] = token;
    writeStore(store);
  }

  function readShareToken(slug) {
    if (typeof slug !== "string" || !slug) return null;
    var token = readStore().share[slug];
    return typeof token === "string" && token ? token : null;
  }

  /* ---------- Verified contributor credential (M21) ----------
   *
   * Presence here is NOT authorization. The backend re-checks the signature,
   * the office and the expiry on every write, and a 403 is what actually
   * decides. This store exists so the UI can avoid offering an action that can
   * only fail, and so the credential can be attached to the requests that
   * need it. */

  var CONTRIBUTOR_NOMINATION = "nomination";
  var CONTRIBUTOR_BACKING = "backing";

  /* M22 — the credential now carries WHICH route issued it.
   *
   * The backend signs a `source` into the token itself and re-checks it on
   * every profile write; this copy exists only so the UI can decide whether to
   * OFFER the office-profile controls. A backing-sourced credential must never
   * present them, because the backend will refuse it.
   *
   * The source is recorded by the page that received the token — the
   * nomination verification page can only ever receive a nomination-sourced
   * one, and the backing page a backing-sourced one — so it is observed, never
   * inferred from the opaque token. */
  function saveContributorToken(slug, token, source) {
    if (typeof slug !== "string" || !slug) return;
    if (typeof token !== "string" || !token) return;
    var store = readStore();
    store.contributors[slug] = { token: token, source: contributorSourceOf(source) };
    writeStore(store);
  }

  function contributorSourceOf(value) {
    return value === CONTRIBUTOR_NOMINATION || value === CONTRIBUTOR_BACKING ? value : "";
  }

  function readContributorToken(slug) {
    if (typeof slug !== "string" || !slug) return null;
    var record = readStore().contributors[slug];
    if (!record || typeof record !== "object") return null;
    return typeof record.token === "string" && record.token ? record.token : null;
  }

  /* "" for a credential stored before M22, which is treated as NOT owning the
   * profile. Fails closed: offering an editor that can only 403 is worse than
   * not offering it, and the nominator still reaches it by reopening their
   * verification link while the recovery window is open. */
  function readContributorSource(slug) {
    if (typeof slug !== "string" || !slug) return "";
    var record = readStore().contributors[slug];
    if (!record || typeof record !== "object") return "";
    return contributorSourceOf(record.source);
  }

  /* Scoped to ONE office: a credential rejected for this Wall says nothing
   * about another, and must never take grants or entry credentials with it. */
  function dropContributorToken(slug) {
    if (typeof slug !== "string" || !slug) return;
    var store = readStore();
    if (!Object.prototype.hasOwnProperty.call(store.contributors, slug)) return;
    delete store.contributors[slug];
    writeStore(store);
  }

  function saveGrant(slug, token, expiresAt) {
    if (typeof slug !== "string" || !slug) return;
    if (typeof token !== "string" || !token) return;

    var store = readStore();
    pruneExpired(store.grants);
    store.grants[slug] = {
      token: token,
      expiresAt: typeof expiresAt === "string" && expiresAt ? expiresAt : null
    };
    store.lastOffice = slug;
    writeStore(store);
  }

  function touchLastOffice(slug) {
    if (typeof slug !== "string" || !slug) return;
    var store = readStore();
    if (store.lastOffice === slug) return;
    store.lastOffice = slug;
    writeStore(store);
  }

  /* Removes exactly one Office's grant. Other Offices are untouched, and
   * lastOffice is cleared only when it points at the removed Office. */
  function dropGrant(slug) {
    if (typeof slug !== "string" || !slug) return;
    var store = readStore();
    if (Object.prototype.hasOwnProperty.call(store.grants, slug)) {
      delete store.grants[slug];
    }
    if (store.lastOffice === slug) store.lastOffice = null;
    writeStore(store);
  }

  function readGrantToken(slug) {
    if (typeof slug !== "string" || !slug) return null;
    var grant = readStore().grants[slug];
    return grant && typeof grant.token === "string" && grant.token ? grant.token : null;
  }

  function readLastOffice() {
    return readStore().lastOffice;
  }

  /* Browser-local "this browser already backed this Office" marker.
   *
   * UI convenience ONLY. It is not identity, not proof, not authorization, not
   * deduplication and not consent. The backend's email deduplication stays
   * authoritative, and this value is NEVER sent to the backend or used to derive
   * backend behaviour. It holds a bare `true` per Office slug — no name, no email,
   * no backing id, no dedupe hash, no token, no timestamp, no form values. */
  /* M21 — the marker now carries one of two states, not just presence:
   *
   *   true                  this browser SUBMITTED a backing for this office
   *   BACKING_VERIFIED      the backing-verification page CONFIRMED it
   *
   * The second is written only by that page, on a successful
   * `backings/verify` response — the one moment anything in this campaign
   * knows that THIS browser's own backing was confirmed.
   *
   * Deliberately not derived from anything else. The aggregate
   * `office.backingCount` says nothing about whose backing was counted, and a
   * contributor credential is not evidence either, because a verified
   * NOMINATOR holds one without ever having backed. Keeping those two apart
   * is the whole reason this is recorded separately.
   *
   * Still a UI convenience only: never sent to the backend, never identity,
   * never authorization. */
  var BACKING_VERIFIED = "verified";

  function markBacked(slug) {
    if (typeof slug !== "string" || !slug) return;
    var store = readStore();
    /* Never downgrades a confirmed backing. Re-submitting the backing form to
     * recover a contributor credential must not turn "Backed the office" back
     * into "confirm your email". */
    if (store.backed[slug] !== BACKING_VERIFIED) store.backed[slug] = true;
    writeStore(store);
  }

  function hasBacked(slug) {
    if (typeof slug !== "string" || !slug) return false;
    /* Truthy, so BOTH states count as "this browser backed" for the CTA and
     * the returning-backer panel, which care about submission, not outcome. */
    return !!readStore().backed[slug];
  }

  function hasVerifiedBacking(slug) {
    if (typeof slug !== "string" || !slug) return false;
    return readStore().backed[slug] === BACKING_VERIFIED;
  }

  /* ----------------------------------------------------------------------
   * Wall Contribution edit credentials (Milestone 11a).
   *
   * `editToken` authorises editing exactly ONE WallEntry, and only together with a
   * valid participantToken. It is a CREDENTIAL: never rendered, never written to a
   * DOM attribute or dataset, never placed in a URL, never logged, never announced,
   * and never held on the element. It is read from storage only at the moment a
   * PATCH body is built, and discarded with that request.
   *
   * `entryId` is NOT a credential — the backend contract states it is safe as a DOM
   * identifier — so it is the only half of the pair that ever reaches the markup.
   *
   * Office-scoped, mirroring `grants` and `backed`: the slug is never duplicated
   * inside a record, and one Office is addressable in a single read.
   *
   * NOTE the deliberate asymmetry with dropGrant(): dropping a participant grant does
   * NOT drop entry tokens. A revoked or expired grant is recoverable by reopening the
   * share link; an edit credential is not recoverable at all.
   *
   * No PII is stored here. The value is an opaque server token, the key a server id.
   * -------------------------------------------------------------------- */

  function entryBucket(store, slug) {
    var bucket = store.entries[slug];
    return bucket && typeof bucket === "object" ? bucket : null;
  }

  function saveEntryToken(slug, entryId, token) {
    if (typeof slug !== "string" || !slug) return;
    if (typeof entryId !== "string" || !entryId) return;
    if (typeof token !== "string" || !token) return;

    var store = readStore();
    if (!entryBucket(store, slug)) store.entries[slug] = {};
    store.entries[slug][entryId] = token;
    writeStore(store);
  }

  function readEntryToken(slug, entryId) {
    if (typeof slug !== "string" || !slug) return null;
    if (typeof entryId !== "string" || !entryId) return null;

    var bucket = entryBucket(readStore(), slug);
    if (!bucket) return null;

    var token = bucket[entryId];
    return typeof token === "string" && token ? token : null;
  }

  /* The entry ids this browser holds a USABLE credential for, in one storage read.
   * Returns ids only — no token value ever leaves this module. */
  function readOwnedEntryIds(slug) {
    if (typeof slug !== "string" || !slug) return [];

    var bucket = entryBucket(readStore(), slug);
    if (!bucket) return [];

    return Object.keys(bucket).filter(function (entryId) {
      var token = bucket[entryId];
      return typeof token === "string" && !!token;
    });
  }

  /* The counterpart of saveEntryToken, and deliberately NOT wired to any failure
   * path. A 401/403/404 cannot distinguish "this edit credential is dead" from "this
   * participant grant is dead", and absence from the Wall is not proof either — the
   * feed is capped at 50, so an older entry drops off the list while remaining
   * editable. Removing a credential on any of those signals would destroy a working
   * one, which is unrecoverable. It exists as the explicit removal primitive for a
   * future backend signal that an entry is genuinely gone. */
  function dropEntryToken(slug, entryId) {
    if (typeof slug !== "string" || !slug) return;
    if (typeof entryId !== "string" || !entryId) return;

    var store = readStore();
    var bucket = entryBucket(store, slug);
    if (!bucket) return;

    if (Object.prototype.hasOwnProperty.call(bucket, entryId)) delete bucket[entryId];
    if (!Object.keys(bucket).length) delete store.entries[slug];
    writeStore(store);
  }

  /* ----------------------------------------------------------------------
   * Contributor display name (Milestone 11a cleanup).
   *
   * A deliberate, narrow exception to "no PII in jco:v1": the display name a
   * coworker already typed into "Back this office" is kept so the contribution form
   * can prefill it. It is CONVENIENCE ONLY and carries no meaning whatsoever:
   *
   *   - it is never identity, authorization, backing deduplication, voting identity
   *     or contribution ownership;
   *   - nothing in this file ever branches on it — it is written to one input's
   *     `.value` and read back only if the user submits that input unchanged;
   *   - it is never transmitted on its own. The only value ever sent is whatever the
   *     editable Name field holds at submit time.
   *
   * The backing EMAIL is never stored, here or anywhere else.
   *
   * Office-scoped, like every other namespace. It deliberately survives dropGrant()
   * for the same reason entry tokens do: losing it on a revoked grant would be a
   * pointless regression, and it grants nothing.
   * -------------------------------------------------------------------- */

  /* Long enough for any real display name. The cap exists because writeStore() fails
   * SILENTLY on a quota error — an unbounded value here could stop participant grants
   * and edit credentials from being persisted at all. */
  var MAX_CONTRIBUTOR_NAME = 120;

  function saveContributorName(slug, name) {
    if (typeof slug !== "string" || !slug) return;
    if (typeof name !== "string") return;

    var trimmed = name.trim();
    if (!trimmed) return;

    var store = readStore();
    store.contributorNames[slug] = trimmed.slice(0, MAX_CONTRIBUTOR_NAME);
    writeStore(store);
  }

  function readContributorName(slug) {
    if (typeof slug !== "string" || !slug) return null;

    var value = readStore().contributorNames[slug];
    if (typeof value !== "string") return null;

    var trimmed = value.trim();
    return trimmed ? trimmed.slice(0, MAX_CONTRIBUTOR_NAME) : null;
  }

  /* Stable ordering for backend-supplied prompts and choices: a usable numeric
   * sortOrder wins, anything else falls back to the position the backend already
   * returned, so a missing or malformed value never randomises the order. */
  function sortValue(value, fallback) {
    var number = Number(value);
    return isFinite(number) ? number : fallback;
  }

  function bySortOrder(a, b) {
    if (a.sortOrder === b.sortOrder) return 0;
    return a.sortOrder < b.sortOrder ? -1 : 1;
  }

  /* Timestamp as a comparable number, or null when the backend gave none that parses.
   *
   * Handles BOTH wall shapes: a contribution's `submittedAt` and a backing's
   * `createdAt`. Used to order the merged timeline and to choose which owned entry the
   * prominent Edit control targets. */
  function itemTimestamp(item) {
    var raw = firstString(item, ["submittedAt", "createdAt", "created_at", "publishedAt"]);
    if (!raw) return null;
    var at = Date.parse(raw);
    return isNaN(at) ? null : at;
  }

  /* True when this entry currently has a renderable photo.
   *
   * Keyed on `photo.url` ONLY, exactly as the backend specifies: `status` is the last
   * operation's outcome, while `url` is whether a photo can actually be shown. A failed
   * or in-flight replacement therefore keeps its previous photo — and its timeline
   * event — rather than briefly losing both. */
  function hasRenderablePhoto(entry) {
    return !!(entry && entry.photo && typeof entry.photo.url === "string" && entry.photo.url);
  }

  /* Ordering key for a photo event, and whether that key is genuinely the photo's own
   * timestamp.
   *
   * `photo.updatedAt` is written only by a successful finalize and cleared by removal,
   * so it is the real answer whenever it exists. It is null for photos uploaded before
   * the column existed; the backend deliberately did not backfill it, because no stored
   * value is known to be the upload time. Those photos stay visible and fall back to the
   * entry's own submission time purely to hold a sensible position — and `dated` stays
   * false, so nothing ever displays that fallback as the date a photo was added. */
  function photoEventTime(entry) {
    var raw = entry && entry.photo ? entry.photo.updatedAt : null;
    if (typeof raw === "string" && raw) {
      var at = Date.parse(raw);
      if (!isNaN(at)) return { at: at, dated: true };
    }
    return { at: itemTimestamp(entry), dated: false };
  }

  /* Writes a backend timestamp into a <time>: machine-readable `datetime` plus the
   * viewer's own locale date.
   *
   * Shared by contributions and backing activity ON PURPOSE. They now sit in one merged
   * timeline, so two different date formats would make the ordering look wrong. This is
   * the absolute-date behaviour contributions already shipped with; nothing becomes
   * relative ("10 minutes ago") without changing both together. */
  function renderTimestamp(metaEl, timeEl, raw) {
    if (!metaEl || !timeEl) return;

    var parsed = raw ? new Date(raw) : null;
    if (!parsed || isNaN(parsed.getTime())) {
      metaEl.remove();
      return;
    }

    timeEl.setAttribute("datetime", parsed.toISOString());

    /* M19: US formatting, explicitly — "Oct 15, 2026".
     *
     * Pinned to en-US rather than the visitor's locale on purpose. This is a
     * US-store campaign whose copy is written in US English, and a browser set
     * to another locale rendered the same row as "15-10-2026" beside that copy.
     * The <time datetime> above stays machine-readable and locale-free. */
    var label;
    try {
      label = parsed.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    } catch (e) {
      label = parsed.toISOString().slice(0, 10);
    }

    setText(timeEl, label);
    metaEl.hidden = false;
  }

  /* The most recently submitted of a set of entries.
   *
   * The DTO is documented newest-first, so the first candidate is the default and is
   * only displaced by a STRICTLY later parsed timestamp. An entry with no usable date
   * therefore never jumps the queue, and a backend that stopped sorting would still
   * produce a sensible answer. */
  function mostRecentEntry(entries) {
    var best = null;
    var bestAt = null;

    for (var i = 0; i < entries.length; i++) {
      var at = itemTimestamp(entries[i]);
      if (best === null) {
        best = entries[i];
        bestAt = at;
        continue;
      }
      if (at === null) continue;
      if (bestAt === null || at > bestAt) {
        best = entries[i];
        bestAt = at;
      }
    }

    return best;
  }

  function usableTemplate(template) {
    return !!(template && template.content && template.content.firstElementChild);
  }

  /* ONE chronological timeline from the two arrays the backend returns separately.
   *
   * The split is a backend data-contract and limiting concern; the Office Wall is a
   * single visual feed. The two models stay completely unrelated here: nothing is
   * matched by id, by displayName or by any other field, no backing suppresses a
   * contribution, and nothing is converted between the two — so a coworker who backs
   * and later contributes correctly appears as two separate items.
   *
   * `backings` is read defensively: a backend deployed before M11a.1 simply omits the
   * key, which must render as a contributions-only Wall rather than an error. */
  /* Does this entry carry WRITTEN content?
   *
   * The backend's own rule for a publishable story: a message, or an answer
   * with free text, or an answer with at least one selected chip. Never the
   * mere existence of an entry, and never a photo — a photo-only contribution
   * has no story, which is the truth the status card and the activity feed
   * must agree on. Module-level so both read the same definition. */
  function entryHasStoryContent(entry) {
    if (!entry || typeof entry !== "object") return false;
    if (firstString(entry, ["message"])) return true;

    var answers = Array.isArray(entry.answers) ? entry.answers : [];
    for (var i = 0; i < answers.length; i++) {
      var answer = answers[i];
      if (!answer || typeof answer !== "object") continue;
      if (firstString(answer, ["answerText"])) return true;
      var chips = Array.isArray(answer.selectedChoiceKeys) ? answer.selectedChoiceKeys : [];
      for (var c = 0; c < chips.length; c++) {
        if (typeof chips[c] === "string" && chips[c].trim() !== "") return true;
      }
    }
    return false;
  }

  function buildTimeline(entries, backings) {
    var items = [];

    var contributions = Array.isArray(entries) ? entries : [];
    for (var i = 0; i < contributions.length; i++) {
      var entry = contributions[i];
      if (!entry || typeof entry !== "object") continue;
      /* M19: a contribution row now says "<name> submitted a contribution", so
       * it may only exist where there IS a written contribution. A photo-only
       * entry carries no message and no answers; it is already represented by
       * its own photo item, and emitting a contribution row for it as well
       * would both double-count the coworker and claim writing they never did.
       *
       * Same rule the status card uses — content, never the existence of an
       * entry — so the feed and the checklist cannot disagree. */
      if (!entryHasStoryContent(entry)) continue;
      items.push({
        type: "contribution",
        at: itemTimestamp(entry),
        order: items.length,
        data: entry
      });
    }

    /* Photo activity, DERIVED from the same entries — never a second source of truth
     * and never persisted. One contribution can produce at most one photo event: the
     * event's identity is the entry's own id, and `seenPhotoIds` enforces that even
     * though the backend already dedupes its two query passes by id.
     *
     * This iterates the FULL entries array deliberately. The backend unions a
     * newest-by-submission pass with a second pass ordered by photoUpdatedAt, so the
     * array can hold up to 100 entries — truncating it here would silently drop exactly
     * the recent photos on older contributions that second pass exists to surface. */
    var seenPhotoIds = {};
    for (var p = 0; p < contributions.length; p++) {
      var photoEntry = contributions[p];
      if (!photoEntry || typeof photoEntry !== "object") continue;
      if (!hasRenderablePhoto(photoEntry)) continue;

      var photoId = typeof photoEntry.id === "string" ? photoEntry.id.trim() : "";
      if (!photoId || seenPhotoIds[photoId] === true) continue;
      seenPhotoIds[photoId] = true;

      var when = photoEventTime(photoEntry);
      items.push({
        type: "photo",
        at: when.at,
        dated: when.dated,
        order: items.length,
        data: photoEntry
      });
    }

    var activity = Array.isArray(backings) ? backings : [];
    for (var b = 0; b < activity.length; b++) {
      var backing = activity[b];
      if (!backing || typeof backing !== "object") continue;
      items.push({
        type: "backing",
        at: itemTimestamp(backing),
        order: items.length,
        data: backing
      });
    }

    /* Newest first. `order` keeps the sort stable across engines and keeps an item the
     * backend gave no usable timestamp for in a predictable place — last, rather than
     * at an arbitrary position decided by comparison order. */
    items.sort(function (a, b2) {
      if (a.at === null && b2.at === null) return a.order - b2.order;
      if (a.at === null) return 1;
      if (b2.at === null) return -1;
      if (a.at !== b2.at) return b2.at - a.at;

      /* An exact tie falls back to the backend's own convention — compare ids — so two
       * items sharing a timestamp order identically on every render and in every
       * engine. A contribution and its own photo tie this way whenever photo.updatedAt
       * is absent, and `order` settles anything still equal. */
      var aId = a.data && typeof a.data.id === "string" ? a.data.id : "";
      var bId = b2.data && typeof b2.data.id === "string" ? b2.data.id : "";
      if (aId && bId && aId !== bId) return aId.localeCompare(bId);
      return a.order - b2.order;
    });

    return items;
  }

  /* ----------------------------------------------------------------------
   * Photo pipeline (Milestone 11b).
   *
   * Image BYTES never pass through the Shopify App Proxy. The proxy carries only
   * small control-plane JSON; the bytes go browser -> private Supabase Storage
   * directly, using a short-lived capability the backend minted for one
   * server-chosen object path. The browser never sees a bucket name, an object
   * path or any Storage credential, and never constructs a path of its own.
   * -------------------------------------------------------------------- */

  /* Mirrors the backend's photo-config.server.ts. These are UX guards that fail
   * fast and explain themselves — the backend re-validates everything and stays
   * authoritative. */
  /* A generous ceiling that still bounds the field. The backend validates the
   * value it stores; this only stops an obviously unusable request leaving. */
  var PROFILE_CITY_MAX = 120;

  var PHOTO_MAX_BYTES = 8 * 1024 * 1024;
  var PHOTO_MAX_PIXELS = 25000000;
  /* The backend canonicalises to JPEG q80 at a 1600px longest edge, so there is
   * nothing to gain by uploading a larger converted image. */
  var PHOTO_CANONICAL_EDGE = 1600;
  var PHOTO_JPEG_QUALITY = 0.8;
  var PHOTO_CONVERT_TIMEOUT_MS = 30000;

  /* A failure carrying a locale key, so every path reports something a person can
   * act on rather than a raw exception. */
  function PhotoError(stringKey) {
    this.stringKey = stringKey;
  }

  function photoFail(stringKey) {
    return Promise.reject(new PhotoError(stringKey));
  }

  /* Reads enough of the file to identify it by signature. Blob.arrayBuffer() is
   * missing on older iOS Safari — one of the platforms this must work on — so
   * FileReader is kept as a fallback. */
  function readFileHead(file) {
    var slice = file.slice(0, 64);
    if (typeof slice.arrayBuffer === "function") {
      return slice.arrayBuffer().then(function (buf) {
        return new Uint8Array(buf);
      });
    }
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () {
        resolve(new Uint8Array(reader.result));
      };
      reader.onerror = function () {
        reject(reader.error);
      };
      reader.readAsArrayBuffer(slice);
    });
  }

  function asciiAt(bytes, from, length) {
    var s2 = "";
    for (var i = from; i < from + length && i < bytes.length; i++) {
      var c = bytes[i];
      s2 += c >= 32 && c <= 126 ? String.fromCharCode(c) : ".";
    }
    return s2;
  }

  var HEIC_BRANDS = ["heic", "heix", "hevc", "hevx", "heim", "heis", "hevm", "hevs"];
  var GENERIC_HEIF_BRANDS = ["mif1", "msf1"];
  var AVIF_BRANDS = ["avif", "avis"];

  /* The authoritative signal. Filename and MIME are never trusted: Safari hands over
   * a `.jpg` name and an image/jpeg type for an HEIC source, and the reverse —
   * image/jpeg declared over HEIC bytes — is exactly what this has to catch. */
  function photoFamily(bytes) {
    if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "jpeg";
    if (
      bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e &&
      bytes[3] === 0x47 && bytes[4] === 0x0d && bytes[5] === 0x0a && bytes[6] === 0x1a && bytes[7] === 0x0a
    ) return "png";
    if (bytes.length >= 12 && asciiAt(bytes, 0, 4) === "RIFF" && asciiAt(bytes, 8, 4) === "WEBP") return "webp";

    if (bytes.length >= 12 && asciiAt(bytes, 4, 4) === "ftyp") {
      var brands = [asciiAt(bytes, 8, 4)];
      for (var o = 16; o + 4 <= bytes.length; o += 4) brands.push(asciiAt(bytes, o, 4));
      brands = brands.map(function (b) { return b.toLowerCase(); });

      function has(set) {
        return brands.some(function (b) { return set.indexOf(b) !== -1; });
      }
      /* AVIF before HEIF: an AVIF legitimately carries `mif1` as a compatible
       * brand, so the generic test alone would mislabel every AVIF as HEIC. */
      if (has(AVIF_BRANDS)) return "avif";
      if (has(HEIC_BRANDS) || has(GENERIC_HEIF_BRANDS)) return "heif";
    }
    return "unknown";
  }

  /* Lazily loads the vendored HEIC decoder, once per page.
   *
   * ~700 KB gzipped, fetched ONLY when the bytes are genuinely HEIC/HEIF and the
   * browser could not decode them itself — so a JPEG, PNG or WebP upload, and every
   * visitor who never adds a photo, pays nothing at all. Vendored in this
   * repository; never a public CDN. */
  var heicDecoderPromise = null;

  function loadHeicDecoder(url) {
    if (heicDecoderPromise) return heicDecoderPromise;
    heicDecoderPromise = new Promise(function (resolve, reject) {
      if (!url) { reject(new PhotoError("photoErrConvert")); return; }
      var el = document.createElement("script");
      el.src = url;
      el.async = true;
      el.onload = function () {
        if (typeof window.libheif === "undefined") { reject(new PhotoError("photoErrConvert")); return; }
        resolve(typeof window.libheif === "function" ? window.libheif() : window.libheif);
      };
      el.onerror = function () { reject(new PhotoError("photoErrConvert")); };
      document.head.appendChild(el);
    });
    return heicDecoderPromise;
  }

  function withPhotoTimeout(promise, ms) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var timer = setTimeout(function () {
        if (!settled) { settled = true; reject(new PhotoError("photoErrConvert")); }
      }, ms);
      promise.then(
        function (v) { if (!settled) { settled = true; clearTimeout(timer); resolve(v); } },
        function (e) { if (!settled) { settled = true; clearTimeout(timer); reject(e); } }
      );
    });
  }

  /* Fits within the canonical longest edge without ever enlarging, matching what
   * the backend would do anyway — so the upload is smaller and the browser holds
   * less pixel data. */
  /* M22 — ONE preparation pipeline, used by both photo flows.
   *
   * Extracted from `onPhotoSelected()` unchanged so the office photo gets the
   * same sniffing, the same HEIC handling and the same ceiling as a
   * contribution photo, rather than a second, weaker copy of them. The format
   * is decided by the file's own bytes, never by its name or its reported MIME
   * type, both of which the browser takes from the client.
   *
   * Throws PhotoError; `onStatus` is optional and receives a string key. */
  async function preparePhotoFile(file, decoderUrl, onStatus) {
    var bytes;
    try {
      bytes = await readFileHead(file);
    } catch (e) {
      throw new PhotoError("photoErrUnsupported");
    }

    var family = photoFamily(bytes);

    if (family === "jpeg" || family === "png" || family === "webp") {
      /* Already something the backend accepts. It is NOT re-encoded here: the
       * backend canonicalises anyway, and a needless extra round of lossy
       * compression would only cost quality. */
      if (file.size > PHOTO_MAX_BYTES) throw new PhotoError("photoErrTooLarge");
      return file;
    }

    if (family === "heif") {
      if (onStatus) onStatus("photoConverting");
      var blob = await withPhotoTimeout(
        heicViaBrowser(file).catch(function (e) {
          /* Only a decode failure justifies pulling the decoder down. A pixel
           * budget rejection is a real answer and must not be retried. */
          if (e instanceof PhotoError && e.stringKey === "photoErrDimensions") throw e;
          return heicViaDecoder(file, decoderUrl);
        }),
        PHOTO_CONVERT_TIMEOUT_MS
      );
      if (blob.size > PHOTO_MAX_BYTES) throw new PhotoError("photoErrTooLarge");
      return new File([blob], (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", {
        type: "image/jpeg"
      });
    }

    throw new PhotoError("photoErrUnsupported");
  }

  function fittedSize(width, height) {
    var longest = Math.max(width, height);
    if (!longest || longest <= PHOTO_CANONICAL_EDGE) return { width: width, height: height };
    var scale = PHOTO_CANONICAL_EDGE / longest;
    return {
      width: Math.max(1, Math.round(width * scale)),
      height: Math.max(1, Math.round(height * scale))
    };
  }

  function canvasToJpegBlob(canvas) {
    return new Promise(function (resolve, reject) {
      if (typeof canvas.toBlob !== "function") { reject(new PhotoError("photoErrConvert")); return; }
      canvas.toBlob(function (blob) {
        blob ? resolve(blob) : reject(new PhotoError("photoErrConvert"));
      }, "image/jpeg", PHOTO_JPEG_QUALITY);
    });
  }

  function drawToJpeg(source, width, height) {
    var size = fittedSize(width, height);
    var canvas = document.createElement("canvas");
    canvas.width = size.width;
    canvas.height = size.height;
    canvas.getContext("2d").drawImage(source, 0, 0, size.width, size.height);
    return canvasToJpegBlob(canvas);
  }

  /* PATH 1 — let the browser do it. Costs no payload, and `from-image` asks the
   * browser to apply EXIF orientation itself. If a browser ever gains native HEIC
   * support this path quietly takes over and the decoder stops being downloaded. */
  function heicViaBrowser(file) {
    if (typeof createImageBitmap !== "function") return photoFail("photoErrConvert");
    return createImageBitmap(file, { imageOrientation: "from-image" }).then(function (bitmap) {
      if (bitmap.width * bitmap.height > PHOTO_MAX_PIXELS) {
        if (bitmap.close) bitmap.close();
        throw new PhotoError("photoErrDimensions");
      }
      return drawToJpeg(bitmap, bitmap.width, bitmap.height).then(function (blob) {
        if (bitmap.close) bitmap.close();
        return blob;
      });
    });
  }

  /* PATH 2 — the vendored libheif build, which carries the libde265 HEVC decoder
   * the server-side image library does not have. libheif applies the container's
   * own rotation/mirror properties while decoding. */
  function heicViaDecoder(file, decoderUrl) {
    return loadHeicDecoder(decoderUrl)
      .then(function () {
        return file.arrayBuffer ? file.arrayBuffer() : new Response(file).arrayBuffer();
      })
      .then(function (buf) {
        var libheif = typeof window.libheif === "function" ? window.libheif() : window.libheif;
        var images = new libheif.HeifDecoder().decode(new Uint8Array(buf));
        if (!images || !images.length) throw new PhotoError("photoErrConvert");

        /* Multi-image containers are real. Only the primary image is ever used —
         * no sequence is animated, and the backend rejects animation anyway. */
        var image = images[0];
        var w = image.get_width();
        var h = image.get_height();
        if (!w || !h) throw new PhotoError("photoErrConvert");
        if (w * h > PHOTO_MAX_PIXELS) throw new PhotoError("photoErrDimensions");

        var full = document.createElement("canvas");
        full.width = w;
        full.height = h;
        var ctx = full.getContext("2d");
        var imageData = ctx.createImageData(w, h);

        return new Promise(function (resolve, reject) {
          image.display(imageData, function (rendered) {
            if (!rendered) { reject(new PhotoError("photoErrConvert")); return; }
            ctx.putImageData(rendered, 0, 0);
            resolve(drawToJpeg(full, w, h));
          });
        });
      });
  }

  /* Reduces a backend key to something safe to use inside an element id and a
   * matching `for`. The RAW key is never used as an id — it travels in the checkbox
   * `value` and in data-jcw-prompt-key, where no escaping is required. */
  function safeIdPart(value) {
    if (typeof value !== "string" || !value) return "x";
    var cleaned = value.replace(/[^A-Za-z0-9_-]/g, "");
    return cleaned || "x";
  }

  /* Credential for WRITE requests (backing).
   *
   * Deliberately narrower than resolveCredential(): it can return a participant
   * grant and nothing else. The raw Office access token is a bootstrap credential
   * for reads only and is NEVER accepted by the backing endpoint, so this function
   * does not reference CREDENTIALS at all. If no grant exists the caller must fail
   * safely rather than fall back. */
  /* The raw Office ACCESS token, for building a share link and nothing else.
   *
   * Deliberately separate from resolveWriteToken(), which must never return
   * this value: the write endpoints do not accept it, and keeping the two
   * accessors apart means a careless call site cannot post it to one.
   *
   * It exists only when this page was opened from a share link — it is
   * captured from the fragment at bootstrap, held in this closure, and never
   * persisted to storage, written to the DOM, logged or put in the address
   * bar. Scoped to its own office, so a stale token can never be attached to
   * a different Wall. */
  function rawAccessToken(slug) {
    if (typeof slug !== "string" || !slug) return null;
    /* This page load's own link first, then the copy kept for this office.
     * The stored copy is what makes Share survive a refresh — see the note on
     * the `share` namespace in readStore(). */
    if (CREDENTIALS.office === slug && CREDENTIALS.access) return CREDENTIALS.access;
    return readShareToken(slug);
  }

  function resolveWriteToken(slug) {
    if (typeof slug !== "string" || !slug) return null;
    if (SESSION_GRANT && SESSION_GRANT.slug === slug && SESSION_GRANT.token) {
      return SESSION_GRANT.token;
    }
    return readGrantToken(slug);
  }

  class CampaignOfficeWallElement extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      this.promptLabels = readTextMap(this, "[data-jcw-prompt-labels]", "data-prompt-key");
      /* Campaign display copy for the simplified prompts, keyed by backend
       * promptKey. Display only — see the comment on these blocks in the
       * section. A key absent from a map simply falls back to the DTO. */
      this.promptHeadings = readTextMap(this, "[data-jcw-prompt-headings]", "data-prompt-key");
      this.promptQuestions = readTextMap(this, "[data-jcw-prompt-questions]", "data-prompt-key");
      this.promptPlaceholders = readTextMap(this, "[data-jcw-prompt-placeholders]", "data-prompt-key");
      this.strings = readTextMap(this, "[data-jcw-strings]", "data-string-key");

      this.els = {
        live: this.querySelector("[data-jcw-live]"),
        loading: this.querySelector("[data-jcw-loading]"),
        content: this.querySelector("[data-jcw-content]"),
        company: this.querySelector("[data-jcw-company]"),
        location: this.querySelector("[data-jcw-location]"),
        backing: this.querySelector("[data-jcw-backing]"),
        entries: this.querySelector("[data-jcw-entries]"),
        empty: this.querySelector("[data-jcw-empty]"),
        privateMsg: this.querySelector("[data-jcw-private]"),
        errorMsg: this.querySelector("[data-jcw-error]"),
        retry: this.querySelector("[data-jcw-retry]"),

        /* M17 presentation. All host-scoped, like every other hook here. */
        locationText: this.querySelector("[data-jcw-location-text]"),
        officeMedia: this.querySelector("[data-jcw-office-media]"),
        officeImage: this.querySelector("[data-jcw-office-image]"),
        statEntries: this.querySelector("[data-jcw-stat-entries]"),
        statEntriesLabel: this.querySelector("[data-jcw-stat-entries-label]"),
        statBackings: this.querySelector("[data-jcw-stat-backings]"),
        statBackingsLabel: this.querySelector("[data-jcw-stat-backings-label]"),
        statPhotos: this.querySelector("[data-jcw-stat-photos]"),
        statPhotosLabel: this.querySelector("[data-jcw-stat-photos-label]"),
        checklist: this.querySelector("[data-jcw-checklist]"),
        photoCard: this.querySelector("[data-jcw-photo-card]"),
        photoCardFigure: this.querySelector("[data-jcw-photo-card-figure]"),
        photoCardImage: this.querySelector("[data-jcw-photo-card-image]"),
        photoCardTitle: this.querySelector("[data-jcw-photo-card-title]"),
        /* M30 — the empty-frame invitation, presentation only. */
        photoCardPlaceholder: this.querySelector("[data-jcw-photo-card-placeholder]"),
        photoCardPlaceholderLabel: this.querySelector("[data-jcw-photo-card-placeholder-label]"),
        photoCardCta: this.querySelector("[data-jcw-photo-card-cta]"),
        photoCardCtaLabel: this.querySelector("[data-jcw-photo-card-cta-label]"),
        shareBtn: this.querySelector("[data-jcw-share]"),
        shareStatus: this.querySelector("[data-jcw-share-status]"),
        verifyNote: this.querySelector("[data-jcw-verify-note]"),
        moderationNotice: this.querySelector("[data-jcw-moderation-notice]"),
        moderationStory: this.querySelector("[data-jcw-moderation-story]"),
        moderationPhoto: this.querySelector("[data-jcw-moderation-photo]"),
        statsList: this.querySelector("[data-jcw-stats]"),
        statEntriesList: document.querySelector("[data-jcw-stat-entries-list]"),
        statEntriesEmpty: document.querySelector("[data-jcw-stat-entries-empty]"),
        statBackingsCount: document.querySelector("[data-jcw-stat-backings-count]"),
        statBackingsNote: document.querySelector("[data-jcw-stat-backings-note]"),
        statBackingsList: document.querySelector("[data-jcw-stat-backings-list]"),
        statBackingsEmpty: document.querySelector("[data-jcw-stat-backings-empty]"),
        statPhotosList: document.querySelector("[data-jcw-stat-photos-list]"),
        statPhotosEmpty: document.querySelector("[data-jcw-stat-photos-empty]"),

        /* M17B */
        backingHeading: this.querySelector("[data-jcw-backing-heading]"),
        storyField: this.querySelector("[data-jcw-contrib-story-field]"),
        sheet: this.querySelector("[data-jcw-sheet]"),
        sheetPanel: this.querySelector("[data-jcw-sheet-panel]"),
        sheetSlot: this.querySelector("[data-jcw-sheet-slot]"),
        sheetTitle: this.querySelector("[data-jcw-sheet-title]"),
        sheetClose: this.querySelector("[data-jcw-sheet-close]"),
        sheetBackdrop: this.querySelector("[data-jcw-sheet-backdrop]"),
        photoLabel: this.querySelector("[data-jcw-photo-label]")
      };

      /* The full editor's own photo label, kept so photo-only mode can swap it
       * and swap it back rather than hardcoding either wording here. */
      this.photoLabelDefault = this.els.photoLabel ? this.els.photoLabel.textContent : "";

      /* M17B sheet state. `sheetOpen` is the single source of truth for whether
       * the contribution shell is currently hosted in the fullscreen sheet. */
      this.sheetOpen = false;
      this.sheetReturnFocus = null;
      this.contribFocus = "all";

      /* Customizer copy, read once. Attribute values only — never data, never a
       * credential. Every one has a non-blank schema default, so a missing
       * attribute means the section was rendered by an older template, and the
       * empty string it yields simply renders nothing rather than "undefined". */
      this.copy = {
        statEntriesOne: this.getAttribute("data-jcw-stat-entries-one") || "",
        statEntriesOther: this.getAttribute("data-jcw-stat-entries-other") || "",
        statBackingsOne: this.getAttribute("data-jcw-stat-backings-one") || "",
        statBackingsOther: this.getAttribute("data-jcw-stat-backings-other") || "",
        statPhotosOne: this.getAttribute("data-jcw-stat-photos-one") || "",
        statPhotosOther: this.getAttribute("data-jcw-stat-photos-other") || "",
        nominated: this.getAttribute("data-jcw-label-nominated") || "",
        storyDone: this.getAttribute("data-jcw-label-story-done") || "",
        storyTodo: this.getAttribute("data-jcw-label-story-todo") || "",
        photoDone: this.getAttribute("data-jcw-label-photo-done") || "",
        photoTodo: this.getAttribute("data-jcw-label-photo-todo") || "",
        photoTitleHas: this.getAttribute("data-jcw-photo-title-has") || "",
        photoTitleNone: this.getAttribute("data-jcw-photo-title-none") || "",
        photoCtaChange: this.getAttribute("data-jcw-photo-cta-change") || "",
        photoCtaAdd: this.getAttribute("data-jcw-photo-cta-add") || "",
        photoPlaceholder: this.getAttribute("data-jcw-photo-placeholder") || "",
        photoSheetTitle: this.getAttribute("data-jcw-photo-sheet-title") || "",
        photoTitleRetry: this.getAttribute("data-jcw-photo-title-retry") || "",
        photoCtaRetry: this.getAttribute("data-jcw-photo-cta-retry") || "",
        photoRetry: this.getAttribute("data-jcw-label-photo-retry") || "",
        photoNoFile: this.getAttribute("data-jcw-photo-no-file") || "",
        photoFieldLabelOnly: this.getAttribute("data-jcw-photo-field-label-only") || "",
        photoErrBegin: this.getAttribute("data-jcw-photo-err-begin") || "",
        photoErrUpload: this.getAttribute("data-jcw-photo-err-upload") || "",
        photoErrFinalize: this.getAttribute("data-jcw-photo-err-finalize") || "",
        photoErrRemove: this.getAttribute("data-jcw-photo-err-remove") || "",
        contribErrCreate: this.getAttribute("data-jcw-contrib-err-create") || "",
        discardPrompt: this.getAttribute("data-jcw-discard-prompt") || ""
      };

      /* The shared dialog is resolved DOCUMENT-scoped, not through `this`.
       *
       * ModalDialog.connectedCallback() in assets/base.js runs
       * `document.body.appendChild(this)` on upgrade, and base.js is deferred from the
       * layout head so it executes before this script. By the time this element
       * initialises, the dialog has already been moved out of the host and is a child
       * of <body>, so a host-scoped lookup returns null. The host carries the dialog's
       * id (an element id only — never entry data and never a credential), which is the
       * same approach the theme's own ModalOpener uses. Its children are then resolved
       * through the dialog element rather than through the host. */
      var modalEl = document.getElementById(this.getAttribute("data-jcw-modal-id") || "");
      this.els.modal = modalEl;
      this.els.modalAuthor = modalEl ? modalEl.querySelector("[data-jcw-modal-author]") : null;
      this.els.modalRole = modalEl ? modalEl.querySelector("[data-jcw-modal-role]") : null;
      this.els.modalMessage = modalEl ? modalEl.querySelector("[data-jcw-modal-message]") : null;
      this.els.modalAnswers = modalEl ? modalEl.querySelector("[data-jcw-modal-answers]") : null;
      this.els.modalFigure = modalEl ? modalEl.querySelector("[data-jcw-modal-figure]") : null;
      this.els.modalPhoto = modalEl ? modalEl.querySelector("[data-jcw-modal-photo]") : null;
      this.els.modalPhotoFallback = modalEl ? modalEl.querySelector("[data-jcw-modal-photo-fallback]") : null;
      this.els.modalPhotoFallbackText = modalEl
        ? modalEl.querySelector("[data-jcw-modal-photo-fallback-text]")
        : null;
      this.els.modalPhotoRetry = modalEl ? modalEl.querySelector("[data-jcw-modal-photo-retry]") : null;

      this.els.officeAvatar = this.querySelector("[data-jcw-office-avatar]");
      this.els.officePhotoFull = null;
      this.els.officePhotoTitle = null;
      this.els.prizeOpen = this.querySelector("[data-jcw-prize-open]");
      this.els.accessOpen = this.querySelector("[data-jcw-access-open]");
      this.els.photoCardFigure = this.querySelector("[data-jcw-photo-card-figure]");
      this.els.photoCardImage = this.querySelector("[data-jcw-photo-card-image]");
      this.els.photoCardTitle = this.querySelector("[data-jcw-photo-card-title]");
      this.els.photoCardPlaceholder = this.querySelector("[data-jcw-photo-card-placeholder]");
      this.els.photoCardPlaceholderLabel = this.querySelector("[data-jcw-photo-card-placeholder-label]");
      this.els.stack = this.querySelector("[data-jcw-stack]");
      this.els.officeCard = this.querySelector(".jcw-office");
      this.els.ctaGrid = this.querySelector(".jcw-grid");
      this.els.backingSection = this.querySelector("[data-jcw-backing-section]");

      /* Role-aware introduction (M23). */
      this.els.intro = this.querySelector("[data-jcw-intro]");
      this.els.introTitle = this.querySelector("[data-jcw-intro-title]");
      this.els.introBody = this.querySelector("[data-jcw-intro-body]");
      this.els.introAccent = this.querySelector("[data-jcw-intro-accent]");

      /* Office profile (M22). Present in the markup for everyone; revealed only
       * for the verified nominator by applyProfileControls(). */
      this.els.profileEdit = this.querySelector("[data-jcw-profile-edit]");
      this.els.profilePanel = this.querySelector("[data-jcw-profile-panel]");
      this.els.profileForm = this.querySelector("[data-jcw-profile-form]");
      this.els.profileCity = this.querySelector("[data-jcw-profile-city]");
      this.els.profileCountry = this.querySelector("[data-jcw-profile-country]");
      this.els.profilePhotoInput = this.querySelector("[data-jcw-profile-photo-input]");
      this.els.profilePhotoCurrent = this.querySelector("[data-jcw-profile-photo-current]");
      this.els.profilePhotoCurrentImg = this.querySelector("[data-jcw-profile-photo-current-img]");
      this.els.profilePhotoRemove = this.querySelector("[data-jcw-profile-photo-remove]");
      this.els.profilePhotoChosen = this.querySelector("[data-jcw-profile-photo-chosen]");
      this.els.profilePhotoName = this.querySelector("[data-jcw-profile-photo-name]");
      this.els.profilePhotoClear = this.querySelector("[data-jcw-profile-photo-clear]");
      this.els.profileSave = this.querySelector("[data-jcw-profile-save]");
      this.els.profileSaveLabel = this.querySelector("[data-jcw-profile-save-label]");
      this.els.profileCancel = this.querySelector("[data-jcw-profile-cancel]");
      this.els.profileStatus = this.querySelector("[data-jcw-profile-status]");
      this.els.profileError = this.querySelector("[data-jcw-profile-error]");

      /* Backing (Milestone 10). All inside the host — unlike the dialog, nothing
       * relocates these. */
      this.els.backReady = this.querySelector("[data-jcw-back-ready]");
      this.els.backRecoverHelp = this.querySelector("[data-jcw-back-recover-help]");
      this.els.backToggle = this.querySelector("[data-jcw-back-toggle]");
      this.els.backToggleLabel = this.querySelector("[data-jcw-back-toggle-label]");
      this.els.backPanel = this.querySelector("[data-jcw-back-panel]");
      this.els.backForm = this.querySelector("[data-jcw-back-form]");
      this.els.backName = this.querySelector("[data-jcw-back-name]");
      this.els.backEmail = this.querySelector("[data-jcw-back-email]");
      this.els.backConsent = this.querySelector("[data-jcw-back-consent]");
      this.els.backSubmit = this.querySelector("[data-jcw-back-submit]");
      this.els.backSubmitLabel = this.querySelector("[data-jcw-back-submit-label]");
      this.els.backError = this.querySelector("[data-jcw-back-error]");
      this.els.backSuccess = this.querySelector("[data-jcw-back-success]");

      /* Wall Contribution (Milestone 11a). One shell, one form, for the whole page. */
      this.verifyNoteId = this.els.verifyNote ? this.els.verifyNote.id || "" : "";
      /* A deferred reorder can also be waiting on an open dialog. Dialogs close
       * by dropping their `open` attribute and dispatch nothing, so that
       * attribute is observed directly — cheaper and more reliable than
       * polling or guessing at a close event. */
      try {
        var selfObs = this;
        var dialogObserver = new MutationObserver(function () {
          selfObs.flushDashboardOrder();
        });
        var campaignDialogs = document.querySelectorAll(".jcw__modal");
        for (var di = 0; di < campaignDialogs.length; di++) {
          dialogObserver.observe(campaignDialogs[di], {
            attributes: true,
            attributeFilter: ["open"]
          });
        }
      } catch (e) {
        /* Without MutationObserver a pending reorder simply waits for the next
         * breakpoint change or Wall refresh. Nothing breaks. */
      }

      /* M24H — dashboard order. Read once; the breakpoint is watched live. */
      this.dashDesktop = this.getAttribute("data-jcw-dash-desktop") === "below" ? "below" : "above";
      this.dashMobile = this.getAttribute("data-jcw-dash-mobile") === "below" ? "below" : "above";
      try {
        this.dashQuery = window.matchMedia("(max-width: 749px)");
        var self2 = this;
        var onChange = function () { self2.applyDashboardOrder(); };
        if (typeof this.dashQuery.addEventListener === "function") {
          this.dashQuery.addEventListener("change", onChange);
        } else if (typeof this.dashQuery.addListener === "function") {
          /* Safari < 14. */
          this.dashQuery.addListener(onChange);
        }
      } catch (e) {
        /* Without matchMedia the DOM simply keeps its authored order, which is
         * the "above" default for both breakpoints. */
        this.dashQuery = null;
      }

      this.prizeDialogId = this.els.prizeOpen
        ? this.els.prizeOpen.getAttribute("aria-controls") || ""
        : "";

      /* Same rule as the prize dialog: resolved by id, because ModalDialog has
       * already moved the access dialog into <body>. */
      this.accessDialogId = this.els.accessOpen
        ? this.els.accessOpen.getAttribute("aria-controls") || ""
        : "";
      this.access = null;
      this.accessBusy = false;

      /* Resolved through the dialog element, not the host: ModalDialog moves
       * itself to <body> on upgrade, so a host-scoped lookup returns null —
       * the same rule the entry dialog already follows. */
      this.officePhotoDialogId = this.els.officeMedia
        ? this.els.officeMedia.getAttribute("aria-controls") || ""
        : "";
      var photoDialog = document.getElementById(this.officePhotoDialogId || "");
      this.els.officePhotoFull = photoDialog
        ? photoDialog.querySelector("[data-jcw-office-photo-full]")
        : null;
      this.els.officePhotoTitle = photoDialog
        ? photoDialog.querySelector("[data-jcw-office-photo-title]")
        : null;

      this.els.next = this.querySelector("[data-jcw-next]");
      this.els.nextCreate = this.querySelector("[data-jcw-next-create]");
      this.els.nextOwned = this.querySelector("[data-jcw-next-owned]");
      this.els.stronger = this.querySelector("[data-jcw-stronger]");
      this.els.topEdit = this.querySelector("[data-jcw-top-edit]");
      this.els.contribCreateSlot = this.querySelector("[data-jcw-contrib-create-slot]");
      this.els.contribPanel = this.querySelector("[data-jcw-contrib-panel]");
      this.els.contribForm = this.querySelector("[data-jcw-contrib-form]");
      this.els.contribTitle = this.querySelector("[data-jcw-contrib-title]");
      this.els.contribName = this.querySelector("[data-jcw-contrib-name]");
      this.els.contribMessage = this.querySelector("[data-jcw-contrib-message]");
      this.els.contribPrompts = this.querySelector("[data-jcw-contrib-prompts]");
      this.els.contribError = this.querySelector("[data-jcw-contrib-error]");
      this.els.contribRetry = this.querySelector("[data-jcw-contrib-retry]");
      this.els.contribRetryTitle = this.querySelector("[data-jcw-contrib-retry-title]");
      this.els.contribRetryCta = this.querySelector("[data-jcw-contrib-retry-cta]");
      this.els.contribSubmit = this.querySelector("[data-jcw-contrib-submit]");
      this.els.contribSubmitLabel = this.querySelector("[data-jcw-contrib-submit-label]");
      this.els.contribCancel = this.querySelector("[data-jcw-contrib-cancel]");
      this.els.photoField = this.querySelector("[data-jcw-photo-field]");
      this.els.photoInput = this.querySelector("[data-jcw-photo-input]");
      this.els.photoCurrent = this.querySelector("[data-jcw-photo-current]");
      this.els.photoCurrentImg = this.querySelector("[data-jcw-photo-current-img]");
      this.els.photoCurrentNote = this.querySelector("[data-jcw-photo-current-note]");
      this.els.photoRemoveBtn = this.querySelector("[data-jcw-photo-remove]");
      this.els.photoChosen = this.querySelector("[data-jcw-photo-chosen]");
      this.els.photoPreview = this.querySelector("[data-jcw-photo-preview]");
      this.els.photoName = this.querySelector("[data-jcw-photo-name]");
      this.els.photoClearBtn = this.querySelector("[data-jcw-photo-clear]");
      this.els.photoStatus = this.querySelector("[data-jcw-photo-status]");
      this.els.photoError = this.querySelector("[data-jcw-photo-error]");
      this.els.contribSuccess = this.querySelector("[data-jcw-contrib-success]");
      this.els.contribSuccessTitle = this.querySelector("[data-jcw-contrib-success-title]");

      /* The MOVED unit. Success region and panel travel together inside it, so a
       * confirmation always appears where the form was. Resolved explicitly rather
       * than as the success element's parent — that would resolve to the create SLOT,
       * and appending the slot to itself throws. */
      this.els.contribShell = this.querySelector("[data-jcw-contrib-shell]");

      /* In-flight locks. Not `disabled` on the focused submit button, which would
       * drop focus to <body>; aria-disabled plus these flags keeps focus intact.
       * Backing and contribution hold SEPARATE locks — one in flight must never
       * block or unlock the other. */
      this.backingBusy = false;
      this.contributionBusy = false;
      /* Its own lock, for the same reason: a share sheet left open must not
       * block backing or contributing, and a second press of Share while the
       * sheet is up must not open a second one. */
      this.sharing = false;
      /* Slug of the Office currently rendered — set on a successful load. */
      this.currentSlug = null;

      /* Contribution form state. Held here, never in the DOM. `contribEntryId` is an
       * identifier and is also mirrored onto the entry row; the edit TOKEN is not
       * kept on the instance at all and is read from storage per request. */
      this.contribMode = "create";
      this.contribEntryId = null;
      this.contribOpen = false;
      this.contribReady = false;
      this.contribDirty = false;
      this.prompts = [];
      this.promptIndex = {};
      /* Both are decided from the Wall DTO on every load. They start in the
       * conservative state so nothing depends on a payload having arrived. */
      this.simplifiedPrompts = false;
      this.supportsAnswerPatch = false;
      /* Entries on the CURRENT Wall that this browser holds an edit credential for,
       * and the one the prominent Edit control targets. Recomputed on every render, so
       * a credential for an entry the Wall no longer returns can never influence the
       * UI — in particular it can never suppress the create call to action. */
      this.ownedVisible = [];
      this.topEditEntry = null;
      /* Owned entries that moderation has frozen, per office, and which owned
       * entries have already been asked about during this page's lifetime. See
       * refreshOwnModeration(). Memory only: never written to `jco:v1`. */
      this.frozenEntries = {};
      this.moderationChecked = {};
      /* The control that opened the form, so a cancel returns focus to it.
       * `contribOpener` deliberately OUTLIVES a collapse, so a form that was
       * collapsed with unsaved work can still be re-revealed and pointed at. */
      this.contribTrigger = null;
      this.contribOpener = null;

      /* Photo state. The chosen File is held here, never in the DOM, and is never
       * part of the create/edit JSON payload — the bytes go straight to Storage. */
      this.photoFile = null;
      this.photoPreviewUrl = null;
      this.photoRemoveRequested = false;
      this.photoExistingUrl = null;
      this.photoBusy = false;
      this.heicDecoderUrl = this.getAttribute("data-jcw-heic-decoder") || "";
      this.contribPanelId = this.getAttribute("data-jcw-panel-id") || "";
      this.contribIdBase = "Jcw" + safeIdPart(this.getAttribute("data-jcw-field-prefix"));

      var self = this;

      if (this.els.backToggle && this.els.backPanel) {
        this.els.backToggle.addEventListener("click", this.toggleBackingPanel.bind(this));
      }
      if (this.els.backForm) {
        this.els.backForm.addEventListener("submit", this.submitBacking.bind(this));
      }

      if (this.els.stronger) {
        this.els.stronger.addEventListener("click", function () {
          /* A second click on an open create form collapses it WITHOUT clearing what
           * has been typed — only an explicit Cancel discards content. */
          if (self.contribOpen && self.contribMode === "create") {
            self.closeContribution(false);
            self.focusElement(self.els.stronger);
            return;
          }
          /* M29 — ADD THE STORY TO THE ENTRY THIS BROWSER ALREADY OWNS.
           *
           * This always created a NEW entry, which is what split a single
           * participant's contribution in two: upload a photo (entry A), then
           * press this (entry B, story only). The photo card then looked at
           * the most recent owned entry — B — found no photo on it, and fell
           * back to "Add your photo" with the polaroid gone, while the photo
           * statistic still counted A and was right.
           *
           * One participant, one entry. Editing the owned entry is also what
           * the per-row Edit control has always done, so this is the existing
           * path rather than a new one. */
          var ownedTarget = self.ownedEntry();
          if (ownedTarget) {
            self.openContribution("edit", ownedTarget, self.els.stronger);
          } else {
            self.openContribution("create", null, self.els.stronger);
          }
        });
      }

      if (this.els.topEdit) {
        this.els.topEdit.addEventListener("click", function () {
          var target = self.topEditEntry;
          if (!target) return;

          var id = typeof target.id === "string" ? target.id.trim() : "";

          /* A second click on the already-open editor for this entry collapses it
           * WITHOUT clearing anything — only an explicit Cancel discards content. */
          if (self.contribOpen && self.contribMode === "edit" && self.contribEntryId === id) {
            self.closeContribution(false);
            self.focusElement(self.els.topEdit);
            return;
          }

          self.openContribution("edit", target, self.els.topEdit);
        });
      }

      /* M17 entry points. Both delegate to the SAME contribution flow the
       * existing triggers use — no second form, no second photo pipeline. The
       * trigger passed through is the button the visitor actually pressed, so
       * focus returns to it when the panel closes. */
      var openContrib = function (trigger, focus) {
        var opts = focus === "photo" ? { focus: "photo" } : undefined;
        var target = self.topEditEntry;

        /* Resume an unfinished photo-only upload instead of creating a second
         * provisional entry: the first one already holds this browser's edit
         * credential, and the campaign allows one photo per coworker. */
        if (!target && focus === "photo" && self.provisionalEntryId) {
          target = {
            id: self.provisionalEntryId,
            displayName: readContributorName(self.currentSlug) || "",
            message: null,
            answers: []
          };
        }

        if (target) {
          self.openContribution("edit", target, trigger, opts);
        } else {
          self.openContribution("create", null, trigger, opts);
        }
      };

      /* A photo action never opens the story editor. */
      if (this.els.photoCardCta) {
        this.els.photoCardCta.addEventListener("click", function () {
          openContrib(self.els.photoCardCta, "photo");
        });
      }

      /* M19 — share. Async handler, but the listener itself stays sync so a
       * rejected promise cannot escape as an unhandled rejection. The
       * in-progress guard lives in shareWall() rather than here, so it also
       * covers any other caller. */
      if (this.els.shareBtn) {
        this.els.shareBtn.addEventListener("click", function () {
          self.shareWall();
        });
      }

      /* M19 — the dashboard tiles. Delegated from the list, so the three
       * buttons need no individual wiring and a tile added later still works. */
      if (this.els.statsList) {
        this.els.statsList.addEventListener("click", function (event) {
          var tile = event.target && event.target.closest
            ? event.target.closest("[data-jcw-stat-action]")
            : null;
          if (!tile || !self.els.statsList.contains(tile)) return;
          self.openStatDialog(tile.getAttribute("data-jcw-stat-action"), tile);
        });
      }

      /* M17B: the row IS the button, so the label is clickable and not only a
       * chevron. Each row carries the action it performs. */
      if (this.els.checklist) {
        var rows = this.els.checklist.querySelectorAll("[data-jcw-check-action]");
        for (var g = 0; g < rows.length; g++) {
          (function (button) {
            button.addEventListener("click", function () {
              if (button.disabled) return;
              openContrib(button, button.getAttribute("data-jcw-check-action"));
            });
          })(rows[g]);
        }
      }

      /* Sheet controls. Close and backdrop both route through the same guarded
       * close, so neither can discard unsaved work without asking. */
      if (this.els.sheetClose) {
        this.els.sheetClose.addEventListener("click", function () {
          self.requestSheetClose();
        });
      }

      if (this.els.sheetBackdrop) {
        this.els.sheetBackdrop.addEventListener("click", function () {
          self.requestSheetClose();
        });
      }

      if (this.els.sheet) {
        this.els.sheet.addEventListener("keydown", function (event) {
          if (!self.sheetOpen) return;

          if (event.key === "Escape") {
            event.preventDefault();
            /* Deliberately NOT a silent close: the theme's own dialog closes on
             * Escape with no confirmation, which is precisely why the
             * contribution form was never put inside it. */
            self.requestSheetClose();
            return;
          }

          if (event.key !== "Tab") return;

          /* Focus trap. Without it, Tab walks out of an aria-modal dialog and
           * into the page behind, which is still rendered. */
          var items = self.sheetFocusables();
          if (!items.length) return;
          var first = items[0];
          var last = items[items.length - 1];
          var active = self.ownerDocument ? self.ownerDocument.activeElement : document.activeElement;

          if (event.shiftKey && active === first) {
            event.preventDefault();
            self.focusElement(last);
          } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            self.focusElement(first);
          }
        });
      }

      if (this.els.contribForm) {
        this.els.contribForm.addEventListener("submit", this.submitContribution.bind(this));
        /* "Dirty" means the contributor has typed or toggled something since the form
         * was opened. It is what distinguishes a harmless switch between triggers from
         * one that would throw away real work. */
        var markDirty = function () {
          self.contribDirty = true;
        };
        this.els.contribForm.addEventListener("input", markDirty);
        this.els.contribForm.addEventListener("change", markDirty);
      }

      if (this.els.contribCancel) {
        this.els.contribCancel.addEventListener("click", this.cancelContribution.bind(this));
      }

      if (this.els.contribRetryCta) {
        this.els.contribRetryCta.addEventListener("click", this.startNewContribution.bind(this));
      }

      /* M24V — the prize dialog. Opened ONLY from this control; nothing opens
       * it on arrival. closeOpenDialogs() first, so a second press cannot
       * stack a dialog behind the one already on screen — the defect M19D
       * found with the statistic tiles. */
      if (this.els.prizeOpen) {
        this.els.prizeOpen.addEventListener("click", function () {
          self.openPrizeDialog();
        });
      }

      /* "Already participating?" — the same one-dialog rule. */
      if (this.els.accessOpen) {
        this.els.accessOpen.addEventListener("click", function () {
          self.openAccessDialog();
        });
      }

      if (this.els.officeMedia) {
        this.els.officeMedia.addEventListener("click", function () {
          self.openOfficePhoto();
        });
      }

      if (this.els.profileEdit) {
        this.els.profileEdit.addEventListener("click", this.toggleProfileEditor.bind(this));
      }
      if (this.els.profileForm) {
        this.els.profileForm.addEventListener("submit", this.saveProfile.bind(this));
      }
      if (this.els.profileCancel) {
        this.els.profileCancel.addEventListener("click", this.closeProfileEditor.bind(this));
      }
      if (this.els.profilePhotoInput) {
        this.els.profilePhotoInput.addEventListener("change", this.onProfilePhotoSelected.bind(this));
      }
      if (this.els.profilePhotoClear) {
        this.els.profilePhotoClear.addEventListener("click", this.clearProfilePhoto.bind(this));
      }
      if (this.els.profilePhotoRemove) {
        this.els.profilePhotoRemove.addEventListener("click", this.toggleProfilePhotoRemoval.bind(this));
      }

      if (this.els.photoInput) {
        this.els.photoInput.addEventListener("change", this.onPhotoSelected.bind(this));
      }
      if (this.els.photoClearBtn) {
        this.els.photoClearBtn.addEventListener("click", this.clearChosenPhoto.bind(this));
      }
      if (this.els.photoRemoveBtn) {
        this.els.photoRemoveBtn.addEventListener("click", this.togglePhotoRemoval.bind(this));
      }
      if (this.els.modalPhotoRetry) {
        /* A signed read URL lives ~15 minutes. Rather than polling, a failed image
         * offers one explicit refresh that re-fetches the Wall and its fresh URLs. */
        this.els.modalPhotoRetry.addEventListener("click", function () {
          self.load({ quiet: true });
          if (self.els.modal && typeof self.els.modal.hide === "function") self.els.modal.hide();
        });
      }

      if (this.els.retry) {
        /* Native <button>. No role="button" substitute and no custom key handler,
         * so the theme's global body-level Enter/Space delegate cannot double-fire.
         * Wrapped rather than bound directly, so the click Event is never mistaken
         * for load()'s options argument. */
        this.els.retry.addEventListener("click", function () {
          self.load();
        });
      }

      this.load();
    }

    announce(message) {
      setText(this.els.live, message || "");
    }

    setState(state) {
      if (this.els.loading) this.els.loading.hidden = state !== "loading";
      if (this.els.content) this.els.content.hidden = state !== "content";
      if (this.els.privateMsg) this.els.privateMsg.hidden = state !== "private";
      if (this.els.errorMsg) this.els.errorMsg.hidden = state !== "error";
    }

    showPrivate() {
      this.setState("private");
      this.announce(this.strings.privateTitle || "");
    }

    showError() {
      this.setState("error");
      this.announce(this.strings.genericTitle || "");
      if (this.els.retry) {
        try {
          this.els.retry.focus({ preventScroll: true });
        } catch (e) {
          /* focus is a convenience only */
        }
      }
    }

    /* Chooses which credential this request should use.
     *
     * Order matters and encodes three locked rules:
     *   1. A grant minted earlier in THIS page lifetime wins, so a retry after a
     *      successful first visit reuses that grant instead of re-presenting the
     *      master access token and minting duplicate grants.
     *   2. A freshly opened share link wins over any older stored grant for its
     *      first request. SESSION_GRANT is null on a fresh page load, so this
     *      follows naturally.
     *   3. Otherwise fall back to the stored grant for the target Office.
     *   4. Failing that, the stored share token for that same Office, read
     *      exactly as a freshly opened share link would be.
     *
     * Returns null when nothing is resolvable, which maps to the existing
     * generic private-invalid state with no request made. */
    resolveCredential() {
      /* Office routing precedence:
       *   1. `office` from the current URL query (captured above)
       *   2. a legacy fragment-carried slug (folded into CREDENTIALS.office)
       *   3. `lastOffice` from storage — a FALLBACK ONLY
       *   4. nothing resolvable -> generic private-invalid state, no request
       *
       * An explicit URL Office always wins over `lastOffice`. If that Office has no
       * usable grant the result is the private state — another Office's grant is
       * NEVER substituted. */
      var slug = CREDENTIALS.office || readLastOffice();
      if (!slug) return null;

      if (SESSION_GRANT && SESSION_GRANT.slug === slug && SESSION_GRANT.token) {
        return { slug: slug, mode: "participant", token: SESSION_GRANT.token };
      }

      if (CREDENTIALS.office === slug && CREDENTIALS.access) {
        return { slug: slug, mode: "access", token: CREDENTIALS.access };
      }

      var stored = readGrantToken(slug);
      if (stored) return { slug: slug, mode: "participant", token: stored };

      /* 4. No grant, but this browser kept the Office's share token (M21).
       *
       * Without this, a dropped grant locked the visitor out of a Wall whose
       * link they still held in storage: the address bar only ever shows
       * `?office=<slug>`, and after F-1/F-2 an already-used verification link
       * no longer mints a fresh grant. This is exactly the share-link read —
       * the backend validates the token and issues a new grant — so it confers
       * nothing a share link does not: writing still needs the grant AND a
       * verified contributor credential, editing an entry its editToken, and
       * the office profile a nomination credential. A rejected token drops
       * nothing (see the access branch in load()). Same office only. */
      var share = readShareToken(slug);
      if (share) return { slug: slug, mode: "access", token: share };

      return null;
    }

    /* `options.quiet` suppresses the loading state and the "loaded" announcement.
     *
     * A contribution create or edit re-fetches the Wall so the server stays
     * authoritative for content and ordering. Swapping to the loading state for that
     * would unmount the content block, drop focus to <body> and throw away the
     * success announcement, so the quiet path keeps the rendered Wall on screen and
     * swaps it in place. The REQUEST and every credential rule are identical.
     *
     * Resolves true only when the Wall actually re-rendered. */
    async load(options) {
      var quiet = !!(options && options.quiet === true);

      /* No resolvable credential is indistinguishable from a bad token or a
       * non-existent office. One generic state, by design. */
      var credential = this.resolveCredential();
      if (!credential) {
        this.showPrivate();
        return false;
      }

      if (!quiet) {
        this.setState("loading");
        this.announce(this.strings.loading || "");
      }

      /* The ONLY place a credential is transmitted. The raw Office access token
       * is sent as `accessToken`; a participant grant as `participantToken`. */
      var requestBody =
        credential.mode === "access"
          ? { accessToken: credential.token }
          : { participantToken: credential.token };

      var response;
      try {
        response = await fetch(
          PROXY_BASE + "/offices/" + encodeURIComponent(credential.slug),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json"
            },
            body: JSON.stringify(requestBody),
            credentials: "same-origin"
          }
        );
      } catch (e) {
        /* Network failure. The thrown value is intentionally not inspected or
         * logged, so a request-echoing error object can never expose the token. */
        this.showError();
        return false;
      }

      if (response.status === 401 || response.status === 403 || response.status === 404) {
        /* A rejected PARTICIPANT grant is expired or revoked: drop exactly that
         * Office's grant. Every other Office is preserved, and lastOffice is
         * cleared only when it pointed at this Office.
         *
         * A rejected ACCESS token means the share link is bad, which says nothing
         * about a stored grant — so stored grants are deliberately left alone and
         * the next refresh self-heals if the grant is genuinely dead. */
        if (credential.mode === "participant") {
          dropGrant(credential.slug);
          if (SESSION_GRANT && SESSION_GRANT.slug === credential.slug) SESSION_GRANT = null;
        }
        this.showPrivate();
        return false;
      }

      if (!response.ok) {
        this.showError();
        return false;
      }

      var payload;
      try {
        payload = await response.json();
      } catch (e) {
        this.showError();
        return false;
      }

      if (!payload || typeof payload !== "object" || !payload.office || typeof payload.office !== "object") {
        this.showError();
        return false;
      }

      /* A first-visit ACCESS exchange returns a participant grant. Persist it and
       * prefer it for the rest of this page lifetime. A participant-token request
       * returns no grant, so only recency is refreshed.
       *
       * `payload.participantToken` is the write credential. M21 additionally
       * keeps `credential.token` when it is the raw ACCESS token, so Share
       * works after a refresh — see the storage note on the `share` namespace.
       * It is kept only here, inside the success branch, so a bad or stale
       * fragment never reaches storage. */
      if (typeof payload.participantToken === "string" && payload.participantToken) {
        saveGrant(credential.slug, payload.participantToken, payload.participantTokenExpiresAt);
        /* The raw share token is kept only after the backend accepted it for
         * this office, so a bad or stale fragment is never written to storage. */
        if (credential.mode === "access") saveShareToken(credential.slug, credential.token);
        SESSION_GRANT = { slug: credential.slug, token: payload.participantToken };
      } else if (credential.mode === "participant") {
        touchLastOffice(credential.slug);
      }

      /* Canonicalise the permanent URL. Only relevant when the Office came from the
       * `lastOffice` fallback; a no-op when `?office=` already names this Office.
       * Never navigates or reloads — replaceState only. */
      if (needsOfficeParam(credential.slug)) rewriteUrl(credential.slug);

      this.currentSlug = credential.slug;
      /* Ordered while the content block is still hidden, so the first paint is
       * already correct and nothing is seen to move. */
      this.applyDashboardOrder();
      /* Before the first paint, so no button is ever wired to a frozen entry. */
      await this.refreshOwnModeration(credential.slug);
      this.render(payload, { quiet: quiet });
      this.applyBackingState();
      return true;
    }

    render(payload, options) {
      /* Prompts FIRST. Both the compact entry rows and the View Entry dialog resolve
       * their question text and their selected-chip labels through the prompt index,
       * so it has to exist before any entry is built. */
      this.setPrompts(payload.prompts);
      this.setAnswerPatchSupport(payload);
      this.renderOffice(payload.office);
      this.renderEntries(payload.entries, payload.backings);
      this.renderModerationNotice();
      /* After renderEntries, which is what resolves photo presence and ownership. */
      this.renderStats(payload);
      this.setState("content");
      if (!(options && options.quiet === true)) this.announce(this.strings.loaded || "");
    }

    /* ---------- Backend-owned prompt model (Milestone 11a) ----------
     *
     * The backend is authoritative for prompt questions and choice labels. Neither is
     * hardcoded in Liquid, in locale copy, or here. This builds two things:
     *
     *   this.prompts      ordered list the contribution form renders from
     *   this.promptIndex  key -> { question, maxLength, choices, choiceLabels }
     *
     * The legacy locale label map stays in place as a FALLBACK ONLY, for an entry that
     * references a prompt key the backend no longer publishes. */
    setPrompts(prompts) {
      var list = [];
      var index = {};
      var source = Array.isArray(prompts) ? prompts : [];

      for (var i = 0; i < source.length; i++) {
        var prompt = source[i];
        if (!prompt || typeof prompt !== "object") continue;

        var key = typeof prompt.key === "string" ? prompt.key.trim() : "";
        if (!key || Object.prototype.hasOwnProperty.call(index, key)) continue;

        var choices = [];
        var labels = {};
        var rawChoices = Array.isArray(prompt.choices) ? prompt.choices : [];
        for (var c = 0; c < rawChoices.length; c++) {
          var choice = rawChoices[c];
          if (!choice || typeof choice !== "object") continue;

          var choiceKey = typeof choice.key === "string" ? choice.key.trim() : "";
          var label = typeof choice.label === "string" ? choice.label.trim() : "";
          /* A choice with no key or no label cannot be rendered or submitted
           * meaningfully, so it is dropped rather than shown unlabelled. */
          if (!choiceKey || !label) continue;
          if (Object.prototype.hasOwnProperty.call(labels, choiceKey)) continue;

          labels[choiceKey] = label;
          choices.push({ key: choiceKey, label: label, sortOrder: sortValue(choice.sortOrder, c) });
        }
        choices.sort(bySortOrder);

        var maxLength = Number(prompt.maxLength);
        var record = {
          key: key,
          question: typeof prompt.question === "string" ? prompt.question.trim() : "",
          maxLength: isFinite(maxLength) && maxLength > 0 ? Math.floor(maxLength) : 0,
          choices: choices,
          choiceLabels: labels,
          sortOrder: sortValue(prompt.sortOrder, i)
        };

        index[key] = record;
        list.push(record);
      }

      list.sort(bySortOrder);

      this.prompts = list;
      this.promptIndex = index;

      /* THE TRANSITION GATE.
       *
       * The DTO publishes ACTIVE prompts only — `loadPromptConfig` filters on
       * `active: true` — so a key appearing here is the one fact that proves
       * the simplified form is live for this shop. Both keys are required:
       * one of the two would be half a form.
       *
       * Everything this flag changes is presentation and validation. It never
       * invents a prompt, never hardcodes a question, and never causes a key
       * to be submitted that did not come from this list, so a theme running
       * ahead of the prompt sync degrades to the legacy rendering rather than
       * posting answers the backend would reject. */
      var simplified = true;
      for (var s = 0; s < SIMPLIFIED_PROMPT_KEYS.length; s++) {
        if (!Object.prototype.hasOwnProperty.call(index, SIMPLIFIED_PROMPT_KEYS[s])) {
          simplified = false;
          break;
        }
      }
      this.simplifiedPrompts = simplified;
    }

    /* The question to SHOW for a prompt key.
     *
     * The campaign override wins where one exists, then whatever the backend
     * carries. Because the override map holds only the two simplified keys, a
     * retired V1 prompt is never rewritten: it keeps the question it was
     * actually asked, which is the whole point of the backend carrying it.
     *
     * Used by the contribution form AND by the read-back, so one contribution
     * can never show two different wordings of the same question. */
    displayQuestion(key, carried) {
      var map = this.promptQuestions || {};
      if (key && Object.prototype.hasOwnProperty.call(map, key) && map[key]) {
        return map[key];
      }
      return carried || "";
    }

    /* Does the DEPLOYED backend understand `answerPatch`?
     *
     * This matters because the theme and the backend ship separately. The two
     * edit contracts behave very differently on an entry that carries an
     * answer to a retired prompt:
     *
     *   old backend  `answers` replaces EVERY answer and chip on the entry;
     *                `answerPatch` is an unknown field and is ignored, so an
     *                edit sent that way would silently save nothing.
     *   new backend  `answerPatch` changes only the prompts it names, and
     *                even a full `answers` replace now spares retired ones.
     *
     * Guessing wrong in either direction loses a contributor's words, so the
     * capability is DETECTED rather than assumed, from the shape of the data
     * the backend actually returned: `selectedChoices` (chips with their
     * labels, carried on the answer itself) is emitted only by the version
     * that also accepts `answerPatch`. A missing signal is treated as "not
     * supported", which keeps the current, already-safe M18A behaviour.
     *
     * A false negative is harmless — a full replace on the new backend no
     * longer destroys retired answers — and a false positive cannot happen,
     * because the old DTO has no such field. */
    setAnswerPatchSupport(payload) {
      var entries = payload && Array.isArray(payload.entries) ? payload.entries : [];
      var supported = false;

      for (var i = 0; i < entries.length && !supported; i++) {
        var answers =
          entries[i] && Array.isArray(entries[i].answers) ? entries[i].answers : [];
        for (var a = 0; a < answers.length; a++) {
          if (answers[a] && Array.isArray(answers[a].selectedChoices)) {
            supported = true;
            break;
          }
        }
      }

      this.supportsAnswerPatch = supported;
    }

    /* ONE resolution pass, shared by the compact row and the dialog, so the
     * "View entry" control and the dialog content can never disagree about whether an
     * entry has anything to show.
     *
     * Returns only rows that render something: free text, or at least one selected
     * choice the backend still publishes a label for. An unresolvable choice key is
     * dropped silently — a raw internal key tells a reader nothing and should not be
     * surfaced. */
    resolveAnswers(entry) {
      var rows = [];
      var answers = entry && Array.isArray(entry.answers) ? entry.answers : [];
      var index = this.promptIndex || {};
      var legacy = this.promptLabels || {};
      var fallback = this.strings.promptFallback || "";

      for (var i = 0; i < answers.length; i++) {
        var answer = answers[i];
        if (!answer || typeof answer !== "object") continue;

        var key = typeof answer.promptKey === "string" ? answer.promptKey : "";
        var prompt = key && Object.prototype.hasOwnProperty.call(index, key) ? index[key] : null;

        /* Question authority, in order:
         *
         *   1. the question carried ON THE ANSWER by the backend (M18B). This
         *      is the only source that stays correct once a prompt is retired,
         *      because it is the question as it was actually asked.
         *   2. the active prompt in the DTO, for a backend that does not carry
         *      it on the answer yet.
         *   3. the legacy locale map, for a retired key on that older backend.
         *   4. the generic fallback.
         *
         * The locale map is retained for case 3 and must not be removed: it is
         * what keeps the five retired prompts readable until the backend that
         * carries their question is deployed. */
        var question = this.displayQuestion(key, firstString(answer, ["question"]));
        if (!question && prompt && prompt.question) question = prompt.question;
        if (!question && key && Object.prototype.hasOwnProperty.call(legacy, key)) {
          question = legacy[key];
        }
        if (!question) question = fallback;

        /* Chip labels, same reasoning. `selectedChoices` carries each chip's own
         * label, so a chip belonging to a retired prompt still renders as words
         * instead of being dropped for want of a label. Falling back to the
         * active prompt's label map preserves the previous behaviour exactly,
         * including dropping a key nothing can name. */
        var labels = [];
        var carried = Array.isArray(answer.selectedChoices) ? answer.selectedChoices : null;
        if (carried) {
          for (var d = 0; d < carried.length; d++) {
            var chip = carried[d];
            if (!chip || typeof chip !== "object") continue;
            var chipLabel = firstString(chip, ["label"]);
            if (chipLabel) labels.push(chipLabel);
          }
        } else {
          var keys = Array.isArray(answer.selectedChoiceKeys) ? answer.selectedChoiceKeys : [];
          for (var c = 0; c < keys.length; c++) {
            var choiceKey = keys[c];
            if (typeof choiceKey !== "string" || !choiceKey) continue;
            if (!prompt) continue;
            if (!Object.prototype.hasOwnProperty.call(prompt.choiceLabels, choiceKey)) continue;
            labels.push(prompt.choiceLabels[choiceKey]);
          }
        }

        var text = firstString(answer, ["answerText"]);
        if (!text && !labels.length) continue;

        rows.push({ question: question, choices: labels, text: text });
      }

      return rows;
    }

    renderOffice(office) {
      this.officeCompanyName = typeof office.companyName === "string" ? office.companyName.trim() : "";
      setText(this.els.company, this.officeCompanyName);

      /* M22 — the profile editor reads back exactly what the server holds, so
       * a save that changes nothing sends nothing, and a cancelled edit
       * restores the server's value rather than a stale local one. */
      this.officeCity = typeof office.city === "string" ? office.city.trim() : "";
      this.officeCountry = typeof office.country === "string" ? office.country.trim().toUpperCase() : "";

      var parts = [];
      if (this.officeCity) parts.push(this.officeCity);
      /* The DTO carries the ISO code; the visitor is shown the country. An
       * unrecognised value is rendered as it stands rather than dropped — a
       * profile written before M22 is still that office's own data. */
      if (typeof office.country === "string" && office.country.trim() !== "") {
        parts.push(countryName(office.country) || office.country.trim());
      }

      if (this.els.location) {
        if (parts.length) {
          /* Written into the inner span, so the location pin beside it survives. */
          setText(this.els.locationText || this.els.location, parts.join(", "));
          this.els.location.hidden = false;
        } else {
          setText(this.els.locationText || this.els.location, "");
          this.els.location.hidden = true;
        }
      }

      /* M22 added `office.photoUrl` — a signed URL, or null. The slot stays
       * empty when there is none: no placeholder, no "add a photo" tile for
       * someone who cannot add one, and never a coworker's contribution photo
       * borrowed and passed off as a picture of the office. The two earlier
       * key names are still read so a DTO that predates M22 keeps working. */
      var imageUrl = firstString(office, ["photoUrl", "imageUrl", "image"]);
      /* M25 — THE NOMINATOR'S NAME, IF THE BACKEND EVER SUPPLIES ONE.
       *
       * It does not today: the Office Wall DTO carries slug, companyName, city,
       * country, status, backingCount and photoUrl, and no nominator identity
       * at all. The card therefore shows its generic variant, and the backend
       * change needed is recorded in CUSTOMIZATIONS.md.
       *
       * Read defensively across the names the field could reasonably take, so
       * the card starts naming the nominator the moment one arrives, with no
       * further theme change. NOTHING is inferred: the name is never taken from
       * the backings list, never derived from an email, and never guessed from
       * "the earliest backing" — that list is other participants' data and the
       * nominator is not identifiable in it. Only the FIRST name is used, and
       * an email address is never displayed. */
      var nominator = firstString(office, [
        "nominatorFirstName",
        "nominatorName",
        "nominatorDisplayName"
      ]);
      this.officeNominator = nominator ? String(nominator).trim().split(/\s+/)[0] : "";

      this.officePhotoUrl = safeImageUrl(imageUrl);
      /* After officePhotoUrl is known: the avatar is the fallback and must not
       * be decided before the thing it falls back from. */
      this.applyOfficeAvatar(office.companyName);
      /* M24C — exactly one identity mark.
       *
       * The orange initial is the FALLBACK, not a companion: showing it beside
       * an uploaded photograph gave one office two identities, which is what
       * DEV testing caught. The photo wins when there is one. */
      var hasOfficePhoto = !!this.officePhotoUrl;
      if (this.els.officeMedia) {
        if (hasOfficePhoto && this.els.officeImage) {
          this.els.officeImage.setAttribute("src", this.officePhotoUrl);
          this.els.officeImage.setAttribute("alt", "");
          var viewLabel = fillToken(
            this.strings.officePhotoView || "",
            "{{ company }}",
            typeof office.companyName === "string" ? office.companyName : ""
          );
          this.els.officeMedia.setAttribute("aria-label", viewLabel);
          setText(this.els.officePhotoTitle, viewLabel);
        }
        this.els.officeMedia.hidden = !hasOfficePhoto;
      }
      if (this.els.officeAvatar) this.els.officeAvatar.hidden = hasOfficePhoto;

      this.renderBackingCount(office.backingCount);
      this.applyProfileControls();
    }

    /* Single source of truth for the visible backing count. Used by the initial
     * Office render and by a successful backing response, so the two can never
     * drift. The value is always the authoritative number supplied by the server —
     * there is no client-side increment anywhere. */
    /* ---------- M22 office profile ----------
     *
     * OWNERSHIP IS THE BACKEND'S DECISION. This only decides whether the
     * controls are OFFERED. `PATCH /profile` and the three profile-photo
     * routes each require a participantToken AND a contributorToken whose
     * signed source is "nomination"; a backing-sourced credential is refused
     * there however this file behaves.
     *
     * Two conditions, both necessary:
     *   - a participant grant for THIS office (a read-only share visitor has
     *     none, so they never see the controls);
     *   - a contributor credential this browser recorded as nomination-sourced.
     *
     * A coworker holds the first and, once they confirm a backing, a
     * backing-sourced second — which fails the test. */
    isProfileOwner() {
      if (!this.currentSlug) return false;
      if (!resolveWriteToken(this.currentSlug)) return false;
      return readContributorSource(this.currentSlug) === CONTRIBUTOR_NOMINATION;
    }

    applyProfileControls() {
      var owner = this.isProfileOwner();
      if (this.els.profileEdit) this.els.profileEdit.hidden = !owner;
      /* An editor left open when ownership lapses — an expired grant, a
       * credential dropped by a 403 — closes rather than lingering. */
      if (!owner && this.els.profilePanel && !this.els.profilePanel.hidden) {
        this.closeProfileEditor();
      }
    }

    /* Options are built once, on first open. A read-only visitor and a
     * coworker never reach this, so neither pays for 249 <option> nodes. */
    buildCountryOptions() {
      var select = this.els.profileCountry;
      if (!select || select.dataset.jcwBuilt === "true") return;

      var blank = document.createElement("option");
      blank.value = "";
      /* Country is optional, so "not given" must be choosable — not merely the
       * state you start in and can never return to. */
      blank.textContent = this.strings.profileCountryNone || "";
      select.appendChild(blank);

      for (var i = 0; i < COUNTRIES.length; i++) {
        var split = COUNTRIES[i].indexOf("|");
        var option = document.createElement("option");
        option.value = COUNTRIES[i].slice(0, split);
        option.textContent = COUNTRIES[i].slice(split + 1);
        select.appendChild(option);
      }
      select.dataset.jcwBuilt = "true";
    }

    toggleProfileEditor() {
      if (!this.els.profilePanel) return;
      if (this.els.profilePanel.hidden) this.openProfileEditor();
      else this.closeProfileEditor();
    }

    openProfileEditor() {
      if (!this.els.profilePanel || !this.isProfileOwner()) return;

      this.buildCountryOptions();
      this.resetProfilePhotoState();
      this.showProfileError("");
      this.setProfileStatus("");

      /* Seeded from the SERVER's current values every time it opens, so a
       * cancelled edit cannot leave a stale draft behind. */
      if (this.els.profileCity) this.els.profileCity.value = this.officeCity || "";
      if (this.els.profileCountry) {
        /* An ISO code the list does not carry selects nothing rather than
         * silently rewriting the office's stored country to a neighbour. */
        this.els.profileCountry.value = countryName(this.officeCountry) ? this.officeCountry : "";
      }

      if (this.els.profilePhotoCurrent) {
        var has = !!this.officePhotoUrl;
        this.els.profilePhotoCurrent.hidden = !has;
        if (has && this.els.profilePhotoCurrentImg) {
          this.els.profilePhotoCurrentImg.src = this.officePhotoUrl;
          this.els.profilePhotoCurrentImg.alt = "";
        }
      }
      if (this.els.profilePhotoRemove) this.els.profilePhotoRemove.hidden = !this.officePhotoUrl;

      this.els.profilePanel.hidden = false;
      if (this.els.profileEdit) this.els.profileEdit.setAttribute("aria-expanded", "true");
      this.focusElement(this.els.profileCity);
    }

    closeProfileEditor() {
      if (!this.els.profilePanel) return;
      this.els.profilePanel.hidden = true;
      /* A deferred reorder may have been waiting on this panel. */
      var selfOrder = this;
      setTimeout(function () { selfOrder.flushDashboardOrder(); }, 0);
      this.resetProfilePhotoState();
      this.showProfileError("");
      this.setProfileStatus("");
      if (this.els.profileEdit) {
        this.els.profileEdit.setAttribute("aria-expanded", "false");
        /* Focus returns to the control that opened the panel rather than
         * dropping to <body> when the panel it was inside disappears. */
        if (!this.els.profileEdit.hidden) this.focusElement(this.els.profileEdit);
      }
    }

    resetProfilePhotoState() {
      this.profilePhotoFile = null;
      this.profilePhotoRemoveRequested = false;
      this.releaseProfilePhotoPreview();
      if (this.els.profilePhotoInput) this.els.profilePhotoInput.value = "";
      if (this.els.profilePhotoChosen) this.els.profilePhotoChosen.hidden = true;
      if (this.els.profilePhotoRemove) {
        setText(this.els.profilePhotoRemove, this.strings.profilePhotoRemove || "");
        this.els.profilePhotoRemove.setAttribute("aria-pressed", "false");
      }
    }

    releaseProfilePhotoPreview() {
      if (!this.profilePhotoPreviewUrl) return;
      try {
        URL.revokeObjectURL(this.profilePhotoPreviewUrl);
      } catch (e) {
        /* Revoking a URL the browser has already released is harmless. */
      }
      this.profilePhotoPreviewUrl = "";
    }

    async onProfilePhotoSelected() {
      var input = this.els.profilePhotoInput;
      var file = input && input.files && input.files[0];
      if (!file) return;

      this.showProfileError("");
      /* Choosing a replacement cancels a pending removal. */
      this.profilePhotoRemoveRequested = false;
      if (this.els.profilePhotoRemove) {
        setText(this.els.profilePhotoRemove, this.strings.profilePhotoRemove || "");
        this.els.profilePhotoRemove.setAttribute("aria-pressed", "false");
      }
      this.setProfileStatus(this.strings.photoStatePreparing || "");

      var prepared;
      try {
        prepared = await preparePhotoFile(file, this.heicDecoderUrl, null);
      } catch (e) {
        this.setProfileStatus("");
        this.showProfileError(
          this.strings[e instanceof PhotoError ? e.stringKey : "photoErrGeneric"] ||
            this.strings.photoErrGeneric ||
            ""
        );
        this.profilePhotoFile = null;
        if (this.els.profilePhotoChosen) this.els.profilePhotoChosen.hidden = true;
        return;
      }

      this.setProfileStatus("");
      this.profilePhotoFile = prepared;
      this.releaseProfilePhotoPreview();
      setText(this.els.profilePhotoName, file.name || prepared.name || "");
      if (this.els.profilePhotoChosen) this.els.profilePhotoChosen.hidden = false;
    }

    clearProfilePhoto() {
      this.profilePhotoFile = null;
      this.releaseProfilePhotoPreview();
      if (this.els.profilePhotoInput) this.els.profilePhotoInput.value = "";
      if (this.els.profilePhotoChosen) this.els.profilePhotoChosen.hidden = true;
      this.showProfileError("");
    }

    /* Removal is staged, not immediate: nothing is destroyed until Save, so a
     * mis-tap is undone by pressing the same control again or cancelling. */
    toggleProfilePhotoRemoval() {
      if (!this.officePhotoUrl) return;
      this.profilePhotoRemoveRequested = !this.profilePhotoRemoveRequested;
      if (this.profilePhotoRemoveRequested) this.clearProfilePhoto();
      if (this.els.profilePhotoRemove) {
        setText(
          this.els.profilePhotoRemove,
          this.profilePhotoRemoveRequested
            ? this.strings.profilePhotoRemoveUndo || ""
            : this.strings.profilePhotoRemove || ""
        );
        this.els.profilePhotoRemove.setAttribute(
          "aria-pressed",
          this.profilePhotoRemoveRequested ? "true" : "false"
        );
      }
    }

    setProfileStatus(message) {
      setText(this.els.profileStatus, message || "");
    }

    showProfileError(message) {
      var el = this.els.profileError;
      if (!el) return;
      setText(el, message || "");
      el.hidden = !message;
    }

    setProfileBusy(busy) {
      this.profileBusy = !!busy;
      if (this.els.profileSave) this.els.profileSave.disabled = !!busy;
      if (this.els.profileCancel) this.els.profileCancel.disabled = !!busy;
      setText(
        this.els.profileSaveLabel,
        busy ? this.strings.profileSaving || "" : this.strings.profileSave || ""
      );
    }

    profileProxyUrl(slug, suffix) {
      return PROXY_BASE + "/offices/" + encodeURIComponent(slug) + suffix;
    }

    /* The office-level credential pair. No editToken: a profile belongs to the
     * office, not to a WallEntry, so there is no authorship to prove. */
    profileCredentials(slug) {
      var participantToken = resolveWriteToken(slug);
      var contributorToken = readContributorToken(slug);
      if (!participantToken || !contributorToken) return null;
      return { participantToken: participantToken, contributorToken: contributorToken };
    }

    /* The profile control-plane call.
     *
     * Deliberately separate from photoControlRequest(): that one maps every
     * failure onto contribution-photo copy, and reusing it here would report a
     * rejected city as an upload problem. Same safety rules though — the body
     * is read BEFORE the status is collapsed, and the thrown value of a failed
     * fetch is never inspected or logged, because it can echo a request that
     * carries two credentials. */
    async profileRequest(url, method, body) {
      var response;
      try {
        response = await fetch(url, {
          method: method,
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(body),
          credentials: "same-origin"
        });
      } catch (e) {
        return { ok: false, key: "profileErrNetwork" };
      }

      var payload = null;
      try {
        payload = await response.json();
      } catch (e) {
        payload = null;
      }

      var reason = payload && typeof payload.error === "string" ? payload.error : "";

      /* AN ENVELOPE PROVES THE CAMPAIGN BACKEND ANSWERED.
       *
       * This matters more here than anywhere else in the file, because these
       * four routes DO NOT EXIST on the currently deployed backend. A missing
       * App Proxy route answers 404 — and the Wall's usual reading of 404 is
       * "your office access is gone", which drops the participant grant. Doing
       * that would let one press of a not-yet-wired Save log the nominator out
       * of a Wall that was working perfectly.
       *
       * So a status is only believed when the body is this backend's own
       * `{ ok: ... }` envelope. Anything else — Shopify's 404 page, an HTML
       * error, an empty body — is reported as "not available yet" and nothing
       * is dropped, nothing is retried. */
      var envelope = !!payload && typeof payload === "object" && typeof payload.ok === "boolean";
      if (!envelope) {
        return { ok: false, key: response.ok ? "profileErrGeneric" : "profileErrUnavailable" };
      }

      if (response.status === 403 && reason === "verification_required") {
        return { ok: false, key: "errVerificationRequired", verificationRequired: true };
      }
      /* "No valid Office access" — the same generic answer the rest of the Wall
       * gives, so a probe cannot tell a wrong token from a wrong office. */
      if (response.status === 401 || response.status === 403 || response.status === 404) {
        return { ok: false, key: "profileErrAccess", accessLost: true };
      }
      if (response.status === 400) return { ok: false, key: "profileErrLocation" };
      if (response.status === 422) return { ok: false, key: this.photoErrorKeyFor(reason) };
      if (!response.ok || payload.ok !== true) {
        return { ok: false, key: "profileErrGeneric" };
      }
      return { ok: true, payload: payload };
    }

    profileFailure(result) {
      if (!result) return;
      this.setProfileStatus("");
      this.setProfileBusy(false);

      var message = this.strings[result.key] || this.strings.profileErrGeneric || "";

      /* A REVOKED CREDENTIAL CLOSES THE EDITOR, so the message cannot be left
       * inside it.
       *
       * Two things were wrong here before this branch existed. The error was
       * written into a panel that applyProfileControls() had just hidden, so
       * the nominator was told nothing at all; and the recovery route the
       * wording points at — "request a confirmation link from the backing
       * section" — stayed hidden, because `backReady` is only revealed by
       * applyBackingState(), which nothing re-ran. That is the same dead end
       * M21 removed from the backing flow, and it must not come back here.
       *
       * So: drop exactly what the backend rejected, re-derive EVERY gated
       * control from the new state, and announce the message through the
       * live region, which survives the panel closing. */
      if (result.verificationRequired || result.accessLost) {
        if (result.verificationRequired) {
          /* Only this office's contributor credential. The grant still reads
           * and still shares, and the entry credentials are untouched. */
          dropContributorToken(this.currentSlug);
        }
        if (result.accessLost) {
          dropGrant(this.currentSlug);
          if (SESSION_GRANT && SESSION_GRANT.slug === this.currentSlug) SESSION_GRANT = null;
        }

        this.applyVerificationState();
        this.applyBackingState();
        this.applyProfileControls();
        this.announce(message);
        return;
      }

      this.showProfileError(message);
    }

    /* City and country are sent only when they actually changed, and the photo
     * is a separate exchange. A visitor who opens the editor, changes one
     * field and saves therefore makes exactly one request. */
    async saveProfile(event) {
      if (event) event.preventDefault();
      if (this.profileBusy) return;

      var slug = this.currentSlug;
      var credentials = this.profileCredentials(slug);
      if (!slug || !credentials) {
        this.showProfileError(this.strings.profileErrAccess || "");
        return;
      }

      var city = this.els.profileCity ? String(this.els.profileCity.value || "").trim() : "";
      var country = this.els.profileCountry ? String(this.els.profileCountry.value || "").trim() : "";
      /* A value the list does not carry is never sent: the field is a <select>,
       * so this can only be a tampered DOM, and the backend would reject it. */
      if (country && !countryName(country)) {
        this.showProfileError(this.strings.profileErrLocation || "");
        return;
      }
      if (city.length > PROFILE_CITY_MAX) {
        this.showProfileError(this.strings.profileErrLocation || "");
        return;
      }

      this.showProfileError("");
      this.setProfileBusy(true);

      var locationChanged = city !== (this.officeCity || "") || country !== (this.officeCountry || "");

      if (locationChanged) {
        this.setProfileStatus(this.strings.profileSaving || "");
        /* Cleared fields are sent as empty strings rather than omitted:
         * omitting means "leave unchanged", which is not the same request as
         * "remove the city I previously gave". */
        var patched = await this.profileRequest(
          this.profileProxyUrl(slug, "/profile"),
          "PATCH",
          {
            participantToken: credentials.participantToken,
            contributorToken: credentials.contributorToken,
            city: city,
            country: country
          }
        );
        if (!patched.ok) return this.profileFailure(patched);
      }

      if (this.profilePhotoRemoveRequested && !this.profilePhotoFile) {
        this.setProfileStatus(this.strings.profilePhotoRemoving || "");
        var removed = await this.profileRequest(
          this.profileProxyUrl(slug, "/profile-photo"),
          "DELETE",
          credentials
        );
        if (!removed.ok) return this.profileFailure(removed);
      } else if (this.profilePhotoFile) {
        /* Identical three-step exchange to the contribution photo: the backend
         * issues a capability, the bytes go straight to private Storage, and a
         * finalize call names the descriptor it handed out. No Storage path is
         * constructed here and no Storage credential exists in the browser. */
        this.setProfileStatus(this.strings.photoStatePreparing || "");
        var begun = await this.profileRequest(
          this.profileProxyUrl(slug, "/profile-photo-upload"),
          "POST",
          credentials
        );
        if (!begun.ok) return this.profileFailure(begun);

        var capability = begun.payload || {};
        var maxBytes = Number(capability.maxBytes);
        if (isFinite(maxBytes) && maxBytes > 0 && this.profilePhotoFile.size > maxBytes) {
          return this.profileFailure({ ok: false, key: "photoErrTooLarge" });
        }

        this.setProfileStatus(this.strings.photoStateUploading || "");
        var uploaded = await this.uploadPhotoBytes(capability, this.profilePhotoFile);
        if (!uploaded.ok) return this.profileFailure(uploaded);

        this.setProfileStatus(this.strings.photoStateProcessing || "");
        var finalized = await this.profileRequest(
          this.profileProxyUrl(slug, "/profile-photo-finalize"),
          "POST",
          {
            participantToken: credentials.participantToken,
            contributorToken: credentials.contributorToken,
            uploadDescriptor: capability.uploadDescriptor
          }
        );
        if (!finalized.ok) return this.profileFailure(finalized);
      }

      this.setProfileBusy(false);
      this.closeProfileEditor();

      /* The server is re-read rather than the card patched locally, so what is
       * displayed is what was actually stored — including a photo URL only the
       * backend can sign. `quiet` keeps the Wall on screen instead of swapping
       * it for the loading state and dropping focus. */
      await this.load({ quiet: true });
      this.announce(this.strings.profileSaved || "");
    }

    renderBackingCount(value) {
      var count = Number(value);
      if (!isFinite(count) || count < 0) count = 0;
      var template = count === 1 ? this.strings.backingOne : this.strings.backingOther;
      /* The Office header is the ONLY visible backing count; the success state
       * deliberately does not repeat it. */
      setText(this.els.backing, String(template || "").replace("{{ count }}", String(count)));
    }

    /* ---------- M17B fullscreen editing sheet ----------
     *
     * Not the theme's <modal-dialog>: that closes on Escape and on any backdrop
     * click with no confirmation, which would silently destroy a part-written
     * contribution. The three things a dialog owes its user are supplied here
     * instead — a focus trap, an Escape that asks before discarding unsaved work,
     * and focus returned to the control that opened it.
     */

    sheetFocusables() {
      if (!this.els.sheetPanel) return [];
      var nodes = this.els.sheetPanel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      var out = [];
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        /* offsetParent is null for anything display:none, which is exactly what
         * photo-only mode does to the story field and the prompts. */
        if (node.offsetParent !== null || node === document.activeElement) out.push(node);
      }
      return out;
    }

    /* ISSUE 1 (M17E) — the sheet follows the VISUAL viewport.
     *
     * iOS Safari does not shrink the layout viewport when the keyboard opens, so
     * a sheet pinned with `inset: 0` keeps its bottom edge — and the submit
     * button with it — underneath the keyboard. Both the offset and the height
     * are taken from `visualViewport`, so the sheet sits exactly over what the
     * visitor can actually see.
     *
     * `offsetTop` matters as much as `height`: iOS also shifts the visual
     * viewport down when it scrolls a focused field into view. */
    syncSheetHeight() {
      var sheet = this.els.sheet;
      if (!sheet) return;
      var vv = window.visualViewport;
      if (vv && vv.height) {
        sheet.style.setProperty("--jcw-sheet-vh", Math.round(vv.height) + "px");
        sheet.style.setProperty("--jcw-sheet-top", Math.round(vv.offsetTop || 0) + "px");
      } else {
        /* No visualViewport: fall back to the CSS dynamic-unit height. */
        sheet.style.removeProperty("--jcw-sheet-vh");
        sheet.style.setProperty("--jcw-sheet-top", "0px");
      }
    }

    openSheet(title, returnFocus) {
      var sheet = this.els.sheet;
      var slot = this.els.sheetSlot;
      var shell = this.els.contribShell;
      if (!sheet || !slot || !shell) return false;

      if (shell.parentNode !== slot) slot.appendChild(shell);

      setText(this.els.sheetTitle, title || "");
      sheet.hidden = false;
      this.sheetOpen = true;
      this.sheetReturnFocus = returnFocus || null;
      this.classList.add("jcw--sheet-open");

      /* The page behind must not scroll. `overflow: hidden` alone does not hold
       * on iOS Safari, so the body is taken out of flow at its current offset
       * and put back exactly where it was on close — otherwise closing the
       * sheet silently returns the visitor to the top of the Wall. */
      this.sheetScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      var body = document.body;
      this.sheetBodyStyle = {
        overflow: body.style.overflow,
        position: body.style.position,
        top: body.style.top,
        width: body.style.width
      };
      body.style.overflow = "hidden";
      body.style.position = "fixed";
      body.style.top = -this.sheetScrollY + "px";
      body.style.width = "100%";

      this.syncSheetHeight();
      if (window.visualViewport && !this.sheetViewportBound) {
        this.sheetViewportBound = this.syncSheetHeight.bind(this);
        window.visualViewport.addEventListener("resize", this.sheetViewportBound);
        window.visualViewport.addEventListener("scroll", this.sheetViewportBound);
      }
      return true;
    }

    closeSheet() {
      if (!this.sheetOpen) return;
      var sheet = this.els.sheet;
      if (sheet) sheet.hidden = true;
      this.sheetOpen = false;
      this.classList.remove("jcw--sheet-open");

      var body = document.body;
      var previous = this.sheetBodyStyle || {};
      body.style.overflow = previous.overflow || "";
      body.style.position = previous.position || "";
      body.style.top = previous.top || "";
      body.style.width = previous.width || "";
      this.sheetBodyStyle = null;
      /* Restore the exact offset the lock froze. */
      try {
        window.scrollTo(0, this.sheetScrollY || 0);
      } catch (e) {
        /* Scrolling is a convenience only. */
      }

      if (window.visualViewport && this.sheetViewportBound) {
        window.visualViewport.removeEventListener("resize", this.sheetViewportBound);
        window.visualViewport.removeEventListener("scroll", this.sheetViewportBound);
        this.sheetViewportBound = null;
      }

      var back = this.sheetReturnFocus;
      this.sheetReturnFocus = null;
      /* Focus returns to the control that opened the sheet, never to <body>. */
      if (back && document.contains(back)) this.focusElement(back);
    }

    /* ---------- M17 presentation ----------
     *
     * Everything below reads state that already exists — the DTO, and this
     * browser's own `jco:v1` credentials — and renders it. None of it invents a
     * number, and none of it asserts something about the visitor that the
     * campaign does not actually know.
     */

    plural(count, one, other) {
      return String(count === 1 ? one : other || one || "");
    }

    /* Counts shown on the summary card.
     *
     * HONEST BOUNDS: `entries` and `backings` are the newest N the backend
     * publishes, not necessarily every one that exists — the DTO caps both. For
     * a campaign-sized office the cap is far above the real figure, so the tiles
     * are accurate in practice; they are nonetheless derived counts, whereas the
     * backing tile uses the server's own authoritative `backingCount`. */
    renderStats(payload) {
      var entries = payload && Array.isArray(payload.entries) ? payload.entries : [];

      /* M23 — THE THREE TILES MEASURE THREE DIFFERENT THINGS.
       *
       * They used to measure two. "Stories" was `entries.length`, the raw
       * count of WallEntry rows, so a coworker who only uploaded a picture
       * was counted as having written a contribution AND as having added a
       * photo — one act, two tiles, and a Stories number that overstated how
       * much had actually been written.
       *
       * `entryHasStory()` is the same predicate the checklist and the activity
       * feed already use, and it mirrors the backend's own `hasTextSubstance`:
       * a message, or a guided answer carrying free text or a selected chip.
       * Nothing is invented and no number is adjusted — the DTO already says
       * which entries carry writing, and this simply stops ignoring it. */
      var photoCount = 0;
      var storyCount = 0;
      for (var i = 0; i < entries.length; i++) {
        if (hasRenderablePhoto(entries[i])) photoCount++;
        if (this.entryHasStory(entries[i])) storyCount++;
      }

      var backingCount = Number(payload && payload.office ? payload.office.backingCount : 0);
      if (!isFinite(backingCount) || backingCount < 0) backingCount = 0;

      /* Held for the dashboard dialogs and the share message, which must read
       * the same numbers the tiles show. `backingCount` is the backend's
       * VERIFIED total — a backing contributes to it only once its email is
       * confirmed — which is why the share copy may quote it. */
      this.wallEntries = entries;
      this.wallBackings = payload && Array.isArray(payload.backings) ? payload.backings : [];
      this.verifiedBackingCount = backingCount;

      /* Held so the Stories dialog lists exactly what the tile counted. */
      this.wallStoryEntries = [];
      for (var k = 0; k < entries.length; k++) {
        if (this.entryHasStory(entries[k])) this.wallStoryEntries.push(entries[k]);
      }

      setText(this.els.statEntries, String(storyCount));
      setText(
        this.els.statEntriesLabel,
        this.plural(storyCount, this.copy.statEntriesOne, this.copy.statEntriesOther)
      );

      setText(this.els.statBackings, String(backingCount));
      setText(
        this.els.statBackingsLabel,
        this.plural(backingCount, this.copy.statBackingsOne, this.copy.statBackingsOther)
      );

      setText(this.els.statPhotos, String(photoCount));
      setText(
        this.els.statPhotosLabel,
        this.plural(photoCount, this.copy.statPhotosOne, this.copy.statPhotosOther)
      );
    }

    /* Story content, exactly as the backend counts it: a message, or any guided
     * answer carrying free text or at least one selected chip. Values are
     * normalised defensively — a whitespace-only message is not a story, and a
     * missing or malformed answers array is simply no story. */
    entryHasStory(entry) {
      return entryHasStoryContent(entry);
    }

    /* The entry this browser owns on the Wall, if any. `topEditEntry` is already
     * resolved by renderEntries() from the `jco:v1` edit credentials, so this
     * reuses that single source rather than re-deriving ownership. */
    /* One entry, recorded once, however many activity rows it produced. */
    recordOwnedVisible(entry) {
      var id = entry && typeof entry.id === "string" ? entry.id.trim() : "";
      if (!id) return;
      for (var i = 0; i < this.ownedVisible.length; i++) {
        var seen = this.ownedVisible[i];
        if (seen && typeof seen.id === "string" && seen.id.trim() === id) return;
      }
      this.ownedVisible.push(entry);
    }

    ownedEntry() {
      return this.topEditEntry || null;
    }

    /* ----------------------------------------------------------------------
     * HIDDEN CONTRIBUTIONS — owner-only moderation state.
     *
     * The Wall DTO never says that anything was moderated, and must not: a
     * hidden photo reads exactly like no photo. So a contributor whose story
     * or photo was hidden saw it vanish without explanation, and "Tell your
     * story" could open the editor on that entry, whose save the backend then
     * refused, because a moderated entry is frozen.
     *
     * The backend answers for the AUTHOR only: its status endpoint needs this
     * entry's edit token, which nobody else holds. Asked once per owned entry
     * per page lifetime, and only when this browser can contribute at all.
     * Never inferred from an entry missing from the feed, which proves nothing
     * (see dropEntryToken).
     *
     * A frozen entry is then simply not an EDIT target, so the existing
     * buttons fall through to their existing CREATE paths. Other owned entries
     * are untouched. The edit credential is kept, so the notice survives a
     * reload, and the backend stays the only authority on what may be edited.
     * -------------------------------------------------------------------- */
    editableOwnedIds(ids) {
      var frozen = this.frozenEntries[this.currentSlug] || {};
      return (ids || []).filter(function (id) {
        return !frozen[id];
      });
    }

    async refreshOwnModeration(slug) {
      if (typeof slug !== "string" || !slug) return;

      var token = resolveWriteToken(slug);
      var contributorToken = readContributorToken(slug);
      if (!token || !contributorToken) return;

      if (!this.frozenEntries[slug]) this.frozenEntries[slug] = {};
      var frozen = this.frozenEntries[slug];
      var checked = this.moderationChecked;

      var pending = readOwnedEntryIds(slug)
        .filter(function (entryId) {
          return !checked[slug + "/" + entryId];
        })
        .slice(0, MAX_MODERATION_CHECKS);
      if (!pending.length) return;

      var lookups = pending.map(async function (entryId) {
        var editToken = readEntryToken(slug, entryId);
        if (!editToken) return;

        var response;
        try {
          response = await fetch(
            PROXY_BASE +
              "/offices/" +
              encodeURIComponent(slug) +
              "/entries/" +
              encodeURIComponent(entryId) +
              "/status",
            {
              method: "POST",
              headers: { "Content-Type": "application/json", Accept: "application/json" },
              body: JSON.stringify({
                participantToken: token,
                contributorToken: contributorToken,
                editToken: editToken
              }),
              credentials: "same-origin"
            }
          );
        } catch (e) {
          /* Never inspected or logged: it can echo the credentials. Asked
           * again on the next load. */
          return;
        }

        if (response.status === 404) {
          /* Not this browser's to know about (a REJECTED entry, another
           * store's credential, an entry that is gone). Settled for this page;
           * nothing is frozen and no credential is dropped on it. */
          checked[slug + "/" + entryId] = true;
          return;
        }
        if (!response.ok) return;

        var payload = null;
        try {
          payload = await response.json();
        } catch (e) {
          payload = null;
        }
        if (
          !payload ||
          typeof payload.storyHidden !== "boolean" ||
          typeof payload.photoHidden !== "boolean"
        ) {
          return;
        }

        checked[slug + "/" + entryId] = true;
        if (payload.storyHidden || payload.photoHidden) {
          frozen[entryId] = { story: payload.storyHidden, photo: payload.photoHidden };
        } else {
          delete frozen[entryId];
        }
      });

      await Promise.race([
        Promise.all(lookups),
        new Promise(function (resolve) {
          setTimeout(resolve, MODERATION_CHECK_TIMEOUT_MS);
        })
      ]);
    }

    /* The notice copy is static Liquid; only `hidden` is toggled here, so no
     * response value ever reaches the DOM. */
    renderModerationNotice() {
      var notice = this.els.moderationNotice;
      if (!notice) return;

      var frozen = this.frozenEntries[this.currentSlug] || {};
      var story = false;
      var photo = false;
      Object.keys(frozen).forEach(function (entryId) {
        if (frozen[entryId].story) story = true;
        if (frozen[entryId].photo) photo = true;
      });

      if (this.els.moderationStory) this.els.moderationStory.hidden = !story;
      if (this.els.moderationPhoto) this.els.moderationPhoto.hidden = !photo;
      notice.hidden = !(story || photo);
    }

    /* M29 — the owned entry carrying a published photo, if any. Derived from
     * the DTO on every render, so it is correct after a reload, a replacement
     * or a removal without any cached flag to go stale. */
    ownedPhotoEntry() {
      var list = this.ownedVisible || [];
      for (var i = 0; i < list.length; i++) {
        if (hasRenderablePhoto(list[i])) return list[i];
      }
      return null;
    }

    /* Does THIS browser hold the edit credential for this entry?
     *
     * Resolved from `jco:v1` edit credentials keyed by entry id — the same
     * secure source the per-entry Edit control uses. Display names are NEVER
     * compared: two coworkers may legitimately share a first name, and a
     * display name is public UGC anyone can type, so matching on it would let
     * one person's row be labelled as another's. */
    ownsEntry(entry) {
      var id = entry && typeof entry.id === "string" ? entry.id.trim() : "";
      if (!id || !this.currentSlug) return false;
      return readOwnedEntryIds(this.currentSlug).indexOf(id) !== -1;
    }

    /* "You" for an entry this browser provably owns, the contributor's own
     * name otherwise. */
    youLabel() {
      return this.strings.youLabel || "You";
    }

    /* Checklist state.
     *
     * nominated  office-level and always complete — the Wall only loads for an
     *            office that exists. It is NEVER presented as this visitor's own
     *            doing: the DTO carries no nominator identity and the theme does
     *            not infer one, so the row has no action and no chevron.
     * story      complete only when this browser's own entry carries a message.
     * photo      complete only when this browser's own entry carries a photo.
     *
     * `available` is the same flag that governs the contribution form, so a row
     * offers its action exactly when that action can actually be performed. */
    /* M24 — the "Your contribution" checklist was removed from the Wall. The
     * method is kept as a no-op guard rather than deleted along with its call
     * sites, because the state it described is still tracked and still drives
     * the CTA labels; only its presentation is gone. */
    applyChecklist(available) {
      if (!this.els.checklist) return;

      /* A story is complete when the SERVER's copy of this browser's entry carries
       * story content, which the backend defines as a message OR a guided answer —
       * free text or selected chips. Mirrors `hasTextSubstance` server-side, so the
       * tick and the backend's own idea of a publishable story agree.
       *
       * It is never inferred from the existence of an entry, and never from a
       * photo: a photo-only contribution leaves the story outstanding, which is
       * the truth. `owned` comes from the DTO that was just re-fetched. */
      /* M29 — each fact answered across everything this browser owns, for the
       * same reason the photo card does: a participant whose contribution was
       * split across two entries has a story on one and a photo on the other,
       * and neither is "the" owned entry. */
      var ownedAll = this.ownedVisible || [];
      var story = false;
      var photo = false;
      for (var oi = 0; oi < ownedAll.length; oi++) {
        if (this.entryHasStory(ownedAll[oi])) story = true;
        if (hasRenderablePhoto(ownedAll[oi])) photo = true;
      }

      /* M19C: the status row is NOT a control, so none of its labels may read
       * as an instruction. "Add a photo" was an imperative sitting on a row
       * nothing happens when you press — the imperative now belongs solely to
       * the share card's CTA. These three are plain statements of fact. */
      var photoLabel = this.strings.statusPhotoTodo;
      /* An unfinished photo-only upload is NEITHER done NOR simply absent: the
       * entry exists server-side but has not published. Saying "Added a photo"
       * would claim something publicly visible that is not. */
      if (!photo && this.provisionalEntryId) {
        photoLabel = this.strings.statusPhotoPending || this.strings.statusPhotoTodo;
      }
      if (photo) photoLabel = this.strings.statusPhotoDone;

      /* BACKING STATUS — three states, all of them now knowable.
       *
       *   no marker          "No backing yet"            empty circle
       *   submitted          "confirm your email"        neutral ring, no tick
       *   verified           "Backed the office"         green tick
       *
       * The verified state comes from whichever page confirmed it: the
       * backing-verification page on a successful `backings/verify`, and —
       * since M22, where the backend creates the nominator's own backing as
       * part of confirming the nomination — the nomination-verification page
       * too. Both record a backing the SERVER has already made verified.
       *
       * It is still NOT inferred from `office.backingCount`, which says
       * nothing about whose backing was counted, and still NOT inferred from
       * merely holding a contributor credential: an office confirmed before
       * M22 has a credential and no nominator backing, and must not show a
       * tick its count would contradict.
       *
       * A submitted but unconfirmed backing still shows no tick: the campaign
       * counts only verified backings, so claiming otherwise would tell the
       * visitor their support counts when it may not. */
      var verifiedBacking = hasVerifiedBacking(this.currentSlug);
      var submitted = hasBacked(this.currentSlug);
      var backingLabel = this.strings.statusBackingTodo;
      if (verifiedBacking) backingLabel = this.strings.statusBackingDone;
      else if (submitted) backingLabel = this.strings.statusBackingPending;

      var rows = [
        { key: "backing", done: verifiedBacking, label: backingLabel },
        { key: "story", done: story, label: story ? this.strings.statusStoryDone : this.strings.statusStoryTodo },
        { key: "photo", done: photo, label: photoLabel }
      ];

      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        var node = this.els.checklist.querySelector('[data-jcw-check-row="' + row.key + '"]');
        if (!node) continue;

        if (row.done) {
          node.classList.add("jcw-check__row--done");
        } else {
          node.classList.remove("jcw-check__row--done");
        }
        /* Pending is its own visual state: no tick, but not the same empty
         * circle as "not started" either. */
        if (row.key === "backing" && submitted && !verifiedBacking) {
          node.classList.add("jcw-check__row--pending");
        } else {
          node.classList.remove("jcw-check__row--pending");
        }

        setText(node.querySelector("[data-jcw-check-label]"), row.label);
      }
    }

    /* Photo card state.
     *
     * The card is shown only once the contribution flow is available to this
     * browser, because every action on it opens that form. The polaroid is
     * rendered only from a photo this browser actually owns — never from another
     * coworker's photo, which would misrepresent whose contribution it is. */
    /* M21 — does this browser hold a verified contributor credential for the
     * office currently on screen?
     *
     * Presence is NOT authorization and is never treated as such: every write
     * is still decided by the backend, which re-checks the signature, the
     * office and the expiry. This only decides whether the UI offers an action
     * at all, so a visitor is not walked into a form that can only end in 403. */
    hasContributorCredential() {
      return !!readContributorToken(this.currentSlug);
    }

    /* Read/share access and verified write permission are separate. Share stays
     * usable for anyone holding a valid Office Wall link; the two contribution
     * actions are disabled with one short explanation and a recovery route,
     * rather than hidden and leaving an unexplained gap. */
    applyVerificationState() {
      var verified = this.hasContributorCredential();

      var actions = [this.els.photoCardCta, this.els.stronger, this.els.topEdit];
      for (var i = 0; i < actions.length; i++) {
        var action = actions[i];
        if (!action) continue;
        action.disabled = !verified;
        /* The button says what it does; the note below says why it cannot be
         * used. Pointing at the note keeps that explanation in the accessible
         * name path without rewriting the label. */
        if (verified) {
          action.removeAttribute("aria-describedby");
        } else if (this.verifyNoteId) {
          action.setAttribute("aria-describedby", this.verifyNoteId);
        }
      }

      if (this.els.verifyNote) this.els.verifyNote.hidden = verified;
    }

    /* M23 — the introduction, keyed on the same signal as the actions card so
     * the instruction and the available controls always agree. Both variants
     * ship in the markup; neither is a placeholder, so the block is shown as
     * soon as the Wall has content to introduce. */
    /* ---------- M24H: dashboard order, in the DOM ----------
     *
     * CSS `order` moved the boxes and left tab order and the screen-reader
     * sequence on the authored order, so a keyboard user met the two sections
     * in the opposite order to the one on screen. The nodes themselves are
     * moved instead — `insertBefore` RELOCATES an existing element, it does not
     * clone it, so no markup, id, handler or piece of state is duplicated.
     *
     * Never while someone is working. A contribution form, the backing form,
     * the profile editor or an open dialog all hold focus and unsaved input
     * that moving a subtree would disturb; the move is deferred and flushed
     * when the interaction ends. */
    dashboardWantsDashboardFirst() {
      var mobile = !!(this.dashQuery && this.dashQuery.matches);
      return (mobile ? this.dashMobile : this.dashDesktop) !== "below";
    }

    canReorderDashboard() {
      if (this.contribOpen || this.contribDirty) return false;
      if (this.els.profilePanel && !this.els.profilePanel.hidden) return false;
      if (this.els.backPanel && !this.els.backPanel.hidden) return false;
      var dialogs = document.querySelectorAll(".jcw__modal[open]");
      return dialogs.length === 0;
    }

    applyDashboardOrder() {
      var stack = this.els.stack;
      var dash = this.els.officeCard;
      var grid = this.els.ctaGrid;
      if (!stack || !dash || !grid) return;
      if (dash.parentNode !== stack || grid.parentNode !== stack) return;

      var wantDashFirst = this.dashboardWantsDashboardFirst();
      var isDashFirst = stack.firstElementChild === dash;
      if (wantDashFirst === isDashFirst) {
        this.pendingDashboardOrder = false;
        return;
      }

      if (!this.canReorderDashboard()) {
        this.pendingDashboardOrder = true;
        return;
      }

      /* Focus survives a move within the same document, but only if it is
       * restored deliberately: moving a subtree containing the active element
       * can drop focus to <body> in some engines. */
      var active = document.activeElement;
      var refocus = active && stack.contains(active) ? active : null;

      if (wantDashFirst) stack.insertBefore(dash, grid);
      else stack.insertBefore(grid, dash);

      if (refocus) this.focusElement(refocus);
      this.pendingDashboardOrder = false;
    }

    /* Called wherever a blocking interaction ends. */
    flushDashboardOrder() {
      if (this.pendingDashboardOrder) this.applyDashboardOrder();
    }

    openOfficePhoto() {
      if (!this.officePhotoUrl) return;
      var dialog = document.getElementById(this.officePhotoDialogId || "");
      if (!dialog || typeof dialog.show !== "function") return;

      if (this.els.officePhotoFull) {
        this.els.officePhotoFull.src = this.officePhotoUrl;
        this.els.officePhotoFull.alt = "";
      }
      this.closeOpenDialogs(dialog);
      dialog.show(this.els.officeMedia || null);
    }

    openPrizeDialog() {
      var dialog = document.getElementById(this.prizeDialogId || "");
      if (!dialog || typeof dialog.show !== "function") return;
      this.closeOpenDialogs(dialog);
      dialog.show(this.els.prizeOpen || null);
    }

    /* "ALREADY PARTICIPATING?" — passwordless access on another device.
     *
     * One email field. The backend matches it against THIS office only, mails
     * the nominator's or the backer's existing single-use link, and answers
     * every address identically, so this dialog has exactly one success state
     * and never reveals whether the address takes part, or as what.
     *
     * Nothing is granted here. The link opens the existing confirmation pages,
     * which store the restored grant and contributor credential exactly as a
     * first confirmation does. The address itself is sent once and never
     * stored: not in `jco:v1`, not in the DOM after success, not in a log. */
    openAccessDialog() {
      var dialog = document.getElementById(this.accessDialogId || "");
      if (!dialog || typeof dialog.show !== "function") return;
      this.bindAccessDialog(dialog);
      this.resetAccessDialog();
      this.closeOpenDialogs(dialog);
      dialog.show(this.els.accessOpen || null);
    }

    /* Bound on first open rather than at init: the dialog lives in <body>, and
     * this keeps a Wall that never opens it from touching it at all. */
    bindAccessDialog(dialog) {
      if (this.access) return;
      this.access = {
        inner: dialog.querySelector("[data-jcw-access-inner]"),
        request: dialog.querySelector("[data-jcw-access-request]"),
        form: dialog.querySelector("[data-jcw-access-form]"),
        email: dialog.querySelector("[data-jcw-access-email]"),
        submit: dialog.querySelector("[data-jcw-access-submit]"),
        submitLabel: dialog.querySelector("[data-jcw-access-submit-label]"),
        error: dialog.querySelector("[data-jcw-access-error]"),
        sent: dialog.querySelector("[data-jcw-access-sent]"),
        sentTitle: dialog.querySelector("[data-jcw-access-sent-title]"),
        titleId: ""
      };
      var title = dialog.querySelector(".jcw-accessmodal__title");
      this.access.titleId = title ? title.id || "" : "";
      if (this.access.form) {
        this.access.form.addEventListener("submit", this.submitAccess.bind(this));
      }
    }

    resetAccessDialog() {
      var a = this.access;
      if (!a) return;
      if (a.request) a.request.hidden = false;
      if (a.sent) a.sent.hidden = true;
      if (a.inner && a.titleId) a.inner.setAttribute("aria-labelledby", a.titleId);
      this.showAccessError("");
      this.setAccessBusy(false);
    }

    showAccessError(message) {
      var el = this.access && this.access.error;
      if (!el) return;
      setText(el, message || "");
      el.hidden = !message;
    }

    /* aria-disabled, never `disabled`, for the same focus reason as backing. */
    setAccessBusy(busy) {
      this.accessBusy = !!busy;
      var a = this.access;
      if (!a || !a.submit) return;
      a.submit.setAttribute("aria-disabled", busy ? "true" : "false");
      a.submit.classList.toggle("jcw-accessmodal__submit--busy", !!busy);
      setText(
        a.submitLabel,
        busy ? this.strings.accessSubmitting || "" : this.strings.accessSubmit || ""
      );
    }

    async submitAccess(event) {
      if (event && typeof event.preventDefault === "function") event.preventDefault();
      if (this.accessBusy) return;

      var a = this.access;
      if (!a || !a.form) return;

      if (typeof a.form.checkValidity === "function" && !a.form.checkValidity()) {
        if (typeof a.form.reportValidity === "function") a.form.reportValidity();
        return;
      }

      var slug = this.currentSlug;
      /* The Office Wall grant only — the endpoint refuses the share token. */
      var token = resolveWriteToken(slug);
      if (!slug || !token) {
        this.showAccessError(this.strings.backErrorAccess || "");
        return;
      }

      this.showAccessError("");
      this.setAccessBusy(true);

      var response;
      try {
        response = await fetch(
          PROXY_BASE + "/offices/" + encodeURIComponent(slug) + "/recover",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json"
            },
            body: JSON.stringify({
              participantToken: token,
              email: a.email ? a.email.value.trim() : ""
            }),
            credentials: "same-origin"
          }
        );
      } catch (e) {
        /* Never inspected or logged: it could echo the address. */
        this.setAccessBusy(false);
        this.showAccessError(this.strings.backErrorGeneric || "");
        return;
      }

      if (response.status === 401 || response.status === 403 || response.status === 404) {
        /* The grant is expired or revoked — the same handling as backing. */
        dropGrant(slug);
        if (SESSION_GRANT && SESSION_GRANT.slug === slug) SESSION_GRANT = null;
        this.setAccessBusy(false);
        this.showAccessError(this.strings.backErrorAccess || "");
        return;
      }

      /* Store- or office-wide ceiling. Never about the address typed. */
      if (response.status === 429) {
        this.setAccessBusy(false);
        this.showAccessError(this.strings.backErrorRateLimited || this.strings.backErrorGeneric || "");
        return;
      }

      var body = null;
      if (response.ok) {
        try {
          body = await response.json();
        } catch (e) {
          body = null;
        }
      }

      this.setAccessBusy(false);

      if (!body || typeof body !== "object" || body.ok !== true) {
        this.showAccessError(this.strings.backErrorGeneric || "");
        return;
      }

      /* The one confirmation. The address is cleared, not kept. */
      if (a.email) a.email.value = "";
      if (a.request) a.request.hidden = true;
      if (a.sent) a.sent.hidden = false;
      if (a.inner && a.sentTitle && a.sentTitle.id) {
        a.inner.setAttribute("aria-labelledby", a.sentTitle.id);
      }
      if (a.sentTitle && typeof a.sentTitle.focus === "function") a.sentTitle.focus();
    }

    /* M24V — the avatar letter, from the company name the DTO already
     * returned. Purely a presentation fallback for an office with no
     * photograph; it is never an identity and never derived from a person. */
    applyOfficeAvatar(companyName) {
      if (!this.els.officeAvatar) return;
      var name = typeof companyName === "string" ? companyName.trim() : "";
      /* Intl-safe first character: a surrogate pair must not be cut in half. */
      var initial = name ? Array.from(name)[0].toUpperCase() : "";
      setText(this.els.officeAvatar, initial);
      /* renderOffice() has the final say once it knows whether a photograph
       * exists; this only decides whether there is a letter to show at all. */
      this.els.officeAvatar.hidden = !initial || !!this.officePhotoUrl;
    }

    applyIntro() {
      if (!this.els.intro) return;
      var verified = this.hasContributorCredential();

      /* M25 — the pre-verification card names the nomination it is asking the
       * visitor to back. Both names go through fillPlaceholder(), which takes
       * its replacement literally: a company name containing `$&` must not be
       * able to rewrite the sentence around it. Both are written with
       * textContent further down, so markup in either is inert.
       *
       * M29H — the placeholders are `[[nominator]]` and `[[office]]`. The old
       * `{{ … }}` spelling still resolves, because it made the Theme Editor
       * reject the template rather than because it was ever rendered wrong. */
      var officeName = this.officeCompanyName || "";
      var headline;
      if (verified) {
        headline = this.strings.introVerifiedTitle || "";
      } else if (this.officeNominator) {
        headline = fillPlaceholder(
          fillPlaceholder(this.strings.heroNominatedBy, "nominator", this.officeNominator),
          "office",
          officeName
        );
      } else {
        headline = fillPlaceholder(this.strings.heroNominatedGeneric, "office", officeName);
      }
      setText(this.els.introTitle, headline);

      setText(
        this.els.introAccent,
        verified ? this.strings.heroAccentVerified : this.strings.heroAccentUnverified
      );
      /* M24V — the supporting sentence belongs to the pre-verification action.
       * Once there is no "Back this office" button it has nothing to explain,
       * so the paragraph is emptied AND hidden rather than left as a gap. */
      var body = verified ? this.strings.introVerifiedBody : this.strings.introUnverifiedBody;
      setText(this.els.introBody, body);
      if (this.els.introBody) this.els.introBody.hidden = !body;

      this.els.intro.hidden = false;
    }

    /* M24V — ONE state class for the whole section.
     *
     * The campaign polaroids live in the header, outside the content block the
     * rest of the state handling touches, and their arrangement differs per
     * state AND per breakpoint. Driving that from CSS off a single class keeps
     * every composition rule in the stylesheet instead of spreading geometry
     * through the script. */
    applyVerifiedClass() {
      this.classList.toggle("jcw--verified", this.hasContributorCredential());
    }

    applyPhotoCard(available) {
      if (!this.els.photoCard) return;

      /* THE CARD IS ALWAYS SHOWN ONCE THE WALL HAS LOADED.
       *
       * It used to be hidden whenever the contribution flow was unavailable,
       * on the reasoning that one live button beside two dead ones reads worse
       * than a card that arrives complete. That reasoning is obsolete and the
       * behaviour was wrong: this card also hosts "Share your Office Wall",
       * and sharing requires only READ access — never a backing, a nomination
       * or a contributor credential. Hiding the card took the share action
       * away from exactly the read-only visitor it exists for.
       *
       * M21 removed the premise too: the two write actions are now disabled
       * with a short explanation rather than silently dead, so the mixed state
       * is legible instead of confusing.
       *
       * No state check is needed for loading/private/error either — the card
       * lives inside `data-jcw-content`, which those states hide wholesale. */
      /* M23 — THE ACTIONS CARD IS FOR VERIFIED PARTICIPANTS ONLY.
       *
       * This reverses the M21 rule that the card is always shown once the Wall
       * loads. That rule existed to keep Share reachable for anyone holding a
       * valid read link; the campaign has since decided that an unverified
       * visitor should see ONE primary action — "Back this office" — and not a
       * card of three, two of which they cannot use. The consequence is
       * deliberate and worth stating: a read-only visitor can no longer
       * re-share the Wall, because Share now travels with the other two.
       *
       * The gate is `hasContributorCredential()`, the same verified-participant
       * signal the contribution actions already use — so a verified nominator
       * has the card from their first visit without backing their own office,
       * a coworker gets it the moment they confirm their email, and a coworker
       * who has only SUBMITTED a backing does not. The backend still decides
       * every write; this only decides what is offered. */
      this.els.photoCard.hidden = !this.hasContributorCredential();

      /* M29 — the owned entry that actually carries a photo, which is not
       * always the most recent one.
       *
       * The fix above stops NEW splits, but anyone who already hit the bug has
       * two entries server-side and they cannot be merged from here. Looking
       * for the photo across everything this browser owns repairs the display
       * for them too, and is simply more honest: the question this card asks
       * is "does this participant have a photo on the Wall", not "does their
       * newest entry have one". */
      var owned = this.ownedPhotoEntry() || this.ownedEntry();
      var url = owned && hasRenderablePhoto(owned) ? firstString(owned.photo || owned, ["url", "photoUrl"]) : "";

      var has = !!url;
      var pending = !has && !!this.provisionalEntryId;

      /* "Change your photo" is only truthful once a photo is actually
       * published. An unfinished upload gets the retry wording instead, so the
       * button never implies the photo is already on the Wall. */
      setText(
        this.els.photoCardCtaLabel,
        has ? this.copy.photoCtaChange : pending ? this.copy.photoCtaRetry : this.copy.photoCtaAdd
      );
      if (this.els.photoCardCta) this.els.photoCardCta.hidden = false;

      /* M24 — THE POLAROID.
       *
       * Shown only for a real photo this browser owns, and only alongside a
       * visible actions card, because it is a confirmation of what those
       * actions achieved. Never a placeholder frame and never a coworker's
       * photo. The src goes through safeImageUrl() like every other DTO image,
       * and it is rewritten on every render, so a replacement updates it. */
      if (this.els.photoCardFigure) {
        var cardVisible = this.els.photoCard.hidden === false;
        var showPolaroid = has && cardVisible;

        /* M30 — THE EMPTY FRAME IS AN INVITATION.
         *
         * Shown when the card is visible (so this browser holds a verified
         * contributor credential and can actually act on it) and there is no
         * photo to show. It is presentation only: no entry, no request, and
         * the Photos statistic is counted from the DTO's entries, so an empty
         * frame cannot move it.
         *
         * It also covers the unfinished-upload state. "No photo is on the
         * Wall" is true there too, and the CTA beside it already carries the
         * retry wording, so the frame does not need to contradict it. */
        var showPlaceholder = !has && cardVisible && !!this.copy.photoPlaceholder;

        if (showPolaroid && this.els.photoCardImage) {
          var self = this;
          /* A signed read URL that has expired must not leave an empty frame
           * claiming a photo is on the Wall. */
          this.els.photoCardImage.onerror = function () {
            if (self.els.photoCardFigure) self.els.photoCardFigure.hidden = true;
          };
          this.els.photoCardImage.src = safeImageUrl(url);
          this.els.photoCardImage.alt = "";
          setText(this.els.photoCardTitle, this.copy.photoTitleHas || "");
        }

        /* The <img> never carries a src in the placeholder state, so nothing
         * requests a missing file and no broken-image glyph can appear. */
        if (this.els.photoCardImage) this.els.photoCardImage.hidden = showPlaceholder;
        if (this.els.photoCardPlaceholder) this.els.photoCardPlaceholder.hidden = !showPlaceholder;
        if (showPlaceholder) {
          setText(this.els.photoCardPlaceholderLabel, this.copy.photoPlaceholder);
          /* The caption belongs to a real photo; the invitation lives inside
           * the frame, so the caption is cleared rather than reused. */
          setText(this.els.photoCardTitle, "");
        }

        this.els.photoCardFigure.hidden = !(showPolaroid || showPlaceholder);
      }

      /* Last, so it has the final say over every action on this card. */
      this.applyVerificationState();
    }

    /* ---------- Sharing (Milestone 19) ----------
     *
     * WHAT IS SHARED, AND WHY IT IS SAFE.
     *
     * The payload is the Office Wall share link and nothing else. That link is
     * the office's own share capability: server-side, the raw access token is
     * accepted by exactly ONE operation — reading this Wall. Every write
     * (backing, photo, wall entry) requires a participantToken the backend
     * issues separately, and editing an entry additionally requires that
     * entry's editToken. So passing this link to a coworker grants precisely
     * what the campaign intends — see the Wall, then begin participating — and
     * confers no owner rights and no ability to edit anyone's contribution.
     *
     * NEVER in the payload: participantToken, any editToken, any backing or
     * nomination verification token. None of them is read by this code path.
     *
     * The URL is rebuilt from the page the visitor is already on rather than
     * from a hardcoded path, so a renamed Shopify page cannot produce a share
     * link that 404s. */
    shareUrl() {
      var slug = this.currentSlug;
      var token = rawAccessToken(slug);
      if (!slug || !token) return "";

      try {
        var url = new URL(window.location.href);
        /* Only the Office slug travels in the query — it is an identifier, not
         * a credential, and keeping it there makes the link bookmarkable. The
         * token goes in the FRAGMENT, which browsers do not send to the server
         * and which this script strips from the address bar on arrival. */
        url.search = "";
        url.searchParams.set("office", slug);
        url.hash = "access=" + encodeURIComponent(token);
        return url.toString();
      } catch (e) {
        return "";
      }
    }

    /* The message, with the live VERIFIED backing count.
     *
     * `office.backingCount` is the only count used. Its backend invariant is
     * that a backing contributes to it if and only if its email was confirmed,
     * so the number in the message cannot overstate support. The `backings`
     * array is deliberately NOT counted here: it is not filtered by
     * verification, so its length would. */
    shareMessage(url) {
      var count = this.verifiedBackingCount;

      /* ONE template for every backing count.
       *
       * M30 briefly carried three, chosen by the count, so the wording could
       * stay grammatical around `[[count]]`. That is superseded: the merchant
       * writes one message and is responsible for phrasing it so it reads
       * correctly at any count. Nothing here rewrites their grammar, and no
       * count-based branch is left to drift out of step with the copy. */
      var template = this.strings.shareMessage;
      template = typeof template === "string" ? template : "";
      if (!template) return "";

      /* M30 — merchant placeholders.
       *
       * `[[ … ]]`, never `{{ … }}`: these templates are section settings now,
       * and the Theme Editor reads curly braces in a setting value as a dynamic
       * source and refuses to save the template. See the M29 hotfix.
       *
       * Only these four are substituted, and all four are values the campaign
       * already publishes: the company name and the nominator's first name are
       * both shown on the Wall itself, the count is the VERIFIED backing count,
       * and the link is the existing invitation URL. No credential is reachable
       * from here — `shareUrl()` is the only thing that touches the office
       * share token, and nothing else is exposed to the template. An unknown
       * placeholder is left exactly as the merchant typed it rather than being
       * silently emptied, so a typo is visible instead of mysterious. */
      var filled = fillPlaceholder(template, "office", this.officeCompanyName || "");
      filled = fillPlaceholder(filled, "count", String(count));
      filled = fillPlaceholder(filled, "nominator", this.officeNominator || "");
      filled = fillPlaceholder(filled, "link", url || "");
      return filled;
    }

    /* The message as it is actually copied: if the merchant placed `[[link]]`
     * the URL is already inside the sentence, otherwise it is appended, which
     * is what this card did before the placeholder existed. */
    shareMessageWithLink(url) {
      var text = this.shareMessage(url);
      if (!text) return url || "";
      if (!url || text.indexOf(url) !== -1) return text;
      return text + " " + url;
    }

    /* One clipboard path for both buttons.
     *
     * Returns nothing and never throws: every outcome is reported in the live
     * region. `writeText` rejects on a denied permission, in a cross-origin
     * iframe and on browsers that expose the API only over HTTPS, so the
     * failure branch is a real path rather than defensive decoration. */
    async copyToClipboard(payload, successMessage) {
      if (!payload) {
        this.setShareStatus(this.strings.shareUnavailable || "");
        return;
      }
      try {
        if (navigator && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
          await navigator.clipboard.writeText(payload);
          this.setShareStatus(successMessage || this.strings.shareCopied || "");
          return;
        }
      } catch (e) {
        /* Denied or unavailable. The advice below is the useful answer. */
      }
      this.setShareStatus(this.strings.shareCopyFailed || "");
    }

    /* ONE SHARE ACTION — native sheet where it exists, clipboard where it does
     * not.
     *
     * M30 briefly replaced this with two copy buttons; the M30 correction puts
     * the single CTA back. The choice is FEATURE DETECTION and nothing else:
     * `typeof navigator.share === "function"`. No screen width, no user-agent
     * sniffing, no "mobile only" — a desktop browser that offers the Web Share
     * API gets the sheet exactly as a phone does.
     *
     * NO DUPLICATE URL.
     *
     * The Web Share API renders `text` and `url` by concatenating them in most
     * targets, so passing both when the merchant has already written `[[link]]`
     * into the template posts the same link twice. The payload is therefore
     * chosen from where the URL actually is:
     *
     *   template contains the URL  ->  { title, text }        (URL inside text)
     *   template does not          ->  { title, text, url }   (URL in its own field)
     *
     * Either way the invitation appears exactly ONCE, and the clipboard
     * fallback uses shareMessageWithLink(), which appends only when the text
     * does not already carry it — the same single-occurrence guarantee.
     *
     * A cancelled sheet is a decision, not a failure: it returns silently and
     * must never fall through to the clipboard, which would copy something the
     * visitor just declined to send. A genuine error does fall through, because
     * somebody who pressed Share still wants the message. */
    async shareWall() {
      /* Double activation guard. A second press while the sheet is open (or a
       * clipboard write is in flight) is ignored rather than queued. */
      if (this.sharing) return;

      var url = this.shareUrl();
      if (!url) {
        /* The raw access token only exists in memory when this page was opened
         * from a share link. A visitor who returned on a stored participant
         * grant has no token to pass on, and the theme must not invent one. */
        this.setShareStatus(this.strings.shareUnavailable || "");
        return;
      }

      this.sharing = true;
      if (this.els.shareBtn) this.els.shareBtn.setAttribute("aria-busy", "true");

      try {
        var message = this.shareMessage(url);
        var embedsUrl = !!message && message.indexOf(url) !== -1;

        if (navigator && typeof navigator.share === "function") {
          var payload = { title: this.strings.shareTitle || "" };
          if (message) payload.text = message;
          /* `url` is added ONLY when the text does not already carry it — that
           * single condition is the whole duplicate-URL rule. With no message
           * at all there is nothing to put in `text`, and the link alone is
           * still a valid thing to share. */
          if (!embedsUrl) payload.url = url;

          try {
            await navigator.share(payload);
            /* Shared. The sheet is its own confirmation, so the live region is
             * cleared rather than given a message the visitor does not need. */
            this.setShareStatus("");
            return;
          } catch (e) {
            /* Cancelled: a normal user action. Nothing is copied, nothing is
             * said, and the previous status is left untouched. */
            if (e && e.name === "AbortError") return;
            /* Anything else is a real failure of the sheet, so fall through to
             * the clipboard rather than leaving the visitor with nothing. */
          }
        }

        await this.copyToClipboard(
          this.shareMessageWithLink(url),
          this.strings.shareMessageCopied || this.strings.shareCopied || ""
        );
      } finally {
        this.sharing = false;
        if (this.els.shareBtn) this.els.shareBtn.removeAttribute("aria-busy");
      }
    }

    setShareStatus(message) {
      setText(this.els.shareStatus, message || "");
    }

    renderEntries(entries, backings) {
      /* Reset FIRST, before any early return: ownership must describe the Wall that is
       * actually on screen, never a previous one. */
      this.ownedVisible = [];
      this.topEditEntry = null;

      var list = this.els.entries;
      var empty = this.els.empty;
      if (!list || !empty) return;

      /* Safety net: the contribution shell is a single reusable element. If it is
       * still parked inside an entry when the list is rebuilt, clearing the list would
       * destroy the form, its listeners and the success region with it. */
      this.parkContribution();

      list.textContent = "";

      var entryTemplate = document.getElementById("JcwEntryTemplate");
      var backingTemplate = document.getElementById("JcwBackingTemplate");
      var photoTemplate = document.getElementById("JcwPhotoTemplate");

      /* One storage read for the whole list rather than one per entry. Ids only — no
       * token value is produced here. */
      /* Frozen entries are left out: they are still this browser's, but no
       * control may target them. See refreshOwnModeration(). */
      var ownedIds = this.editableOwnedIds(readOwnedEntryIds(this.currentSlug));
      var owned = {};
      for (var o = 0; o < ownedIds.length; o++) owned[ownedIds[o]] = true;

      var timeline = buildTimeline(entries, backings);
      var anonymous = this.strings.anonymous || "";

      for (var i = 0; i < timeline.length; i++) {
        var item = timeline[i];
        var node = null;

        if (item.type === "contribution") {
          if (!usableTemplate(entryTemplate)) continue;

          /* Owned FOR THIS UI means both halves: the entry is on the Wall right now,
           * AND this browser holds an edit credential for its id. A stale credential
           * for an entry the DTO no longer returns matches nothing here.
           *
           * This runs ONLY for contributions. A backing id is never looked up in the
           * edit-credential store, so a backing can never acquire entry controls. */
          var itemId = typeof item.data.id === "string" ? item.data.id.trim() : "";
          if (itemId && owned[itemId] === true) this.recordOwnedVisible(item.data);

          node = this.buildEntry(item.data, entryTemplate, anonymous, owned);
        } else if (item.type === "photo") {
          if (!usableTemplate(photoTemplate)) continue;
          /* A photo row represents an ENTRY too, and since M19 a photo-only
           * entry produces no contribution row at all — it has no written
           * contribution to announce. Recording ownership here as well is what
           * keeps such an entry recognised as published: without it the entry
           * was absent from `ownedVisible`, the provisional check then treated
           * a perfectly good published photo as an unfinished upload, and the
           * card offered "Try your photo again" for a photo already on the
           * Wall. Deduplicated by id, so an entry with both rows counts once. */
          var photoId = typeof item.data.id === "string" ? item.data.id.trim() : "";
          if (photoId && owned[photoId] === true) this.recordOwnedVisible(item.data);

          node = this.buildPhoto(item, photoTemplate, anonymous);
        } else {
          if (!usableTemplate(backingTemplate)) continue;
          node = this.buildBacking(item.data, backingTemplate, anonymous);
        }

        if (node) list.appendChild(node);
      }

      /* Legacy/test state: DEV data can contain several locally owned entries. They are
       * never deleted or migrated — each keeps its own row control — but the prominent
       * action can only point at one, so it targets the most recently submitted. */
      this.topEditEntry = this.ownedVisible.length ? mostRecentEntry(this.ownedVisible) : null;

      /* PROVISIONAL ENTRIES (M17D).
       *
       * The Wall DTO selects `status: "APPROVED"` only, so a provisional entry is
       * invisible here by design. An id this browser holds an edit credential for
       * that is NOT on the Wall is therefore an entry that exists server-side but
       * has not published — an abandoned or failed photo-only upload.
       *
       * It is derived rather than stored: `jco:v1` already carries the edit
       * credentials, and `readStore()` reconstructs from named keys, so adding a
       * sixth namespace would risk silently erasing one of the five. */
      var visibleIds = {};
      for (var v = 0; v < this.ownedVisible.length; v++) {
        var vid = this.ownedVisible[v] && typeof this.ownedVisible[v].id === "string"
          ? this.ownedVisible[v].id.trim()
          : "";
        if (vid) visibleIds[vid] = true;
      }
      /* M27 — a dangling credential is only an UNFINISHED UPLOAD if this
       * browser actually started one. Without that, a brand-new participant
       * who has never touched the photo control was shown "Try your photo
       * again", and the button it offered could not work: the entry it pointed
       * at does not exist, so the editor had no row to open into.
       *
       * The credential itself is still kept. It is NOT evidence of a pending
       * upload, but it may be a perfectly good credential for an entry that is
       * off the end of the feed, and dropping it would be unrecoverable. */
      this.provisionalEntryId = null;
      if (this.photoAttempted) {
        /* A frozen entry missing from the Wall is moderated, not unfinished. */
        var ownedIds = this.editableOwnedIds(readOwnedEntryIds(this.currentSlug) || []);
        for (var o = 0; o < ownedIds.length; o++) {
          if (!visibleIds[ownedIds[o]]) {
            this.provisionalEntryId = ownedIds[o];
            break;
          }
        }
      }

      /* The empty state belongs to the WHOLE timeline, so a Wall carrying only backing
       * activity is not reported as empty. */
      var hasItems = list.children.length > 0;
      list.hidden = !hasItems;
      empty.hidden = hasItems;
    }

    /* One compact backing activity row.
     *
     * Deliberately NOT a contribution: no View Entry, no Edit, no chips, no modal, no
     * photo, no role label and nothing interactive at all. Only `displayName` and
     * `createdAt` are read — the backing DTO carries nothing else, and the backing
     * email is never part of it and is never rendered. */
    buildBacking(backing, template, anonymous) {
      if (!backing || typeof backing !== "object") return null;

      var node = template.content.firstElementChild.cloneNode(true);
      var name = firstString(backing, ["displayName", "display_name", "name"]) || anonymous;

      var line = node.querySelector("[data-jcw-activity-line]");
      var nameEl = node.querySelector("[data-jcw-activity-name]");

      if (line && nameEl) {
        setText(nameEl, name);

        /* Split the locale sentence around its {{ name }} placeholder so the name keeps
         * its own element for typography while the theme makes no assumption about word
         * order. Both halves are text nodes — no innerHTML anywhere. */
        var pattern = this.strings.activityBacked || "";
        var token = "{{ name }}";
        var at = pattern.indexOf(token);
        var before = at === -1 ? "" : pattern.slice(0, at);
        var after = at === -1 ? (pattern ? " " + pattern : "") : pattern.slice(at + token.length);

        if (before) line.insertBefore(document.createTextNode(before), nameEl);
        if (after) line.appendChild(document.createTextNode(after));
      }

      renderTimestamp(
        node.querySelector("[data-jcw-activity-meta]"),
        node.querySelector("[data-jcw-activity-time]"),
        firstString(backing, ["createdAt", "created_at", "submittedAt"])
      );

      return node;
    }

    /* One photo activity row.
     *
     * The contribution it belongs to keeps its own separate row — this is an additional
     * item, not a replacement. The preview uses the same short-lived signed URL the
     * dialog does; no thumbnail is generated and no second request is made.
     *
     * Activating it opens the existing View Entry dialog on the associated entry, so
     * the full-size photo, the story and the answers all stay in one place. */
    buildPhoto(item, template, anonymous) {
      var entry = item.data;
      if (!entry || typeof entry !== "object") return null;

      var node = template.content.firstElementChild.cloneNode(true);

      /* M23 — the row identifies its entry, so findEntryRow()/findEntryEditSlot()
       * can host the editor here. A photo-only entry has no contribution row,
       * and without this the photo editor had nowhere to open. */
      var photoEntryId = typeof item.data.id === "string" ? item.data.id.trim() : "";
      if (photoEntryId) node.setAttribute("data-jcw-entry-id", photoEntryId);
      else node.removeAttribute("data-jcw-entry-id");
      var name = firstString(entry, ["displayName", "display_name", "name"]) || anonymous;

      var line = node.querySelector("[data-jcw-activity-line]");
      var nameEl = node.querySelector("[data-jcw-activity-name]");

      if (line && nameEl) {
        /* Same construction as the backing row: the sentence is split around its
         * {{ name }} placeholder so the name keeps its own element, and both halves are
         * text nodes. A contributor name is never interpolated into markup. */
        /* "You" when this browser owns the entry the photo belongs to, resolved
         * from the edit credential and never from the name. */
        setText(nameEl, this.ownsEntry(entry) ? this.youLabel() : name);

        var pattern = this.strings.activityPhoto || "";
        var token = "{{ name }}";
        var at = pattern.indexOf(token);
        var before = at === -1 ? "" : pattern.slice(0, at);
        var after = at === -1 ? (pattern ? " " + pattern : "") : pattern.slice(at + token.length);

        if (before) line.insertBefore(document.createTextNode(before), nameEl);
        if (after) line.appendChild(document.createTextNode(after));
      }

      /* A time is shown ONLY when the photo carries its own. When photo.updatedAt is
       * absent the event still sorts by the entry's submission time, but displaying
       * that would assert a photo date nobody actually recorded. */
      if (item.dated) {
        renderTimestamp(
          node.querySelector("[data-jcw-activity-meta]"),
          node.querySelector("[data-jcw-activity-time]"),
          entry.photo.updatedAt
        );
      } else {
        var meta = node.querySelector("[data-jcw-activity-meta]");
        if (meta) meta.remove();
      }

      var button = node.querySelector("[data-jcw-photo-open]");
      var tile = node.querySelector("[data-jcw-photo-tile]");
      if (!button) return node;

      /* Distinct accessible name per control — "View photo, Alex" — rather than a
       * timeline full of identical "View photo" buttons. */
      setText(button.querySelector("[data-jcw-photo-open-label]"), this.strings.viewPhoto || "");
      setText(button.querySelector("[data-jcw-photo-open-context]"), name);

      if (tile) {
        /* A signed read URL lives about 15 minutes. If this one has expired the tile
         * is hidden rather than left as a broken image; the control stays, and the
         * dialog it opens carries its own reload affordance. */
        tile.onerror = function () {
          tile.hidden = true;
        };
        tile.src = safeImageUrl(entry.photo.url);
      }

      var self = this;
      button.addEventListener("click", function () {
        self.openEntry(entry, self.resolveAnswers(entry), button);
      });

      return node;
    }

    buildEntry(entry, template, anonymous, owned) {
      if (!entry || typeof entry !== "object") return null;

      var node = template.content.firstElementChild.cloneNode(true);

      /* The entry id is an IDENTIFIER, not a credential — the backend contract states
       * it is safe as a DOM reference. It is what lets an edit relocate the form into
       * the right row and lets a confirmation come back to it. The edit TOKEN is never
       * written to the DOM. */
      var entryId = typeof entry.id === "string" ? entry.id.trim() : "";
      if (entryId) node.setAttribute("data-jcw-entry-id", entryId);

      /* Entry DTO browser-verified 2026-10-05 against a real APPROVED entry:
       * `displayName`, `roleLabel`, `message` (null omitted cleanly) and
       * `answers[] { promptKey, answerText }`. Every field stays presence-guarded, so an
       * entry carrying none of them still renders safely with the anonymous label. */
      var author = firstString(entry, ["displayName", "display_name", "name", "authorName"]);
      var role = firstString(entry, ["roleLabel", "role_label", "role"]);
      var message = firstString(entry, ["message", "text", "body"]);
      var created = firstString(entry, ["submittedAt", "createdAt", "created_at", "publishedAt"]);

      /* "You" only when this browser provably holds the entry's edit credential.
       * Never by comparing display names — see ownsEntry(). The contributor's
       * own name is still used for the View entry control's accessible name, so
       * that control stays distinguishable in a list.
       *
       * M19: the row states what HAPPENED, not just who. The name keeps its own
       * element inside the sentence — the pattern is split around its
       * {{ name }} placeholder and both halves are written as text, exactly as
       * the backing and photo rows do it, so a contributor name is never
       * interpolated into markup. */
      var mine = this.ownsEntry(entry);
      var who = mine ? this.youLabel() : author || anonymous;
      var authorEl = node.querySelector("[data-jcw-entry-author]");

      if (authorEl) {
        var sentence = mine
          ? this.strings.activityContributionYou || ""
          : this.strings.activityContribution || "";

        authorEl.textContent = "";
        var nameSpan = document.createElement("span");
        nameSpan.className = "jcw-row__name";
        setText(nameSpan, who);

        var marker = "{{ name }}";
        var at = sentence.indexOf(marker);
        if (at === -1) {
          /* "You submitted a contribution" carries no placeholder. */
          if (sentence) {
            authorEl.appendChild(nameSpan);
            authorEl.appendChild(document.createTextNode(sentence.replace(/^You\s*/, " ")));
          } else {
            authorEl.appendChild(nameSpan);
          }
        } else {
          var before = sentence.slice(0, at);
          var after = sentence.slice(at + marker.length);
          if (before) authorEl.appendChild(document.createTextNode(before));
          authorEl.appendChild(nameSpan);
          if (after) authorEl.appendChild(document.createTextNode(after));
        }
      }

      var roleEl = node.querySelector("[data-jcw-entry-role]");
      if (roleEl) {
        if (role) {
          setText(roleEl, role);
          roleEl.hidden = false;
        } else {
          roleEl.remove();
        }
      }

      renderTimestamp(
        node.querySelector("[data-jcw-entry-meta]"),
        node.querySelector("[data-jcw-entry-time]"),
        created
      );

      /* Wire the compact row's native control to the ONE shared dialog. The entry
       * object is captured in this closure — it is never written to a DOM attribute,
       * never serialised, and never re-fetched. */
      /* Resolved ONCE and reused by the dialog, so a contribution made only of
       * selected chips — no message, no free text — is correctly treated as having
       * detail. The earlier test looked at `answerText` alone and would have removed
       * the control from exactly those entries. */
      var answerRows = this.resolveAnswers(entry);

      var viewBtn = node.querySelector("[data-jcw-view-entry]");
      if (viewBtn) {
        var hasDetail = !!message || answerRows.length > 0;

        if (hasDetail && this.els.modal) {
          setText(viewBtn.querySelector("[data-jcw-view-label]"), this.strings.viewEntry || "");
          /* Distinct accessible name per control: "View entry, Alex from Marketing". */
          setText(viewBtn.querySelector("[data-jcw-view-context]"), author || anonymous);
          var self = this;
          viewBtn.addEventListener("click", function () {
            self.openEntry(entry, answerRows, viewBtn);
          });
        } else {
          /* Nothing to reveal — do not offer a control that opens an empty dialog. */
          viewBtn.remove();
        }
      }

      /* "Edit your contribution" exists ONLY on entries this browser holds a usable
       * local edit credential for. Everything else has the control REMOVED from the
       * DOM, not hidden: a coworker's entry carries no edit affordance at all, for
       * keyboard users, screen readers and anyone reading the markup alike.
       *
       * Ownership is a boolean test. The token itself is never read here. */
      /* M19C: the per-row edit control is gone from the template. The edit slot
       * below remains, because the share card's CTA still relocates the
       * contribution panel into the row it is editing. Nothing here needs to
       * wire a trigger any more. */

      return node;
    }

    /* ---------- Backing (Milestone 10) ---------- */

    toggleBackingPanel() {
      var panel = this.els.backPanel;
      var toggle = this.els.backToggle;
      if (!panel || !toggle) return;

      var open = panel.hidden;
      panel.hidden = !open;
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
      /* Hide the reveal button once the form is open, so the panel's own submit is
       * the only "BACK THIS OFFICE" control on screen. aria-expanded is set before
       * the control is hidden, so the state is correct at every moment. */
      toggle.hidden = open;

      if (open && this.els.backName) {
        try {
          this.els.backName.focus({ preventScroll: true });
        } catch (e) {
          /* focus is a convenience only */
        }
      }
    }

    showBackingError(message) {
      var el = this.els.backError;
      if (!el) return;
      setText(el, message || "");
      el.hidden = !message;
    }

    setBackingBusy(busy) {
      this.backingBusy = !!busy;

      var submit = this.els.backSubmit;
      if (!submit) return;

      /* aria-disabled, never the `disabled` property: disabling the element that
       * currently holds focus drops focus to <body>. The busy flag is what actually
       * blocks a second submission, so a double-click cannot produce two requests. */
      submit.setAttribute("aria-disabled", busy ? "true" : "false");
      submit.classList.toggle("jcw__back-submit--busy", !!busy);

      setText(
        this.els.backSubmitLabel,
        busy ? this.strings.backSubmitting || "" : this.strings.backSubmit || ""
      );
    }

    async submitBacking(event) {
      if (event && typeof event.preventDefault === "function") event.preventDefault();
      if (this.backingBusy) return;

      var form = this.els.backForm;
      if (!form) return;

      /* Native constraint validation first — `required` and `type="email"` already
       * express what this form needs, so no parallel custom validator is added. */
      if (typeof form.checkValidity === "function" && !form.checkValidity()) {
        if (typeof form.reportValidity === "function") form.reportValidity();
        return;
      }

      var slug = this.currentSlug;
      /* WRITE credential only. The raw Office access token is never acceptable here,
       * and resolveWriteToken() cannot return it. */
      var token = resolveWriteToken(slug);

      if (!slug || !token) {
        this.showBackingError(this.strings.backErrorAccess || "");
        return;
      }

      this.showBackingError("");
      this.setBackingBusy(true);

      var payload = {
        participantToken: token,
        name: this.els.backName ? this.els.backName.value.trim() : "",
        email: this.els.backEmail ? this.els.backEmail.value.trim() : ""
      };
      /* Only ever a real boolean, and only when the control exists. */
      if (this.els.backConsent) {
        payload.contestUpdatesConsent = this.els.backConsent.checked === true;
      }

      var response;
      try {
        response = await fetch(
          PROXY_BASE + "/offices/" + encodeURIComponent(slug) + "/backings",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json"
            },
            body: JSON.stringify(payload),
            credentials: "same-origin"
          }
        );
      } catch (e) {
        /* Network failure. The thrown value is never inspected or logged, so a
         * request-echoing error object cannot expose the credential or the PII in
         * the body. The grant and the entered values are both retained for retry. */
        this.setBackingBusy(false);
        this.showBackingError(this.strings.backErrorGeneric || "");
        return;
      }

      if (response.status === 401 || response.status === 403 || response.status === 404) {
        /* The grant is expired or revoked. Drop exactly this Office's grant and the
         * matching session grant — and never retry the write with the raw Office
         * token, which the backing endpoint does not accept in any case. */
        dropGrant(slug);
        if (SESSION_GRANT && SESSION_GRANT.slug === slug) SESSION_GRANT = null;
        this.setBackingBusy(false);
        this.showBackingError(this.strings.backErrorAccess || "");
        return;
      }

      /* M21 — the backend's cooldown and daily limit answer 429. Saying
       * "something went wrong" would invite exactly the repeated retries the
       * limit exists to stop, and would hide the one useful instruction:
       * wait a little. Not an error about the backing itself. */
      if (response.status === 429) {
        this.setBackingBusy(false);
        this.showBackingError(this.strings.backErrorRateLimited || this.strings.backErrorGeneric || "");
        return;
      }

      /* M31 — one address, one office.
       *
       * The ONLY new response on this endpoint (backend 64e41f3,
       * app/backing-proxy.server.ts): 409 { ok:false, code:"duplicate_email" }
       * when the address already takes part in a DIFFERENT office. Backing the
       * office this browser is already on never answers 409, so a repeat
       * submission, the nominator's own automatic backing and a returning
       * supporter all still succeed exactly as before.
       *
       * Everything else about this endpoint is unchanged on purpose. A new
       * backing, a repeat, an already-confirmed address and a cooled-down
       * resubmission all still answer 200 { ok:true, backingCount } and stay
       * deliberately indistinguishable, so a grant holder cannot probe whether
       * a colleague has already backed. There is no cooldown, resend,
       * already-participating or dispatch-failure code here — the nomination
       * endpoint has those, this one does not, and inventing them in the UI
       * would describe behaviour the backend does not have.
       *
       * The grant is NOT dropped: 409 is a statement about the address that was
       * typed, not about this browser's access. Entered values are kept so the
       * coworker can correct the address and submit again. */
      if (response.status === 409) {
        var duplicate = false;
        try {
          var conflict = await response.json();
          duplicate = !!conflict && conflict.code === "duplicate_email";
        } catch (e) {
          duplicate = false;
        }
        this.setBackingBusy(false);
        this.showBackingError(
          (duplicate ? this.strings.backErrorDuplicateEmail : "") ||
            this.strings.backErrorGeneric ||
            ""
        );
        return;
      }

      if (!response.ok) {
        this.setBackingBusy(false);
        this.showBackingError(this.strings.backErrorGeneric || "");
        return;
      }

      var body;
      try {
        body = await response.json();
      } catch (e) {
        this.setBackingBusy(false);
        this.showBackingError(this.strings.backErrorGeneric || "");
        return;
      }

      if (!body || typeof body !== "object" || body.ok !== true) {
        this.setBackingBusy(false);
        this.showBackingError(this.strings.backErrorGeneric || "");
        return;
      }

      /* Convenience prefill only, from the name the coworker just submitted. The
       * email in `payload` is deliberately NOT stored. */
      saveContributorName(slug, payload.name);

      this.setBackingBusy(false);

      /* Persist the browser-local marker BEFORE the refresh, so the re-render's
       * applyBackingState() already knows this browser has backed instead of briefly
       * rendering the ready state on its way to the success state. */
      markBacked(slug);

      /* Pull the authoritative Wall so the backing the backend just created appears as
       * activity without a hard page refresh. Quiet, so the rendered Wall stays on
       * screen and focus is not dropped to <body>. The row is NEVER synthesised from
       * the submitted form values — only the backend DTO is ever rendered. */
      var refreshed = await this.load({ quiet: true });

      /* The backing itself succeeded; only the refresh failed, and load() has already
       * shown the Wall error state, which hides the region the success message lives
       * in. The marker is stored, so a retry lands on the returning-backer state. */
      if (!refreshed) return;

      this.showBackingSuccess(body.backingCount);
    }

    /* The response is deliberately identical for a new and a duplicate backing, so
     * nothing here may hint at which occurred. The count is always the authoritative
     * server value — never a local increment. Nothing about this submission is
     * persisted: a refresh returns to the ready state on purpose, because a shared
     * link or device may be used by several coworkers in turn. */
    /* Chooses between the ready CTA and the returning-backer confirmation on load.
     * Office-scoped: backing Office A never hides the CTA for Office B. Deliberately
     * does NOT move focus — this is ordinary page load, not a fresh success. */
    applyBackingState() {
      var backed = hasBacked(this.currentSlug);

      /* M21 — RECOVERY.
       *
       * A backing that was submitted but never confirmed used to hide this
       * whole block, which left the participant with "confirm your email", two
       * disabled actions and no way forward — the real case being that the
       * verification mail never arrived. Their only escape was a private
       * window and a second backing, which is not something a public campaign
       * may ask for.
       *
       * The block now stays for anyone holding a backing they cannot yet use:
       * submitted and unconfirmed, or confirmed on a device whose contributor
       * credential has since expired or been lost. It is hidden only once this
       * browser has BOTH a verified backing and a usable credential, when
       * there is genuinely nothing left to ask for.
       *
       * It reuses the same form and the same endpoint — the M20 backend
       * already sends a fresh link when the same address is submitted again,
       * so no resend route is invented here, and its cooldown, daily limits
       * and anti-enumeration all still apply. Re-requesting grants nothing:
       * write permission still arrives only with a contributorToken. */
      var settled = hasVerifiedBacking(this.currentSlug) && this.hasContributorCredential();
      var recovering = backed && !settled;

      /* M24 — the backing CTA now lives under the introduction, so when there
       * is nothing left to ask for the WHOLE section goes, not just the button
       * inside it. Leaving an empty section behind would put unexplained space
       * between the intro text and the office profile. */
      if (this.els.backingSection) this.els.backingSection.hidden = settled;
      if (this.els.backReady) this.els.backReady.hidden = settled;
      if (this.els.backRecoverHelp) this.els.backRecoverHelp.hidden = !recovering;

      /* "Already participating?" is offered only to a browser without personal
       * permissions for this office: once a contributor credential is held —
       * nominator or backer — there is nothing for it to restore. */
      if (this.els.accessOpen) this.els.accessOpen.hidden = this.hasContributorCredential();

      /* The control is the same button; only what it promises changes. */
      if (this.els.backToggle) {
        /* M24V — the LABEL span, never the button: the button also carries the
         * arrow, and writing over the button would delete it. */
        setText(
          this.els.backToggleLabel || this.els.backToggle,
          recovering
            ? this.strings.backRecoverCta || this.strings.backCta || ""
            : this.strings.backCta || ""
        );
      }

      /* M17B — ONE status, never a stack of them.
       *
       * Backing previously showed the "Back this office" heading, a success
       * message and a returning message all at once. The heading belongs to the
       * call to action, so it goes once there is nothing to call for; the
       * transient success message is the same statement as the persistent
       * returning one, so only the persistent one survives a reload.
       *
       * NOTE ON WORDING: this browser knows only that it SUBMITTED a backing.
       * The backing POST returns `backingCount` unchanged and deliberately says
       * nothing about verification — only the emailed confirmation moves the
       * count. So the status must not claim the backing is counted. */
      if (this.els.backingHeading) this.els.backingHeading.hidden = backed;
      if (backed && this.els.backSuccess) this.els.backSuccess.hidden = true;

      /* M21 — a verified NOMINATOR earns contribution permission without ever
       * backing their own office, so gating the contribution card on the local
       * backing marker alone hid it from exactly the people M20 authorizes.
       * Either route to verified participation opens the card; which route it
       * was is still reported separately by the first status row, so the
       * backer/nominator distinction is preserved rather than blurred.
       *
       * The backend remains the authority: this only decides whether the card
       * is offered, and every write is still checked by `mayContribute()`. */
      this.applyContributionAvailability(backed || this.hasContributorCredential());
    }

    /* MAKE THIS NOMINATION STRONGER is offered in BOTH post-backing states and stays
     * offered after a contribution has been made: an office laptop or a forwarded link
     * is legitimately used by several coworkers in turn, and the backend allows more
     * than one WallEntry per browser. Hiding it would strand a second coworker with no
     * way back.
     *
     * It is the ONLY action offered here. There is deliberately no generic
     * share-with-coworkers control: the nominator drives the initial distribution, and
     * milestone and finalist/voting sharing are separate work with their own moments. */
    applyContributionAvailability(available) {
      if (this.els.next) this.els.next.hidden = !available;

      /* Exactly one of the two states is ever present.
       *
       * M19C: the test is whether this browser has a WRITTEN contribution, not
       * merely whether it owns an entry. A photo-only participant owns an entry
       * but has written nothing, and "Edit your contribution" would be a false
       * label for them — there is no contribution to edit yet. They keep
       * "Make your nomination stronger", which is the invitation they still
       * need, and it opens the same editor on the same entry.
       *
       * Same textual-substance rule as the status row above, so the card and
       * the checklist can never disagree about whether something was written. */
      /* M29 — "Edit your story" once a story exists ANYWHERE this browser
       * owns, not only on its newest entry. */
      var hasStoryAnywhere = false;
      var ownedList = this.ownedVisible || [];
      for (var si = 0; si < ownedList.length; si++) {
        if (this.entryHasStory(ownedList[si])) { hasStoryAnywhere = true; break; }
      }
      var owns = !!available && hasStoryAnywhere;
      if (this.els.nextCreate) this.els.nextCreate.hidden = owns;
      if (this.els.nextOwned) this.els.nextOwned.hidden = !owns;

      /* The checklist, the photo card and the introduction all describe the
       * same state this flag governs, so they are refreshed from the one place
       * that owns it. */
      this.applyChecklist(!!available);
      this.applyPhotoCard(!!available);
      this.applyIntro();
      this.applyVerifiedClass();
    }

    showBackingSuccess(count) {
      this.renderBackingCount(count);

      if (this.els.backReady) this.els.backReady.hidden = true;
      /* The CTA heading has nothing left to introduce. */
      if (this.els.backingHeading) this.els.backingHeading.hidden = true;
      /* A first backing unlocks the Wall Contribution entry point immediately, with no
       * reload — the returning-backer path reaches the same place via
       * applyBackingState(). */
      this.applyContributionAvailability(true);

      var success = this.els.backSuccess;
      if (!success) return;

      success.hidden = false;
      try {
        success.focus({ preventScroll: false });
      } catch (e) {
        /* focus is a convenience only */
      }
    }

    /* ---------- Wall Contribution (Milestone 11a) ----------
     *
     * ONE form exists in the document. It is not cloned per entry and it is not put
     * inside the shared <modal-dialog>: that component closes on Escape and on any
     * backdrop click with no confirmation, which would silently destroy a long,
     * part-written contribution, and it stays single-purpose for View Entry so the
     * relocation fix and its focus handling are not put at risk.
     *
     * For an edit the whole shell MOVES into the owned entry's slot, so the trigger
     * and the panel it controls stay adjacent and aria-controls stays honest. Moving a
     * node blurs whatever was focused, so every path here moves first and focuses
     * afterwards, deliberately. */

    focusElement(el) {
      if (!el || typeof el.focus !== "function") return;
      try {
        el.focus({ preventScroll: true });
      } catch (e) {
        /* focus is a convenience only */
      }
    }

    /* Returns the single contribution shell to its create slot.
     *
     * Called before every list rebuild. When the shell was sitting inside an entry,
     * that entry node is about to be replaced, so the editor is closed as well — the
     * target it was editing no longer exists in the DOM. Create-mode content already
     * living in the create slot is never touched by this. */
    parkContribution() {
      var shell = this.els.contribShell;
      var slot = this.els.contribCreateSlot;
      if (!shell || !slot) return;
      if (shell.parentNode === slot) return;

      slot.appendChild(shell);

      if (this.contribMode === "edit") {
        if (this.els.contribPanel) this.els.contribPanel.hidden = true;
        this.contribOpen = false;
        this.contribReady = false;
        this.contribDirty = false;
        this.contribMode = "create";
        this.contribEntryId = null;
        this.resetContributionForm();
      }
    }

    findEntryRow(entryId) {
      var list = this.els.entries;
      if (!list || typeof entryId !== "string" || !entryId) return null;

      /* Compared in JS rather than built into an attribute selector, so a server
       * value never has to be escaped into a selector string. */
      var rows = list.querySelectorAll("[data-jcw-entry-id]");
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].getAttribute("data-jcw-entry-id") === entryId) return rows[i];
      }
      return null;
    }

    findEntryEditSlot(entryId) {
      var row = this.findEntryRow(entryId);
      return row ? row.querySelector("[data-jcw-entry-edit-slot]") : null;
    }

    /* Exactly one trigger may claim the panel at a time: the create CTA and every
     * owned entry's Edit control all reference the same panel id. */
    setTriggerExpanded(trigger) {
      if (this.els.stronger) this.els.stronger.setAttribute("aria-expanded", "false");
      if (this.els.topEdit) this.els.topEdit.setAttribute("aria-expanded", "false");

      if (trigger && typeof trigger.setAttribute === "function") {
        trigger.setAttribute("aria-expanded", "true");
      }
    }

    contributionSubmitLabel() {
      return this.contribMode === "edit"
        ? this.strings.contribSave || ""
        : this.strings.contribSubmit || "";
    }

    openContribution(mode, entry, trigger, options) {
      var panel = this.els.contribPanel;
      var shell = this.els.contribShell;
      if (!panel || !shell) return;

      var opts = options || {};
      var targetMode = mode === "edit" ? "edit" : "create";

      /* PHOTO-ONLY MODE.
       *
       * Available on CREATE as well since M17C: `validateContribution` accepts
       * `allowPhotoOnly`, which the proxy sets from `photoIntent`. Such an entry
       * is created PENDING — provisional — so an abandoned upload never leaves an
       * empty activity item on the Wall, and it publishes itself when its photo
       * finalizes. On an edit the existing entry already satisfies the substance
       * rule, so a photo can be replaced on its own exactly as before. */
      this.contribFocus = opts.focus === "photo" ? "photo" : "all";
      var entryId = entry && typeof entry.id === "string" ? entry.id.trim() : "";
      if (targetMode === "edit" && !entryId) return;

      /* Never silently discard unsaved work because another control was clicked.
       *
       * This deliberately does NOT test `contribOpen`. A form that was collapsed
       * rather than cancelled still holds everything that was typed, chosen and
       * selected, and the earlier guard let exactly that case through: collapse a
       * dirty form, click a different trigger, and the rebuild wiped it silently.
       * Dirtiness alone decides, and the form is re-revealed so the message and the
       * content it protects are both visible. A chosen photo counts as dirty like
       * any other field, because the file input lives inside the form. */
      var switching =
        this.contribMode !== targetMode ||
        (targetMode === "edit" && this.contribEntryId !== entryId);
      if (this.contribDirty && switching) {
        panel.hidden = false;
        this.contribOpen = true;
        this.setTriggerExpanded(this.visibleTrigger(this.contribOpener));
        this.showContributionError(this.strings.contribErrorOpenForm || "");
        this.focusElement(this.els.contribName);
        try {
          panel.scrollIntoView({ block: "nearest" });
        } catch (e) {
          /* Scrolling is a convenience only. */
        }
        return;
      }

      /* Re-opening the SAME target restores what is already in the form — only an
       * explicit Cancel or a saved submission clears it. */
      var rebuild = switching || !this.contribReady;

      if (rebuild) {
        if (targetMode === "edit") {
          var slot = this.findEntryEditSlot(entryId);
          if (!slot) {
            /* M27 — the row is gone, so there is nothing to edit INTO.
             *
             * This used to `return`, which is what made "Try your photo again"
             * do nothing at all: the entry it named had been removed, so no
             * slot existed and the press was swallowed in silence. A control
             * that is on screen must always do something, so this falls back
             * to creating a fresh contribution — which is what the visitor
             * wanted in the first place — rather than dead-ending. */
            targetMode = "create";
            entryId = "";
            /* parkContribution() runs just below, on the shared create path. */
          } else {
            /* Move BEFORE focusing. */
            if (shell.parentNode !== slot) slot.appendChild(shell);
          }
        }
        if (targetMode !== "edit") {
          this.parkContribution();
        }

        this.contribMode = targetMode;
        this.contribEntryId = targetMode === "edit" ? entryId : null;

        this.resetContributionForm();
        this.renderPromptFields();
        if (targetMode === "edit") {
          /* The entry's own displayName is authoritative for an edit and is never
           * replaced by the locally remembered one. */
          this.fillContributionForm(entry);
        } else {
          this.prefillContributorName();
        }

        /* A create has no server-side state to preserve, so there is nothing to
         * compare against and both fields are always sent. */
        if (targetMode !== "edit") this.contribSnapshot = null;

        this.contribReady = true;
        this.contribDirty = false;
      }

      /* Photo-only strips the story field and the guided prompts from the form
       * entirely (display:none), so neither is reachable by keyboard nor read
       * out while someone is only changing a picture. The textarea keeps its
       * prefilled value, so submitting sends the existing story back unchanged
       * rather than clearing it. */
      /* M24 — ONE PHOTO PATH, NOT TWO.
       *
       * The photo field used to live in the story form as well, so a photo
       * could be uploaded from the story editor AND from the dedicated photo
       * CTA. Two routes to one thing, with different validation and different
       * success copy, is what the LIVE review ran into.
       *
       * The story editor is now story-only: the field is hidden by
       * `--story-only` and the input is disabled, which takes it out of the tab
       * order and out of form submission. It is hidden rather than deleted
       * because the SAME panel is the photo editor a moment later, and because
       * `photoExistingUrl` must still be read on an edit so a story change to
       * an entry that already has a photo does not read as "no photo" and fail
       * validation — or worse, remove it. */
      var photoOnly = this.contribFocus === "photo";
      panel.classList.toggle("jcw__contrib-panel--photo-only", photoOnly);
      panel.classList.toggle("jcw__contrib-panel--story-only", !photoOnly);
      if (this.els.photoInput) this.els.photoInput.disabled = !photoOnly;

      /* Nothing chosen in the story editor can reach the photo pipeline. */
      if (!photoOnly) {
        this.photoFile = null;
        this.photoRemoveRequested = false;
      }

      /* The simplified form has no separate story box: the two questions are the
       * whole of it. The field is HIDDEN rather than removed, exactly as the
       * photo-only editor hides it, so a legacy entry's existing message is still
       * loaded into it, still compared against the snapshot, and therefore still
       * left untouched by an edit instead of being cleared. */
      panel.classList.toggle("jcw__contrib-panel--simplified", this.simplifiedPrompts === true);

      /* "Photo (optional)" is true in the full editor and wrong in this one,
       * where the photo IS the contribution and submitting without it is
       * refused before anything is sent. */
      if (this.els.photoLabel) {
        setText(
          this.els.photoLabel,
          this.contribFocus === "photo"
            ? this.copy.photoFieldLabelOnly || this.photoLabelDefault
            : this.photoLabelDefault
        );
      }

      var title =
        this.contribFocus === "photo"
          ? this.copy.photoSheetTitle || ""
          : this.contribMode === "edit"
            ? this.strings.contribEditTitle || ""
            : this.strings.contribCreateTitle || "";

      setText(this.els.contribTitle, title);
      setText(this.els.contribSubmitLabel, this.contributionSubmitLabel());

      if (this.els.contribSuccess) this.els.contribSuccess.hidden = true;
      panel.hidden = false;
      this.contribOpen = true;
      this.contribTrigger = trigger || null;
      this.contribOpener = trigger || this.contribOpener;
      this.setTriggerExpanded(trigger);

      /* The sheet hosts the form whenever the viewport is narrow, and also for a
       * photo action at any width — a picture swap is a self-contained task that
       * does not belong buried in the page. */
      var useSheet = this.shouldUseSheet();
      if (useSheet) {
        if (this.openSheet(title, trigger)) {
          /* Focus moves INTO the sheet so the trap and the screen reader have a
           * starting point — but never onto a text field, which would raise the
           * keyboard before the visitor has asked for it and bury the form. The
           * file input is safe: tapping it opens a picker, not a keyboard. */
          this.focusElement(
            this.contribFocus === "photo" ? this.els.photoInput : this.els.sheetPanel
          );
          return;
        }
      }

      /* The prominent trigger sits above the Wall while the panel moves DOWN into the
       * owned entry, so the panel is brought into view explicitly — otherwise a mouse
       * user clicking the top control would see nothing happen. `block: "nearest"`
       * scrolls the minimum needed, so opening from the row's own control (already in
       * view) stays still. No `behavior: "smooth"`, so no new motion is introduced and
       * prefers-reduced-motion needs nothing. */
      try {
        panel.scrollIntoView({ block: "nearest" });
      } catch (e) {
        /* Scrolling is a convenience only. */
      }

      this.focusElement(this.els.contribName);
    }

    /* Closing from the sheet chrome. Unsaved work is never discarded silently:
     * a dirty form asks first, exactly as the inline form has always done. */
    requestSheetClose() {
      if (this.contribDirty) {
        var message = this.copy.discardPrompt || "";
        var confirmed = true;
        try {
          confirmed = window.confirm(message);
        } catch (e) {
          /* A blocked confirm must not strand the visitor in the sheet. */
          confirmed = true;
        }
        if (!confirmed) return;
      }
      this.cancelContribution();
    }

    /* M19D — ALWAYS the sheet.
     *
     * It used to be "narrow viewports, plus a photo action at any width",
     * which is exactly the inconsistency reported from DEV: on desktop
     * "Add your photo" opened a dialog while "Make your nomination stronger"
     * expanded the form inline inside the status card, making that column very
     * tall. The sheet is the same component in both cases; desktop simply
     * presents it as a centred panel rather than fullscreen (see the CSS).
     *
     * This is NOT the theme's <modal-dialog>. That component closes on Escape
     * and on any backdrop click with no confirmation, which would silently
     * destroy a part-written contribution — the reason M11a kept this form out
     * of it, which has not changed. The sheet supplies its own focus handling,
     * scroll lock and iOS visualViewport sizing. */
    shouldUseSheet() {
      return true;
    }

    /* `reset` is true only for an explicit cancel or a saved submission. Nothing else
     * may clear what the contributor typed. */
    closeContribution(reset) {
      var selfOrder = this;
      setTimeout(function () { selfOrder.flushDashboardOrder(); }, 0);
      if (this.els.contribPanel) this.els.contribPanel.hidden = true;
      this.setTriggerExpanded(null);
      this.contribOpen = false;
      this.contribTrigger = null;
      /* Leaving the form always leaves the sheet, so the page can never be left
       * behind a backdrop with nothing in front of it. */
      this.closeSheet();

      if (reset) {
        this.contribMode = "create";
        this.contribEntryId = null;
        this.contribReady = false;
        this.contribDirty = false;
        this.contribOpener = null;
        this.resetContributionForm();
        this.parkContribution();
      }

      /* A collapse (reset === false) deliberately does NOT park the shell. Parking an
       * edit discards the form, which parkContribution() does on purpose for a list
       * rebuild — the row it was sitting in is about to be replaced. A collapse is not
       * that: the row is still there, so the hidden form stays inside it and reopening
       * the same entry restores exactly what was typed, matching how collapsing the
       * create form already behaves. A real re-render still parks and clears it, via
       * renderEntries(). */
    }

    cancelContribution() {
      /* A request is already in flight; cancelling would leave the outcome unknown. */
      if (this.contributionBusy) return;

      /* Captured before closeContribution() clears it. */
      var opener = this.contribTrigger;

      this.closeContribution(true);

      /* Focus returns to whichever control actually opened the form.
       *
       * M19C: that is now always one of the share card's CTAs — the per-row
       * edit control is gone, so there is no row-level trigger to fall back to
       * and no entry id is needed here any more. */
      var target = this.visibleTrigger(opener);
      if (!target) target = this.visibleTrigger(this.els.topEdit) || this.els.stronger;

      this.focusElement(target);
    }

    /* An element only counts as a focus target while it is still in this component and
     * actually rendered. `offsetParent` is null whenever the element or any ancestor is
     * display:none, which is how [hidden] takes effect — so this catches a control
     * hidden by its STATE WRAPPER, not just one carrying `hidden` itself. */
    visibleTrigger(el) {
      if (!el || !this.contains(el)) return null;
      return el.offsetParent === null ? null : el;
    }

    resetContributionForm() {
      this.resetPhotoState();
      if (this.els.contribName) this.els.contribName.value = "";
      if (this.els.contribMessage) this.els.contribMessage.value = "";
      if (this.els.contribPrompts) this.els.contribPrompts.textContent = "";
      this.showContributionError("");
      this.showEditUnavailable(false);
      this.setContributionBusy(false);
    }

    /* Ids are composed from the section identity plus sanitised keys, so two instances
     * of this section cannot collide and a backend key can never produce an invalid
     * id. The index guarantees uniqueness even if two keys reduce to the same string. */
    fieldId(kind, promptKey, choiceKey, index) {
      return (
        (this.contribIdBase || "Jcw") +
        kind +
        "-" +
        safeIdPart(promptKey) +
        "-" +
        safeIdPart(choiceKey) +
        "-" +
        String(index)
      );
    }

    /* Builds the guided prompts from the backend DTO.
     *
     * Every value is written with setText/setAttribute into markup cloned from the
     * Liquid <template> elements. No innerHTML, so no backend string is ever parsed as
     * HTML, and no sanitizer dependency is needed. The RAW choice key travels in the
     * checkbox `value`, where no escaping is required. */
    renderPromptFields() {
      var host = this.els.contribPrompts;
      if (!host) return;

      host.textContent = "";
      host.classList.toggle("jcw__prompt-fields--simplified", this.simplifiedPrompts === true);

      /* The simplified form is a different shape, not a restyled guided card:
       * one label, one question, one textarea, no chips and no fieldset. It is
       * built from its own template so the guided rendering below stays exactly
       * as it is for a backend that still publishes V1 prompts. */
      if (this.simplifiedPrompts) {
        this.renderSimplePromptFields(host);
        return;
      }

      var promptTemplate = document.getElementById("JcwPromptTemplate");
      var choiceTemplate = document.getElementById("JcwChoiceTemplate");
      if (!promptTemplate || !promptTemplate.content || !promptTemplate.content.firstElementChild) return;
      if (!choiceTemplate || !choiceTemplate.content || !choiceTemplate.content.firstElementChild) return;

      var prompts = this.prompts || [];
      for (var i = 0; i < prompts.length; i++) {
        var prompt = prompts[i];
        var node = promptTemplate.content.firstElementChild.cloneNode(true);
        node.setAttribute("data-jcw-prompt-key", prompt.key);

        setText(node.querySelector("[data-jcw-prompt-question]"), prompt.question);

        var list = node.querySelector("[data-jcw-choice-list]");
        if (list) {
          for (var c = 0; c < prompt.choices.length; c++) {
            var choice = prompt.choices[c];
            var item = choiceTemplate.content.firstElementChild.cloneNode(true);
            var input = item.querySelector("[data-jcw-choice]");
            var label = item.querySelector("[data-jcw-choice-label]");
            var id = this.fieldId("Choice", prompt.key, choice.key, c);

            if (input) {
              input.id = id;
              input.value = choice.key;
            }
            if (label) {
              label.setAttribute("for", id);
              setText(label, choice.label);
            }
            list.appendChild(item);
          }
        }

        var textId = this.fieldId("Text", prompt.key, "", i);
        var textLabel = node.querySelector("[data-jcw-prompt-text-label]");
        var textarea = node.querySelector("[data-jcw-prompt-text]");

        if (textLabel) {
          textLabel.setAttribute("for", textId);
          setText(textLabel, this.strings.contribPromptTextLabel || "");
        }
        if (textarea) {
          textarea.id = textId;
          /* The backend owns the limit as well as the copy. */
          if (prompt.maxLength) textarea.setAttribute("maxlength", String(prompt.maxLength));
        }

        host.appendChild(node);
      }
    }

    /* Builds the two simplified questions.
     *
     * Same markup and same classes as the legacy story field, so the two
     * sections are typographically identical to the field they replace without
     * a parallel set of styles to maintain.
     *
     * Copy authority per prompt: the campaign heading/question/placeholder maps
     * where a key has one, the DTO's own question as the fallback. The backend
     * still owns the prompt KEY, the character limit, and which prompts exist
     * at all — none of which is hardcoded here. A prompt the campaign has no
     * copy for still renders, using the DTO question as its heading, so an
     * unexpected active prompt is never silently unanswerable. */
    renderSimplePromptFields(host) {
      var template = document.getElementById("JcwPromptSimpleTemplate");
      if (!template || !template.content || !template.content.firstElementChild) return;

      var headings = this.promptHeadings || {};
      var placeholders = this.promptPlaceholders || {};
      var prompts = this.prompts || [];

      for (var i = 0; i < prompts.length; i++) {
        var prompt = prompts[i];
        var node = template.content.firstElementChild.cloneNode(true);
        node.setAttribute("data-jcw-prompt-key", prompt.key);

        var textId = this.fieldId("Text", prompt.key, "", i);
        var askId = textId + "-ask";

        var question = this.displayQuestion(prompt.key, prompt.question);
        var heading =
          Object.prototype.hasOwnProperty.call(headings, prompt.key) && headings[prompt.key]
            ? headings[prompt.key]
            : question;

        var label = node.querySelector("[data-jcw-prompt-heading]");
        if (label) {
          label.setAttribute("for", textId);
          setText(label, heading);
        }

        /* The question is the field's description, exactly as the story field
         * describes itself. Removed outright when there is nothing to say, so
         * `aria-describedby` can never point at an empty node. */
        var ask = node.querySelector("[data-jcw-prompt-ask]");
        var described = false;
        if (ask) {
          if (question && question !== heading) {
            ask.id = askId;
            setText(ask, question);
            described = true;
          } else {
            ask.remove();
          }
        }

        var textarea = node.querySelector("[data-jcw-prompt-text]");
        if (textarea) {
          textarea.id = textId;
          if (described) textarea.setAttribute("aria-describedby", askId);
          /* The backend owns the limit. */
          if (prompt.maxLength) textarea.setAttribute("maxlength", String(prompt.maxLength));
          /* A placeholder is inspiration, never the label — the label is above. */
          if (Object.prototype.hasOwnProperty.call(placeholders, prompt.key) && placeholders[prompt.key]) {
            textarea.setAttribute("placeholder", placeholders[prompt.key]);
          }
        }

        host.appendChild(node);
      }
    }

    /* The first answer box in the simplified form, used as a focus target when
     * the hidden story field would be the wrong place to send someone. */
    firstPromptTextarea() {
      var host = this.els.contribPrompts;
      return host ? host.querySelector("[data-jcw-prompt-text]") : null;
    }

    findPromptField(key) {
      var host = this.els.contribPrompts;
      if (!host || typeof key !== "string" || !key) return null;

      var fields = host.querySelectorAll("[data-jcw-prompt-key]");
      for (var i = 0; i < fields.length; i++) {
        if (fields[i].getAttribute("data-jcw-prompt-key") === key) return fields[i];
      }
      return null;
    }

    /* CREATE mode only. Fills the Name field with the display name this browser last
     * submitted for this Office, purely to save retyping.
     *
     * Only ever writes into an EMPTY field, so a name the contributor has already
     * typed is never overwritten — reopening a form that was collapsed rather than
     * cancelled does not rebuild it and therefore does not reach this at all.
     *
     * The field stays fully editable, and nothing downstream reads the stored value:
     * what is submitted is whatever the input holds at submit time. On a shared
     * browser this may show the previous coworker's name, which is accepted — it is a
     * convenience with no identity or authorization meaning, and the email is never
     * stored. */
    prefillContributorName() {
      var field = this.els.contribName;
      if (!field || field.value) return;

      var name = readContributorName(this.currentSlug);
      if (name) field.value = name;
    }

    /* Prefills from the Wall DTO only. The edit credential is NOT read here, is NOT
     * needed to display anything, and is NOT held on the instance. */
    fillContributionForm(entry) {
      if (!entry || typeof entry !== "object") return;

      /* `.value`, never textContent: on a <textarea> textContent sets the DEFAULT
       * value, which a later reset would silently restore. */
      if (this.els.contribName) {
        this.els.contribName.value = firstString(entry, ["displayName", "display_name", "name"]);
      }
      if (this.els.contribMessage) {
        this.els.contribMessage.value = firstString(entry, ["message", "text", "body"]);
      }

      /* Show the photo already on this entry, so a replacement never looks like it
       * has already happened and a removal reads as a pending intent. */
      this.showExistingPhoto(entry);

      var answers = Array.isArray(entry.answers) ? entry.answers : [];
      for (var i = 0; i < answers.length; i++) {
        var answer = answers[i];
        if (!answer || typeof answer !== "object") continue;

        var field = this.findPromptField(typeof answer.promptKey === "string" ? answer.promptKey : "");
        if (!field) continue;

        var selected = Array.isArray(answer.selectedChoiceKeys) ? answer.selectedChoiceKeys : [];
        var inputs = field.querySelectorAll("[data-jcw-choice]");
        for (var c = 0; c < inputs.length; c++) {
          inputs[c].checked = selected.indexOf(inputs[c].value) !== -1;
        }

        var textarea = field.querySelector("[data-jcw-prompt-text]");
        if (textarea) textarea.value = firstString(answer, ["answerText"]);
      }

      /* Record what the server holds, so submitContribution() can tell an
       * untouched field from an edited one, and tell WHICH answers changed.
       * See the note there: on a backend without `answerPatch`, sending
       * `answers` at all DELETES every answer and chip on the entry. */
      var filled = this.collectAnswers();
      this.contribSnapshot = {
        message: this.els.contribMessage ? this.els.contribMessage.value.trim() : "",
        answers: JSON.stringify(filled),
        answerStates: this.answerStateMap(filled)
      };
    }

    /* promptKey -> a stable serialisation of that prompt's answer.
     *
     * Built from the form in both directions, so "unchanged" means the same
     * thing on the way in and on the way out. Chip keys are sorted because
     * their ORDER is not meaningful to the backend and reordering a selection
     * is not an edit. A prompt with nothing in it is absent from the map,
     * which is how collectAnswers already represents "empty". */
    answerStateMap(answers) {
      var map = {};
      var list = Array.isArray(answers) ? answers : [];

      for (var i = 0; i < list.length; i++) {
        var answer = list[i];
        if (!answer || typeof answer.promptKey !== "string" || !answer.promptKey) continue;

        var choices = Array.isArray(answer.selectedChoiceKeys)
          ? answer.selectedChoiceKeys.slice().sort()
          : [];
        map[answer.promptKey] = JSON.stringify({
          text: answer.text || null,
          choices: choices
        });
      }

      return map;
    }

    /* The minimal `answerPatch` for an edit: only the prompts whose answer
     * actually changed.
     *
     * Three cases, and nothing else is ever named:
     *
     *   changed   the prompt is answered now and differently than it was
     *   added     the prompt is answered now and was not before
     *   cleared   the prompt was answered before and is now empty, sent as
     *             an explicit null/[] because that is what tells the backend
     *             to delete it (an omitted prompt means "leave alone")
     *
     * A prompt the contributor did not touch is never named, so it is never
     * deleted and never rewritten. Retired prompts cannot appear here at all:
     * both sides of the comparison are read from the rendered form, which only
     * ever contains prompts the backend currently publishes — which is exactly
     * why an answer to a retired prompt now survives an edit untouched.
     *
     * Returns [] when nothing changed. The caller must then omit the field
     * entirely: an empty array is rejected by `validateAnswerPatch`. */
    buildAnswerPatch(answers, snapshot) {
      var before = snapshot && snapshot.answerStates ? snapshot.answerStates : {};
      var after = this.answerStateMap(answers);
      var list = Array.isArray(answers) ? answers : [];
      var patch = [];

      for (var i = 0; i < list.length; i++) {
        var answer = list[i];
        if (!answer || typeof answer.promptKey !== "string" || !answer.promptKey) continue;

        var key = answer.promptKey;
        var was = Object.prototype.hasOwnProperty.call(before, key) ? before[key] : null;
        if (was === after[key]) continue;

        patch.push({
          promptKey: key,
          selectedChoiceKeys: Array.isArray(answer.selectedChoiceKeys)
            ? answer.selectedChoiceKeys
            : [],
          text: answer.text || null
        });
      }

      for (var prior in before) {
        if (!Object.prototype.hasOwnProperty.call(before, prior)) continue;
        if (Object.prototype.hasOwnProperty.call(after, prior)) continue;
        /* Answered before, empty now — an intentional clearing. */
        patch.push({ promptKey: prior, selectedChoiceKeys: [], text: null });
      }

      return patch;
    }

    /* The normalised current state of every prompt field.
     *
     * A prompt with no selected chip and no text is OMITTED. Under the backend's
     * replacement semantics that is exactly how an edit clears a prompt the
     * contributor emptied, so create and edit share one normalisation and an edit
     * always sends the complete current answer set. */
    collectAnswers() {
      var answers = [];
      var host = this.els.contribPrompts;
      if (!host) return answers;

      var fields = host.querySelectorAll("[data-jcw-prompt-key]");
      for (var i = 0; i < fields.length; i++) {
        var field = fields[i];
        var key = field.getAttribute("data-jcw-prompt-key");
        if (!key) continue;

        var selected = [];
        var inputs = field.querySelectorAll("[data-jcw-choice]");
        for (var c = 0; c < inputs.length; c++) {
          if (inputs[c].checked) selected.push(inputs[c].value);
        }

        var textarea = field.querySelector("[data-jcw-prompt-text]");
        var text = textarea ? textarea.value.trim() : "";

        if (!selected.length && !text) continue;

        answers.push({ promptKey: key, selectedChoiceKeys: selected, text: text || null });
      }

      return answers;
    }

    /* ---------- Photo (Milestone 11b) ---------- */

    setPhotoStatus(stringKey) {
      if (!this.els.photoStatus) return;
      setText(this.els.photoStatus, stringKey ? this.strings[stringKey] || "" : "");
    }

    showPhotoError(stringKey) {
      this.showPhotoMessage(
        stringKey ? this.strings[stringKey] || this.strings.photoErrGeneric || "" : ""
      );
    }

    /* One place where photo failure text reaches the page. Takes resolved copy,
     * never a backend string and never anything carrying a URL or a token. */
    showPhotoMessage(message) {
      var el = this.els.photoError;
      if (!el) return;
      setText(el, message || "");
      el.hidden = !message;
    }

    releasePhotoPreview() {
      if (!this.photoPreviewUrl) return;
      try { URL.revokeObjectURL(this.photoPreviewUrl); } catch (e) { /* non-fatal */ }
      this.photoPreviewUrl = null;
    }

    /* Clears everything photo-related. Called only from the form reset, i.e. after an
     * explicit cancel or a saved submission — never as a side effect of anything else,
     * so a chosen photo is as safe from silent loss as the typed text. */
    resetPhotoState() {
      this.photoFile = null;
      this.photoRemoveRequested = false;
      this.photoExistingUrl = null;
      this.releasePhotoPreview();

      if (this.els.photoInput) {
        try { this.els.photoInput.value = ""; } catch (e) { /* non-fatal */ }
      }
      if (this.els.photoChosen) this.els.photoChosen.hidden = true;
      if (this.els.photoPreview) this.els.photoPreview.removeAttribute("src");
      if (this.els.photoCurrent) this.els.photoCurrent.hidden = true;
      if (this.els.photoCurrentImg) this.els.photoCurrentImg.removeAttribute("src");
      this.setPhotoStatus("");
      this.showPhotoError("");
    }

    /* Shows the photo already on the entry being edited, so a replacement never looks
     * like it has already happened and a removal is clearly a pending intent. */
    showExistingPhoto(entry) {
      var url = entry && entry.photo && typeof entry.photo.url === "string" ? entry.photo.url : "";
      this.photoExistingUrl = url || null;
      if (!this.els.photoCurrent) return;

      if (!url) {
        this.els.photoCurrent.hidden = true;
        return;
      }
      if (this.els.photoCurrentImg) {
        this.els.photoCurrentImg.src = safeImageUrl(url);
        this.els.photoCurrentImg.alt = "";
      }
      setText(this.els.photoCurrentNote, this.strings.photoCurrentNote || "");
      this.els.photoCurrent.hidden = false;
      if (this.els.photoRemoveBtn) this.els.photoRemoveBtn.setAttribute("aria-pressed", "false");
    }

    togglePhotoRemoval() {
      if (this.photoBusy) return;
      this.photoRemoveRequested = !this.photoRemoveRequested;
      /* Choosing removal and choosing a replacement are mutually exclusive. */
      if (this.photoRemoveRequested) this.clearChosenPhoto();

      setText(
        this.els.photoCurrentNote,
        this.photoRemoveRequested
          ? this.strings.photoPendingRemoval || ""
          : this.strings.photoCurrentNote || ""
      );
      if (this.els.photoRemoveBtn) {
        this.els.photoRemoveBtn.setAttribute("aria-pressed", this.photoRemoveRequested ? "true" : "false");
      }
      /* A pending removal is unsaved work and must be protected like typed text. */
      this.contribDirty = true;
    }

    clearChosenPhoto() {
      if (this.photoBusy) return;
      this.photoFile = null;
      this.releasePhotoPreview();
      if (this.els.photoInput) {
        try { this.els.photoInput.value = ""; } catch (e) { /* non-fatal */ }
      }
      if (this.els.photoChosen) this.els.photoChosen.hidden = true;
      if (this.els.photoPreview) this.els.photoPreview.removeAttribute("src");
      this.setPhotoStatus("");
      this.showPhotoError("");
    }

    /* Validates and, when needed, converts the selection. Nothing is uploaded here —
     * the bytes are only prepared, so the contributor learns about an unsupported or
     * oversized file immediately rather than after submitting the whole form. */
    async onPhotoSelected() {
      var input = this.els.photoInput;
      var file = input && input.files && input.files[0];
      if (!file) return;

      this.showPhotoError("");
      /* Selecting a replacement cancels a pending removal. */
      this.photoRemoveRequested = false;
      if (this.els.photoCurrentNote && this.photoExistingUrl) {
        setText(this.els.photoCurrentNote, this.strings.photoCurrentNote || "");
      }
      this.setPhotoStatus("photoStatePreparing");

      var prepared;
      try {
        prepared = await preparePhotoFile(file, this.heicDecoderUrl, this.setPhotoStatus.bind(this));
      } catch (e) {
        this.setPhotoStatus("");
        this.showPhotoError(e instanceof PhotoError ? e.stringKey : "photoErrGeneric");
        this.photoFile = null;
        if (this.els.photoChosen) this.els.photoChosen.hidden = true;
        return;
      }

      this.setPhotoStatus("");
      this.showChosenPhoto(prepared, file.name || "");
      /* The file input is inside the form, so its change event already marked the
       * form dirty; this keeps that true even if the event were ever stopped. */
      this.contribDirty = true;
    }

    /* Pushes the prepared bytes straight to private Storage.
     *
     * This is the ONLY request carrying image data, and it deliberately does not go
     * through the Shopify App Proxy. The URL, the token and the descriptor all come
     * from the backend's begin response — no Storage path is ever constructed here,
     * no bucket name is known, and no Storage credential exists in the browser.
     *
     * Shape mirrors Supabase's own signed-upload client: PUT, the token as a query
     * parameter, and a Blob sent as multipart with the file under the empty key.
     * `credentials: "omit"` keeps Shopify cookies off a cross-origin request. */
    async uploadPhotoBytes(capability, file) {
      var url = capability.uploadUrl;
      if (typeof url !== "string" || !url) return { ok: false, key: "photoErrGeneric" };

      /* The destination must be encrypted AND approved by the theme.
       *
       * These bytes are a participant's photograph and the URL is whatever the
       * upload endpoint returned. Trusting that response alone meant any https
       * host could receive the photo. The destination is now parsed, required to
       * be https, and required to be an approved Storage host — a decision made
       * here, never derived from the response being validated.
       *
       * An unparseable URL, a non-https scheme, and a relative URL (which would
       * resolve to the storefront rather than Storage) are all refused. The
       * failure is the ordinary photo error, so a wrong destination reads to the
       * participant exactly like any other upload failure and reveals nothing. */
      var parsed;
      try {
        parsed = new URL(url, window.location.href);
      } catch (e) {
        return { ok: false, key: "photoErrGeneric" };
      }
      if (!isApprovedUploadTarget(parsed)) {
        return { ok: false, key: "photoErrGeneric" };
      }

      /* The backend already hands back a tokenised URL; the separate token is only
       * applied if it is genuinely absent, so nothing is invented either way. */
      if (!parsed.searchParams.get("token") && capability.uploadToken) {
        parsed.searchParams.set("token", capability.uploadToken);
      }
      url = parsed.toString();

      var body = new FormData();
      body.append("cacheControl", "3600");
      body.append("", file);

      var response;
      try {
        response = await fetch(url, {
          method: "PUT",
          /* Content-Type is deliberately unset so the browser writes the multipart
           * boundary itself. */
          headers: { "x-upsert": "false" },
          body: body,
          credentials: "omit",
          /* A 3xx would move the photo bytes to a host the approval check above
           * never saw. Refuse rather than follow. */
          redirect: "error"
        });
      } catch (e) {
        /* A thrown PUT is a network failure OR a blocked cross-origin request —
         * the browser reports both identically and `e` is never inspected,
         * because a fetch rejection can echo the request and this one carries a
         * signed URL. Reported as the transport stage so the next person knows
         * where to look without any of it reaching the page. */
        return { ok: false, key: "photoErrNetwork", stage: "upload" };
      }

      if (!response.ok) {
        /* 400 and 403 both mean the capability is no longer good — expired, or
         * refused by Storage. Anything else is treated as transport. */
        var expired = response.status === 400 || response.status === 403;
        return {
          ok: false,
          key: expired ? "photoErrExpired" : "photoErrNetwork",
          stage: "upload"
        };
      }
      return { ok: true };
    }

    /* begin -> PUT -> finalize, or DELETE. Resolves a locale key on failure.
     *
     * Runs only AFTER the entry itself is saved, so a photo problem can never cost
     * the contributor their text, their chips or their prompt answers. */
    async runPhotoOperations(slug, entryId) {
      var token = resolveWriteToken(slug);
      var editToken = readEntryToken(slug, entryId);
      if (!token || !editToken) return { ok: false, key: "photoErrGeneric" };

      /* M27 — THE ONLY PLACE AN UPLOAD ATTEMPT IS RECORDED.
       *
       * The retry state used to be inferred from storage: an edit credential
       * whose entry was not on the Wall was taken to mean "an upload that did
       * not finish". That inference is unsound, as the note on dropEntryToken
       * already says of this very signal — absence from the Wall proves
       * nothing. It is equally true of an entry deleted server-side, of a
       * credential left behind when an office slug was reused, and of an older
       * entry that simply fell off the 50-item feed.
       *
       * Attempting an upload is something this browser DOES, so it is recorded
       * when it happens rather than guessed afterwards. */
      this.photoAttempted = true;

      /* M21 — the three photo endpoints each run `mayContribute()`, so the
       * verified credential travels ALONGSIDE the existing two. It never
       * replaces them: the participant token still says which office, the edit
       * token still proves authorship, and this says the holder finished email
       * verification. Omitted rather than sent empty when absent, so the
       * backend's own 403 is what reports the problem. */
      var contributorToken = readContributorToken(slug);
      var credentials = { participantToken: token, editToken: editToken };
      if (contributorToken) credentials.contributorToken = contributorToken;

      if (this.photoRemoveRequested && !this.photoFile) {
        this.setPhotoStatus("photoStateRemoving");
        var removed = await this.photoControlRequest(
          this.photoProxyUrl(slug, entryId, "/photo"),
          "DELETE",
          credentials
        );
        if (!removed.ok) return this.taggedPhotoFailure(removed, "remove");
        this.setPhotoStatus("photoStateRemoved");
        return { ok: true };
      }

      if (!this.photoFile) return { ok: true };

      this.setPhotoStatus("photoStatePreparing");
      var begun = await this.photoControlRequest(
        this.photoProxyUrl(slug, entryId, "/photo-upload"),
        "POST",
        credentials
      );
      if (!begun.ok) return this.taggedPhotoFailure(begun, "begin");

      var capability = begun.payload;
      /* The backend states its own ceiling in the capability; honour it rather than
       * only the local constant, so a backend change cannot be silently ignored. */
      var maxBytes = Number(capability.maxBytes);
      if (isFinite(maxBytes) && maxBytes > 0 && this.photoFile.size > maxBytes) {
        return { ok: false, key: "photoErrTooLarge" };
      }

      this.setPhotoStatus("photoStateUploading");
      var uploaded = await this.uploadPhotoBytes(capability, this.photoFile);
      if (!uploaded.ok) return uploaded;

      this.setPhotoStatus("photoStateProcessing");
      var finalized = await this.photoControlRequest(
        this.photoProxyUrl(slug, entryId, "/photo-finalize"),
        "POST",
        contributorToken
          ? {
              participantToken: token,
              editToken: editToken,
              contributorToken: contributorToken,
              uploadDescriptor: capability.uploadDescriptor
            }
          : { participantToken: token, editToken: editToken, uploadDescriptor: capability.uploadDescriptor }
      );
      if (!finalized.ok) return this.taggedPhotoFailure(finalized, "finalize");

      this.setPhotoStatus("photoStateAdded");
      return { ok: true };
    }

    /* Shows a prepared file as the pending choice. Separate from selection so the
     * same state can be restored after a rebuild — a failed upload must not cost the
     * contributor the photo they already chose and waited for. */
    showChosenPhoto(file, displayName) {
      this.photoFile = file;
      this.releasePhotoPreview();
      try {
        this.photoPreviewUrl = URL.createObjectURL(file);
        if (this.els.photoPreview) {
          this.els.photoPreview.src = this.photoPreviewUrl;
          this.els.photoPreview.alt = "";
        }
      } catch (e) {
        /* A preview is a convenience; the upload does not depend on it. */
      }
      setText(this.els.photoName, displayName || file.name || "");
      if (this.els.photoChosen) this.els.photoChosen.hidden = false;
    }

    photoProxyUrl(slug, entryId, suffix) {
      return (
        PROXY_BASE +
        "/offices/" +
        encodeURIComponent(slug) +
        "/entries/" +
        encodeURIComponent(entryId) +
        suffix
      );
    }

    /* ISSUE 3 (M17E) — say WHICH step failed.
     *
     * Every stage previously collapsed into one "Something went wrong", which
     * told neither the contributor what to do nor anyone else where to look.
     * The specific messages (too large, expired, unsupported) are kept exactly
     * as they are — they are already actionable — and the stage wording is used
     * only where the alternative would have been the generic line.
     *
     * No signed URL, token, entry id or backend string ever reaches this text. */
    taggedPhotoFailure(result, stage) {
      result.stage = result.stage || stage;
      return result;
    }

    photoFailureMessage(result) {
      if (!result || result.ok) return "";
      var key = result.key || "photoErrGeneric";
      var specific = key !== "photoErrGeneric" && key !== "photoErrNetwork"
        ? this.strings[key]
        : "";
      if (specific) return specific;

      var byStage = {
        begin: this.copy.photoErrBegin,
        upload: this.copy.photoErrUpload,
        finalize: this.copy.photoErrFinalize,
        remove: this.copy.photoErrRemove
      };
      return (
        byStage[result.stage] ||
        this.strings[key] ||
        this.strings.photoErrGeneric ||
        ""
      );
    }

    /* Maps the backend's documented failure reasons onto copy a contributor can act
     * on. Anything unrecognised falls back to the generic message rather than
     * surfacing a raw backend string. */
    photoErrorKeyFor(reason) {
      switch (reason) {
        case "file_too_large": return "photoErrTooLarge";
        case "image_too_large": return "photoErrDimensions";
        case "unsupported_format":
        case "animated_not_supported": return "photoErrUnsupported";
        case "invalid_image": return "photoErrConvert";
        case "upload_expired":
        case "upload_missing": return "photoErrExpired";
        default: return "photoErrGeneric";
      }
    }

    /* The control-plane calls. Small JSON only — never image bytes — through the
     * authenticated App Proxy. */
    async photoControlRequest(url, method, body) {
      var response;
      try {
        response = await fetch(url, {
          method: method,
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(body),
          credentials: "same-origin"
        });
      } catch (e) {
        /* The thrown value is never inspected or logged: it can echo the request,
         * which carries both credentials. */
        return { ok: false, key: "photoErrNetwork" };
      }

      /* M21 — the body is read BEFORE the status is collapsed into an error.
       * A 403 is no longer always "something went wrong": the M20 backend uses
       * it for `verification_required`, which has a specific, truthful message
       * and a recovery route, and reporting that as a generic upload failure
       * would send the visitor to retry something that cannot succeed. */
      var payload = null;
      try {
        payload = await response.json();
      } catch (e) {
        payload = null;
      }

      var reason = payload && typeof payload.error === "string" ? payload.error : "";

      if (response.status === 403 && reason === "verification_required") {
        return { ok: false, key: "errVerificationRequired", verificationRequired: true };
      }

      if (response.status === 401 || response.status === 403 || response.status === 404) {
        return { ok: false, key: "photoErrGeneric", notFound: true };
      }

      if (!response.ok || !payload || payload.ok !== true) {
        return { ok: false, key: this.photoErrorKeyFor(reason) };
      }
      return { ok: true, payload: payload };
    }

    showContributionError(message) {
      var el = this.els.contribError;
      if (!el) return;
      setText(el, message || "");
      el.hidden = !message;
    }

    /* "Your previous story can't be edited" — shown ONLY after a story EDIT
     * answered 404 (see submitContribution). The copy is static Liquid markup.
     * Returns false when the section has no such block, so the caller can fall
     * back to the ordinary error line. */
    showEditUnavailable(show) {
      var box = this.els.contribRetry;
      if (!box) return false;
      box.hidden = !show;
      return true;
    }

    /* The "Submit a new story" CTA. Exactly what Cancel does — the form is
     * closed and reset, which clears the edit mode, the old entry id and the
     * dirty flag — and then a fresh, empty CREATE form is opened. Nothing is
     * submitted. The old entry and its edit credential stay in storage
     * untouched, and the grant and contributor credential are never read or
     * written here: the new story goes through the normal create request. */
    startNewContribution() {
      if (this.contributionBusy) return;
      var opener = this.contribTrigger || this.contribOpener;
      this.closeContribution(true);
      this.openContribution("create", null, this.visibleTrigger(opener) || this.els.stronger);
    }

    setContributionBusy(busy) {
      this.contributionBusy = !!busy;

      var submit = this.els.contribSubmit;
      var cancel = this.els.contribCancel;

      /* aria-disabled, never the `disabled` property: disabling the element that
       * currently holds focus drops focus to <body> and takes the error message's
       * context with it. The busy flag is what actually blocks a second submission,
       * so a double-click cannot produce two requests. Kept SEPARATE from the backing
       * lock so neither flow can block or unlock the other. */
      if (submit) {
        submit.setAttribute("aria-disabled", busy ? "true" : "false");
        submit.classList.toggle("jcw__contrib-submit--busy", !!busy);
      }
      if (cancel) cancel.setAttribute("aria-disabled", busy ? "true" : "false");

      setText(
        this.els.contribSubmitLabel,
        busy ? this.strings.contribSubmitting || "" : this.contributionSubmitLabel()
      );
    }

    async submitContribution(event) {
      if (event && typeof event.preventDefault === "function") event.preventDefault();
      if (this.contributionBusy) return;

      var form = this.els.contribForm;
      if (!form) return;

      /* Native constraint validation first: `required` on the name already expresses
       * what the browser can check, so no parallel custom validator is added for it. */
      if (typeof form.checkValidity === "function" && !form.checkValidity()) {
        if (typeof form.reportValidity === "function") form.reportValidity();
        return;
      }

      var message = this.els.contribMessage ? this.els.contribMessage.value.trim() : "";
      var answers = this.collectAnswers();

      /* PHOTO-ONLY CREATE: nothing is sent until a picture has actually been
       * chosen. `photoIntent` creates a provisional row server-side, and one of
       * those with no photo behind it is exactly the orphan the provisional
       * mechanism exists to avoid.
       *
       * Checked BEFORE the generic empty test so the message names the thing the
       * visitor is actually missing — they opened a photo editor, so "add a
       * story or pick an answer" would be the wrong instruction. */
      var photoOnlyCreate =
        this.contribFocus === "photo" && this.contribMode !== "edit";
      if (this.contribFocus === "photo" && !this.photoFile && !this.photoRemoveRequested) {
        this.showContributionError(this.copy.photoNoFile || this.strings.contribErrorEmpty || "");
        this.focusElement(this.els.photoInput);
        return;
      }

      /* A name on its own is not a contribution — but a photo on its own IS, so it
       * counts here alongside the story and the prompt answers.
       *
       * On an edit, a photo ALREADY on the entry counts too. The backend reaches
       * the same conclusion through `hasPhoto`, and without this the simplified
       * form would refuse to save a photo entry whose answers were deliberately
       * emptied — a save the backend would have accepted. */
      var keepsExistingPhoto =
        this.contribMode === "edit" && !!this.photoExistingUrl && !this.photoRemoveRequested;

      /* M23 — did THIS submission carry writing?
       *
       * Recorded here, from the fields actually being sent, rather than
       * re-derived afterwards from the refreshed DTO: by the time the success
       * message is shown the Wall has been re-read, and an entry that already
       * had a story from an earlier submission would make a photo-only edit
       * look like a written one. A selected chip counts as writing, exactly as
       * `entryHasStory()` and the backend's `hasTextSubstance` count it. */
      var storySubmitted = !!message || answers.length > 0;

      if (!message && !answers.length && !this.photoFile && !keepsExistingPhoto) {
        /* The simplified form has no story box, so neither the wording about one
         * nor a focus call into a hidden field would make sense. */
        if (this.simplifiedPrompts) {
          this.showContributionError(
            this.strings.contribErrorEmptySimple || this.strings.contribErrorEmpty || ""
          );
          this.focusElement(this.firstPromptTextarea() || this.els.contribName);
        } else {
          this.showContributionError(this.strings.contribErrorEmpty || "");
          this.focusElement(this.els.contribMessage);
        }
        return;
      }

      var slug = this.currentSlug;
      /* WRITE credential only. resolveWriteToken() cannot return the raw Office
       * access token, which the write endpoints do not accept in any case. */
      var participantToken = resolveWriteToken(slug);
      if (!slug || !participantToken) {
        this.showContributionError(this.strings.contribErrorAccess || "");
        return;
      }

      var editing = this.contribMode === "edit";
      var entryId = this.contribEntryId;
      /* Read at send time only. Never held on the instance, never in the DOM, and
       * out of scope again as soon as this request is built. */
      var editToken = editing ? readEntryToken(slug, entryId) : null;

      if (editing && (!entryId || !editToken)) {
        this.showContributionError(this.strings.contribErrorAccess || "");
        return;
      }

      var payload = {
        participantToken: participantToken,
        displayName: this.els.contribName ? this.els.contribName.value.trim() : ""
      };

      /* M21 — both the create and the edit route run `mayContribute()`. Sent
       * alongside the existing credentials, never instead of them. */
      var contributorToken = readContributorToken(slug);
      if (contributorToken) payload.contributorToken = contributorToken;

      /* WHY AN EDIT SENDS ONLY WHAT CHANGED.
       *
       * `updateContribution` in the backend treats both fields as REPLACEMENTS,
       * and `answers` destructively so:
       *
       *     if (body.answers !== undefined) {
       *       deleteMany(selectedChoices); deleteMany(answers); writeAnswers(...)
       *     }
       *     message: body.message === undefined ? entry.message : body.message
       *
       * So an edit that posts `answers` wipes every answer and chip on the entry
       * and rewrites only what the form could show. Any answer whose prompt is no
       * longer published is not rendered — `fillContributionForm` cannot find a
       * field for it — so it would be silently destroyed, and it could not even
       * be resent: an inactive promptKey is rejected by `validateContribution`.
       *
       * Omitting an unchanged field is therefore not an optimisation, it is what
       * keeps a contributor's earlier answers intact. The backend's own
       * `hasExistingSubstance` path covers the "nothing sent" case.
       *
       * A deliberate clearing still sends the field, because the value changed. */
      var snapshot = editing ? this.contribSnapshot : null;
      var serialisedAnswers = JSON.stringify(answers);

      if (!snapshot || snapshot.message !== message) {
        payload.message = message || null;
      }

      if (snapshot && this.supportsAnswerPatch) {
        /* PREFERRED EDIT PATH. `answerPatch` names only the prompts that
         * changed; everything else — including every answer to a prompt this
         * form no longer shows — is explicitly out of scope server-side and
         * survives untouched.
         *
         * Omitted entirely when nothing changed. An empty array is NOT the
         * same request: `validateAnswerPatch` rejects it, so sending one
         * would fail the whole edit, including the name and message changes
         * that may be the only thing the contributor came to make. */
        var patch = this.buildAnswerPatch(answers, snapshot);
        if (patch.length) payload.answerPatch = patch;
      } else if (!snapshot || snapshot.answers !== serialisedAnswers) {
        /* CREATE, or an edit against a backend without `answerPatch`. Sending
         * only a changed `answers` set is the M18A behaviour and remains the
         * safest thing available there. `answers` and `answerPatch` are
         * mutually exclusive, and this branch structure is what guarantees
         * only one of them is ever built. */
        payload.answers = answers;
      }

      if (editing) payload.editToken = editToken;

      /* Declared ONLY for a genuine photo-only creation. Sending it alongside a
       * story would be meaningless — the backend ignores it for provisioning in
       * that case — and sending it on an edit is not part of the contract. */
      if (photoOnlyCreate) payload.photoIntent = true;

      var url =
        PROXY_BASE +
        "/offices/" +
        encodeURIComponent(slug) +
        "/entries" +
        (editing ? "/" + encodeURIComponent(entryId) : "");

      this.showContributionError("");
      this.setContributionBusy(true);

      var response;
      try {
        response = await fetch(url, {
          method: editing ? "PATCH" : "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify(payload),
          credentials: "same-origin"
        });
      } catch (e) {
        /* Network failure. The thrown value is never inspected or logged: a
         * request-echoing error object would otherwise carry both credentials and the
         * entered text. Everything typed is retained for a retry. */
        this.setContributionBusy(false);
        this.showContributionError(this.strings.contribErrorGeneric || "");
        return;
      }

      /* M21 — a 403 may now mean "you have not finished verifying", which is a
       * different problem with a different fix. Read the body first and handle
       * that case truthfully: the grant is NOT dropped (it is still valid, the
       * visitor may still read and share), the entry credential is NOT dropped,
       * nothing is retried, and only this office's contributor credential is
       * discarded, because the backend has just told us it will not accept it. */
      if (response.status === 403) {
        var reason403 = null;
        try {
          var body403 = await response.json();
          reason403 = body403 && typeof body403.error === "string" ? body403.error : null;
        } catch (e) {
          reason403 = null;
        }

        if (reason403 === "verification_required") {
          dropContributorToken(slug);
          this.setContributionBusy(false);
          this.applyVerificationState();
          this.showContributionError(this.strings.errVerificationRequired || "");
          return;
        }
      }

      /* A STORY edit that answers 404: the entry can no longer be edited (for
       * example hidden during moderation). Offer a fresh story instead of the
       * access error. Deliberately narrow: only an edit, only 404, never the
       * photo editor — every other 401/403/404 keeps the handling below. The
       * grant is kept, as for every edit 404. */
      if (editing && response.status === 404 && this.contribFocus !== "photo") {
        this.setContributionBusy(false);
        this.showContributionError("");
        if (this.showEditUnavailable(true)) {
          this.focusElement(this.els.contribRetryTitle);
        } else {
          this.showContributionError(this.strings.contribErrorAccess || "");
        }
        return;
      }

      if (response.status === 401 || response.status === 403 || response.status === 404) {
        /* Exactly the handling the backing flow already uses: drop this Office's
         * participant grant and the matching session grant, and never retry with the
         * raw Office token.
         *
         * The edit credential is deliberately NOT dropped. This response cannot
         * distinguish a dead grant from a dead entry, a grant is recoverable by
         * reopening the share link, and an edit credential is not recoverable at all.
         * No distinction is surfaced to the contributor either way.
         *
         * EXCEPT a 404 on an EDIT. There the backend also answers 404 for an entry
         * that is no longer editable (hidden by moderation, rejected, stale edit
         * token) while the grant is perfectly valid, and dropping it locked the
         * visitor out of their own Wall. The grant is kept; if it really is dead,
         * the next Wall read answers 401/403/404 and load() drops it there. */
        if (!(editing && response.status === 404)) {
          dropGrant(slug);
          if (SESSION_GRANT && SESSION_GRANT.slug === slug) SESSION_GRANT = null;
        }
        this.setContributionBusy(false);
        this.showContributionError(this.strings.contribErrorAccess || "");
        return;
      }

      if (!response.ok) {
        this.setContributionBusy(false);
        /* Stage 1 of the photo sequence is creating the entry itself. Saying so
         * separates "your contribution never saved" from "it saved but the
         * picture did not", which the generic line could not. */
        this.showContributionError(
          (!editing && this.contribFocus === "photo" && this.copy.contribErrCreate) ||
            this.strings.contribErrorGeneric ||
            ""
        );
        return;
      }

      var body;
      try {
        body = await response.json();
      } catch (e) {
        this.setContributionBusy(false);
        this.showContributionError(this.strings.contribErrorGeneric || "");
        return;
      }

      if (!body || typeof body !== "object" || body.ok !== true) {
        this.setContributionBusy(false);
        this.showContributionError(this.strings.contribErrorGeneric || "");
        return;
      }

      if (!editing) {
        var newId = typeof body.entryId === "string" ? body.entryId.trim() : "";
        var newToken = typeof body.editToken === "string" ? body.editToken.trim() : "";
        /* The entry exists server-side either way, so the success path runs either
         * way. Without both halves this browser simply cannot own it, and no edit
         * control will appear — which is the honest outcome, and better than an error
         * that invites a duplicate submission. */
        if (newId && newToken) saveEntryToken(slug, newId, newToken);
        entryId = newId;
      }

      /* Keep the local prefill in step with the name actually accepted, for both a
       * create and an edit. Never written while typing — only after a success. */
      saveContributorName(slug, payload.displayName);

      /* The entry is now saved. From here a photo failure must never cost the
       * contributor their text, so the form switches to EDIT mode for the entry that
       * now exists — a retry then PATCHes it instead of creating a duplicate. */
      this.contribMode = "edit";
      this.contribEntryId = entryId;
      setText(this.els.contribSubmitLabel, this.contributionSubmitLabel());

      var photoWanted = !!this.photoFile || this.photoRemoveRequested;
      var photoResult = { ok: true };

      if (photoWanted && entryId) {
        this.photoBusy = true;
        photoResult = await this.runPhotoOperations(slug, entryId);
        this.photoBusy = false;
      }

      this.setContributionBusy(false);

      if (!photoResult.ok) {
        /* Saved, but without the picture.
         *
         * The refresh has to come FIRST and the messages last. Re-rendering the Wall
         * parks the shell out of the entry row that is about to be replaced, and
         * parking an edit form deliberately resets it — so anything shown before the
         * refresh would be wiped by it. The chosen file is carried across that rebuild
         * by hand, because having to find and wait for the photo again would be its
         * own small punishment for a failure that was not the contributor's fault. */
        var keptFile = this.photoFile;
        var keptName = this.els.photoName ? this.els.photoName.textContent : "";
        var opener = this.visibleTrigger(this.contribOpener);

        this.setPhotoStatus("");
        await this.load({ quiet: true });

        /* Re-open on the entry as the server now has it, so the form shows the text
         * that was genuinely saved rather than a hopeful local copy. */
        var fresh = null;
        var owned = this.ownedVisible || [];
        for (var f = 0; f < owned.length; f++) {
          if (owned[f] && owned[f].id === entryId) { fresh = owned[f]; break; }
        }

        if (fresh) {
          this.contribDirty = false;
          this.contribReady = false;
          this.openContribution("edit", fresh, opener);
        }

        if (keptFile) this.showChosenPhoto(keptFile, keptName);
        this.showPhotoMessage(this.photoFailureMessage(photoResult));
        this.showContributionError(this.strings.photoErrSavedNoPhoto || "");
        /* A retry is one click away and nothing has been lost, so the form is not
         * treated as dirty — the contributor can walk away without being blocked. */
        this.contribDirty = false;
        this.focusElement(this.els.photoInput);
        return;
      }

      await this.finishContribution(editing, entryId, storySubmitted);
    }

    /* Closes and parks the form BEFORE the re-fetch, because renderEntries() clears
     * the list — an edit form still sitting inside an entry would be destroyed with
     * it. The confirmation is then re-homed next to the entry that was edited. */
    async finishContribution(wasEditing, entryId, storySubmitted) {
      this.closeContribution(true);

      var refreshed = await this.load({ quiet: true });
      /* The write succeeded but the refresh did not. load() has already shown the
       * error state, which hides the content block the success region lives in, so
       * claiming success here would be both invisible and untrue. */
      if (!refreshed) return;

      if (wasEditing && entryId) {
        var slot = this.findEntryEditSlot(entryId);
        if (slot && this.els.contribShell && this.els.contribShell.parentNode !== slot) {
          slot.appendChild(this.els.contribShell);
        }
      }

      this.showContributionSuccess(wasEditing, storySubmitted);
    }

    /* Announced by the persistent role="status" region the shell carries, and focused
     * so a keyboard user lands on the confirmation rather than at the top of the page.
     * Focus moves only AFTER the Wall has re-rendered, so it cannot be discarded by
     * the re-render. */
    showContributionSuccess(wasEditing, storySubmitted) {
      var success = this.els.contribSuccess;
      if (!success) return;

      /* M23 — say what actually happened.
       *
       * "Your contribution is on the Office Wall" was shown even when the
       * submission carried no writing at all, which told a photo-only
       * contributor they had done something they had not. The form knows which
       * it was: `contribFocus === "photo"` is the photo-only editor, and a
       * submission whose own fields carry no story is a photo submission
       * whatever route opened it. */
      var wroteSomething = this.contribFocus !== "photo" && storySubmitted === true;
      var titleKey = wroteSomething
        ? (wasEditing ? "contribSuccessUpdated" : "contribSuccessCreated")
        : (wasEditing ? "photoSuccessUpdated" : "photoSuccessCreated");

      setText(this.els.contribSuccessTitle, this.strings[titleKey] || this.strings.contribSuccessCreated || "");

      success.hidden = false;
      try {
        success.focus({ preventScroll: false });
      } catch (e) {
        /* focus is a convenience only */
      }
    }

    /* Populates the single shared dialog from the already-fetched DTO and opens it
     * through the theme's ModalDialog, which supplies Escape, Tab containment, focus
     * into the close control, and focus restoration to the trigger passed here. No
     * second API request is made. */
    /* ---------- Dashboard statistic dialogs (Milestone 19) ----------
     *
     * Read-only views over the payload already in hand. Nothing here fetches,
     * and nothing here reveals anything the Wall did not already return. */
    /* Close whichever campaign dialog is currently open.
     *
     * M19D: every campaign dialog shares `.jcw__modal` and therefore the same
     * z-index, so when a second one was shown the winner was decided by DOM
     * order — and the stat dialogs are declared after the entry viewer, so
     * "View entry" from the Contributions overview opened BEHIND it. The fix
     * is not a higher z-index but the rule the brief asks for: one active
     * modal. hide() is the component's own API, so the overlay, the scroll
     * lock and the focus trap are all unwound properly rather than left behind.
     *
     * `openedBy` is cleared first so the dialog being closed does not yank
     * focus back to the tile that opened it a moment before the next dialog
     * takes focus — that race is what left focus stranded on an inert
     * element. */
    /* Dialog closes do not route through here, so the flush is attached to the
     * element's own close event further down as well; this covers the
     * programmatic path. */
    closeOpenDialogs(except) {
      var open = document.querySelectorAll(".jcw__modal[open]");
      for (var i = 0; i < open.length; i++) {
        var dialog = open[i];
        if (dialog === except) continue;
        if (typeof dialog.hide !== "function") continue;
        dialog.openedBy = null;
        dialog.hide();
      }
    }

    openStatDialog(kind, trigger) {
      var dialog = document.getElementById(this.statDialogId(kind));
      if (!dialog || typeof dialog.show !== "function") return;

      /* One active modal, here too. Without this, clicking a second tile while
       * the first dialog was open left both on screen — and three rapid clicks
       * left three, stacked by DOM order, with the page behind them unclickable
       * because the lowest overlay never released. Re-showing the dialog that
       * is already open is a no-op rather than a close-then-reopen flicker. */
      this.closeOpenDialogs(dialog);

      this.lastStatTrigger = trigger || null;

      if (kind === "entries") this.renderStatEntries();
      else if (kind === "backings") this.renderStatBackings();
      else if (kind === "photos") this.renderStatPhotos();

      /* `trigger` is handed to the shared dialog so focus returns to the tile
       * that opened it when it closes. */
      dialog.show(trigger);
    }

    statDialogId(kind) {
      var suffix =
        kind === "entries" ? "Entries" : kind === "backings" ? "Backings" : kind === "photos" ? "Photos" : "";
      if (!suffix) return "";
      /* The dialogs are section-scoped by id, and the element may already have
       * been relocated to <body> by ModalDialog, so this resolves by prefix
       * against the document rather than through `this`. */
      var prefix = "JcwStat" + suffix + "Dialog-";
      var all = document.querySelectorAll('[id^="' + prefix + '"]');
      return all.length ? all[0].id : "";
    }

    /* Contributions: name, date and a View entry action. Deliberately no
     * answer text — the overview lists who contributed, and the existing entry
     * dialog is where a contribution is actually read. */
    renderStatEntries() {
      var list = this.els.statEntriesList;
      var template = document.getElementById("JcwStatEntryTemplate");
      if (!list || !template || !template.content || !template.content.firstElementChild) return;

      list.textContent = "";
      /* M23 — the same set the tile counted. Listing every WallEntry here put
       * photo-only rows in a dialog headed "Contributions", with nothing to
       * show for them. */
      var entries = Array.isArray(this.wallStoryEntries) ? this.wallStoryEntries : [];
      var anonymous = this.strings.anonymous || "";
      var self = this;

      for (var i = 0; i < entries.length; i++) {
        (function (entry) {
          var node = template.content.firstElementChild.cloneNode(true);
          var name = firstString(entry, ["displayName", "display_name", "name"]) || anonymous;

          setText(node.querySelector("[data-jcw-statrow-name]"), self.ownsEntry(entry) ? self.youLabel(name) : name);

          var timeEl = node.querySelector("[data-jcw-statrow-time]");
          renderTimestamp(timeEl, timeEl, firstString(entry, ["submittedAt", "submitted_at"]));

          setText(node.querySelector("[data-jcw-statrow-view-label]"), self.strings.viewEntry || "");
          setText(node.querySelector("[data-jcw-statrow-view-context]"), name);

          var btn = node.querySelector("[data-jcw-statrow-view]");
          if (btn) {
            btn.addEventListener("click", function () {
              self.openEntry(entry, self.resolveAnswers(entry), btn);
            });
          }
          list.appendChild(node);
        })(entries[i]);
      }

      if (this.els.statEntriesEmpty) {
        setText(this.els.statEntriesEmpty, this.strings.statEntriesEmpty || "");
        this.els.statEntriesEmpty.hidden = entries.length > 0;
      }
    }

    /* Backings: the VERIFIED total and an honest explanation, never a list of
     * names. See the note on this dialog in the section. */
    renderStatBackings() {
      var count = this.verifiedBackingCount || 0;
      setText(
        this.els.statBackingsCount,
        count === 1
          ? this.strings.statBackingsCountOne || ""
          : (this.strings.statBackingsCountOther || "").replace("{{ count }}", String(count))
      );

      /* M19D — the supporters are listed by name.
       *
       * This became safe to render when M19A landed: the Wall query now filters
       * `verifiedAt: { not: null }` alongside `hiddenAt: null`, so the
       * COLLECTION itself is the guarantee — every row in `backings[]` is a
       * verified, non-moderated supporter. No per-record `verified` flag is
       * needed, and M19/M19C's refusal to list names was based on the older
       * unfiltered contract.
       *
       * Privacy: these are the same names, from the same field, already shown
       * in this Wall's activity feed to the same audience — holders of the
       * private link — and the backing form discloses at the point of
       * collection that the name appears on the office's Wall. No email, no
       * dedupe hash and no credential is in the DTO, so none can be rendered.
       * See the open items for the one question left for legal. */
      var list = this.els.statBackingsList;
      var template = document.getElementById("JcwStatBackingTemplate");
      if (!list || !template || !template.content || !template.content.firstElementChild) return;

      list.textContent = "";
      var backings = Array.isArray(this.wallBackings) ? this.wallBackings : [];
      var anonymous = this.strings.anonymous || "";

      for (var i = 0; i < backings.length; i++) {
        var backing = backings[i];
        if (!backing || typeof backing !== "object") continue;

        var node = template.content.firstElementChild.cloneNode(true);
        setText(
          node.querySelector("[data-jcw-statrow-name]"),
          firstString(backing, ["displayName", "display_name", "name"]) || anonymous
        );
        var timeEl = node.querySelector("[data-jcw-statrow-time]");
        renderTimestamp(timeEl, timeEl, firstString(backing, ["createdAt", "created_at"]));
        list.appendChild(node);
      }

      /* The Wall returns at most MAX_WALL_BACKINGS rows while the count is the
       * office's full verified total, so a popular office would show fewer
       * names than the number above it. Say so rather than letting the list
       * read as complete. */
      if (this.els.statBackingsNote) {
        var truncated = count > backings.length;
        setText(
          this.els.statBackingsNote,
          truncated
            ? (this.strings.statBackingsTruncated || "").replace("{{ count }}", String(backings.length))
            : ""
        );
        this.els.statBackingsNote.hidden = !truncated;
      }

      if (this.els.statBackingsEmpty) {
        setText(this.els.statBackingsEmpty, this.strings.statBackingsEmpty || "");
        this.els.statBackingsEmpty.hidden = backings.length > 0;
      }
    }

    /* Photos: only entries carrying a renderable published photo. `photo.url`
     * is the signed URL the backend returned for this Wall read, so the
     * gallery inherits the same authorization as every other photo on the
     * page — and an entry whose upload never finalized has no url and is
     * therefore absent rather than rendered broken. */
    renderStatPhotos() {
      var list = this.els.statPhotosList;
      var template = document.getElementById("JcwGalleryItemTemplate");
      if (!list || !template || !template.content || !template.content.firstElementChild) return;

      list.textContent = "";
      var entries = Array.isArray(this.wallEntries) ? this.wallEntries : [];
      var anonymous = this.strings.anonymous || "";
      var shown = 0;
      /* Counted first, so one photo can be presented large rather than as a
       * lone tile in an empty grid. */
      var total = 0;
      for (var c = 0; c < entries.length; c++) {
        if (hasRenderablePhoto(entries[c]) && firstString(entries[c].photo || entries[c], ["url", "photoUrl"])) total++;
      }
      list.classList.toggle("jcw-gallery--single", total === 1);
      var self = this;

      for (var i = 0; i < entries.length; i++) {
        (function (entry) {
          if (!hasRenderablePhoto(entry)) return;
          var url = firstString(entry.photo || entry, ["url", "photoUrl"]);
          if (!url) return;

          var node = template.content.firstElementChild.cloneNode(true);
          var name = firstString(entry, ["displayName", "display_name", "name"]) || anonymous;

          var img = node.querySelector("[data-jcw-gallery-img]");
          if (img) {
            img.setAttribute("src", safeImageUrl(url));
            img.setAttribute("alt", fillToken(self.strings.photoThumbAlt, "{{ name }}", name));
          }

          var btn = node.querySelector("[data-jcw-gallery-open]");
          if (btn) {
            btn.addEventListener("click", function () {
              /* The entry dialog IS the photo viewer — it renders the photo
               * alongside the contribution. No second viewer is introduced. */
              self.openEntry(entry, self.resolveAnswers(entry), btn);
            });
          }
          list.appendChild(node);
          shown++;
        })(entries[i]);
      }

      if (this.els.statPhotosEmpty) {
        setText(this.els.statPhotosEmpty, this.strings.statPhotosEmpty || "");
        this.els.statPhotosEmpty.hidden = shown > 0;
      }
    }

    openEntry(entry, answerRows, trigger) {
      var modal = this.els.modal;
      if (!modal || typeof modal.show !== "function") return;

      /* If this was opened from the Contributions or Photos overview, that
       * overview closes first — see closeOpenDialogs(). Focus returns to the
       * dashboard tile that started the journey rather than to a control
       * inside a dialog that is no longer on screen. */
      var fallback = this.lastStatTrigger;
      this.closeOpenDialogs(modal);
      /* visibleTrigger() rejects anything outside the host, which is exactly
       * right here: a control inside a relocated dialog is not a safe place to
       * send focus once that dialog has closed. */
      if (!this.visibleTrigger(trigger)) trigger = fallback || null;

      var anonymous = this.strings.anonymous || "";
      var author = firstString(entry, ["displayName", "display_name", "name", "authorName"]);
      var role = firstString(entry, ["roleLabel", "role_label", "role"]);
      var message = firstString(entry, ["message", "text", "body"]);

      setText(this.els.modalAuthor, author || anonymous);

      if (this.els.modalRole) {
        setText(this.els.modalRole, role);
        this.els.modalRole.hidden = !role;
      }

      if (this.els.modalMessage) {
        setText(this.els.modalMessage, message);
        this.els.modalMessage.hidden = !message;
      }

      this.renderPhoto(entry, author || anonymous);
      this.renderAnswers(this.els.modalAnswers, answerRows);

      modal.show(trigger);
    }

    /* Renders the entry's photo inside the dialog.
     *
     * Keyed on `photo.url` ONLY, exactly as the backend specifies: `status` is the
     * last operation's outcome, while `url` is whether a photo can actually be
     * shown — a failed or in-flight replacement still has a renderable previous
     * photo. The Storage object path is not in the DTO and is never referenced here.
     *
     * Alt text states the one fact available, who added it, without inventing a
     * description of content nobody has described. */
    renderPhoto(entry, author) {
      var figure = this.els.modalFigure;
      var img = this.els.modalPhoto;
      if (!figure || !img) return;

      var url = entry && entry.photo && typeof entry.photo.url === "string" ? entry.photo.url : "";

      if (this.els.modalPhotoFallback) this.els.modalPhotoFallback.hidden = true;
      img.hidden = false;

      if (!url) {
        figure.hidden = true;
        img.removeAttribute("src");
        return;
      }

      img.alt = fillToken(this.strings.photoAlt, "{{ name }}", author || "");

      /* Signed read URLs expire after about 15 minutes. A stale one gets a single
       * explicit refresh rather than a polling loop. */
      var self = this;
      img.onerror = function () {
        img.hidden = true;
        if (self.els.modalPhotoFallback) {
          setText(self.els.modalPhotoFallbackText, self.strings.photoLoadFailed || "");
          self.els.modalPhotoFallback.hidden = false;
        }
      };
      img.src = safeImageUrl(url);
      figure.hidden = false;
    }

    /* Renders the prompts this coworker actually answered.
     *
     * Driven by the rows resolveAnswers() already produced for this entry, so the
     * dialog shows exactly what the "View entry" control promised. A partial answer
     * set (the seeded entry answers 3 of 5) renders naturally with no gaps and no
     * placeholder rows, and backend order is preserved as-is.
     *
     * Each row may carry selected chips, free text, or both. Chips are rendered as a
     * list of resolved LABELS — never raw choice keys. */
    renderAnswers(listEl, rows) {
      if (!listEl) return;

      /* Shared dialog: always clear the previous entry's answers before repopulating. */
      listEl.textContent = "";
      listEl.hidden = true;

      var items = Array.isArray(rows) ? rows : [];
      var template = document.getElementById("JcwAnswerTemplate");
      var choiceTemplate = document.getElementById("JcwAnswerChoiceTemplate");

      if (!items.length || !template || !template.content || !template.content.firstElementChild) {
        return;
      }

      for (var i = 0; i < items.length; i++) {
        var row = items[i];
        var node = template.content.firstElementChild.cloneNode(true);

        setText(node.querySelector("[data-jcw-answer-question]"), row.question);

        var choicesEl = node.querySelector("[data-jcw-answer-choices]");
        if (choicesEl) {
          if (row.choices.length && choiceTemplate && choiceTemplate.content && choiceTemplate.content.firstElementChild) {
            for (var c = 0; c < row.choices.length; c++) {
              var chip = choiceTemplate.content.firstElementChild.cloneNode(true);
              setText(chip, row.choices[c]);
              choicesEl.appendChild(chip);
            }
            choicesEl.hidden = false;
          } else {
            choicesEl.remove();
          }
        }

        var textEl = node.querySelector("[data-jcw-answer-text]");
        if (textEl) {
          if (row.text) {
            setText(textEl, row.text);
            textEl.hidden = false;
          } else {
            textEl.remove();
          }
        }

        listEl.appendChild(node);
      }

      listEl.hidden = listEl.children.length === 0;
    }
  }

  if (!window.customElements.get("campaign-office-wall")) {
    window.customElements.define("campaign-office-wall", CampaignOfficeWallElement);
  }
})();
